<template>
  <section class="pos-screen" :style="{'--pos-card-width': tileDensity === 'small' ? '8.5rem' : tileDensity === 'large' ? '14rem' : '11.5rem'}">
    <header class="pos-topbar">
      <div class="pos-brand">
        <router-link to="/dashboard" class="round-icon home-icon" title="Ir al inicio">🏠</router-link>
        <div>
          <strong>Punto de venta</strong>
          <small>{{ contextLabel }}</small>
        </div>
      </div>
      <nav class="pos-quick-actions" aria-label="Accesos rápidos del punto de venta">
        <button class="pos-quick-chip clock-only" type="button" title="Checar entrada o salida" aria-label="Checar entrada o salida" @click="showTimeClock = true">
          <span aria-hidden="true">🕒</span>
        </button>
        <button class="pos-quick-chip new-account-chip" type="button" :aria-busy="posBusy.createTicket || undefined" :disabled="posBusy.createTicket || !cashSession || !registerStatusChecked" @click="openNewTicketModal">
          <span aria-hidden="true">＋</span><span>Nueva</span>
        </button>
        <button class="pos-quick-chip pending-accounts-chip" type="button" @click="openAccountsModal">
          <span class="pending-label">Pendientes</span><span v-if="tickets.length" class="quick-count">{{ tickets.length }}</span>
        </button>
      </nav>
      <div class="pos-top-actions compact-top-actions"><PosSyncStatus />
        <span v-if="multiRegisterEnabled" class="status-pill" :class="cashSession ? 'ok' : 'warn'" :title="register?.name || 'Sin caja elegida'">{{ register?.name || 'Elegir caja' }}</span>
        <span v-else class="status-pill" :class="cashSession ? 'ok' : 'warn'">{{ cashSession ? 'Caja abierta' : 'Abrir caja' }}</span>
        <button class="pos-menu-trigger" type="button" @click="posMenuOpen = true" aria-label="Abrir menu PV"><span></span><span></span><span></span></button>
      </div>
    </header>

    <TimeClockModal v-if="showTimeClock" @close="showTimeClock = false" @success="handleTimeClockSuccess" />
    <div v-if="multiRegisterEnabled && showRegisterModal" class="modal-backdrop" @click.self.stop.prevent>
      <section class="modal small-modal" role="dialog" aria-modal="true" aria-label="Seleccionar caja de este dispositivo">
        <div class="modal-head"><div><small>Fase 2B · Piloto en línea</small><h2>Caja de esta terminal</h2></div><button class="round-icon" @click="showRegisterModal = false" :disabled="!selectedRegisterId" aria-label="Cerrar">×</button></div>
        <p>Selecciona una caja. Los cobros, gastos y cortes se registrarán únicamente en ella. No permite operaciones sin conexión.</p>
        <div class="register-picker-list">
          <button v-for="option in registerOptions" :key="option.id" type="button" class="pos-btn ghost register-option" @click="chooseRegister(option.id)">
            <strong>{{ option.name }}</strong>
            <small v-if="option.open_session">Abierta · {{ option.open_session.cashier_name || 'Cajero asignado' }}</small>
            <small v-else>Cerrada</small>
            <span v-if="Number(option.id) === Number(selectedRegisterId)">✓ Actual</span>
          </button>
        </div>
        <div v-if="canManageRegisters" class="register-management">
          <label>Nueva caja <input v-model.trim="newRegisterName" placeholder="Ej. Terminal barra" maxlength="80" /></label>
          <button class="pos-btn ghost" :disabled="!newRegisterName || registerBusy" @click="createRegisterRecord">Crear caja</button>
        </div>
        <small>Antes de cambiar, sincroniza todas las ventas y confirma las operaciones pendientes en este dispositivo.</small>
      </section>
    </div>

    <div v-if="posMenuOpen" class="pos-side-overlay" @click="posMenuOpen = false"></div>
    <aside class="pos-side-menu" :class="{ open: posMenuOpen }" @click.stop>
      <header>
        <div><small>Menu PV</small><strong>Acciones rápidas</strong></div>
        <button class="round-icon" @click="posMenuOpen = false">×</button>
      </header>
      <button class="side-action" @click="sideMenuAction(loadAll)">Actualizar</button>
      <button v-if="multiRegisterEnabled" class="side-action featured" @click="sideMenuAction(() => { showRegisterModal = true; loadRegisterOptions(false); })">🏧 Caja y terminal · {{ register?.name || 'Sin asignar' }}</button>
      <button class="side-action" @click="sideMenuAction(switchCashier)">Cambiar cajero</button>
      <router-link v-if="isPosAdmin" class="side-action" to="/pv/inventario">Agregar inventario</router-link>
      <router-link v-if="isPosAdmin" class="side-action" to="/pv/productos-menu">Productos del menú / vínculos</router-link>
      <router-link v-if="isPosAdmin" class="side-action featured" to="/pv/inteligencia" @click="posMenuOpen = false">📊 Inteligencia comercial</router-link>
      <router-link v-if="isPosAdmin" class="side-action featured" to="/pv/cupones" @click="posMenuOpen = false">🎟️ Cupones y beneficios</router-link>
      <router-link v-if="isPosAdmin" class="side-action featured" to="/pv/clientes-beneficios" @click="posMenuOpen = false">👥 Clientes y descuentos fijos</router-link>
      <router-link class="side-action" to="/pv/preparacion" @click="posMenuOpen = false">👨‍🍳 Tablero de preparación</router-link>
      <router-link class="side-action" to="/pv/equipo">Equipo e impresión</router-link>
      <a class="side-action" href="gestiontotal-pos://launch">Lanzar con auto impresión</a>
      <label class="side-action">Tamaño de tarjetas <select v-model="tileDensity"><option value="small">Compacto</option><option value="normal">Normal</option><option value="large">Grande</option></select></label>
      <router-link class="side-action" to="/comandero" @click="posMenuOpen = false">Comandero</router-link>
      <router-link class="side-action" to="/cuenta" @click="posMenuOpen = false">Mi cuenta</router-link>
      <button class="side-action" @click="sideMenuAction(() => { showCustomers = true })">Clientes</button>
      <button class="side-action featured" @click="sideMenuAction(openLoyaltyScan)">⭐ Lealtad / QR</button>
      <button v-if="couponEnabled" class="side-action featured" :disabled="!activeTicket" @click="sideMenuAction(openCouponModal)">🎟️ Escanear cupón</button>
      <button v-if="isPosAdmin" class="side-action" @click="sideMenuAction(openOptionAdmin)">Catálogo</button>
      <button class="side-action" @click="sideMenuAction(() => { showTables = true })">Mesas</button>
      <button class="side-action featured" @click="sideMenuAction(openPendingPrints)">🖨️ Impresiones pendientes</button>
      <button class="side-action" @click="sideMenuAction(openSalesModal)">Ventas</button>
      <button class="side-action" @click="sideMenuAction(openProductReport)">Reporte por producto</button>
      <button class="side-action" :disabled="!cashSession" @click="sideMenuAction(openCashExpenseModal)">Gastos / salidas</button>
      <button class="side-action" @click="sideMenuAction(() => { showReceiptSettings = true })">Ticket</button>
      <button class="side-action" :disabled="!cashSession" @click="sideMenuAction(openCashCutModal)">Realizar corte</button>
      <button class="side-action" @click="sideMenuAction(openCutHistory)">Historial de cortes</button>
      <button class="side-action featured" @click="sideMenuAction(openPointAdjustModal)">Asignar puntos</button>
      <button class="side-action featured" @click="sideMenuAction(openDiscountManager)">Descuentos</button>
    </aside>

    <div v-if="message" class="toast" :class="messageType">{{ message }}</div>

    <div v-if="commandNotifications.length" class="command-notification-stack">
      <article v-for="notice in commandNotifications" :key="notice.local_id" class="command-notification">
        <header>
          <div>
            <small>{{ notice.subtitle || 'Nueva comanda desde comandero' }}</small>
            <strong>{{ notice.title }}</strong>
            <em>{{ shortTime(notice.created_at) }} · {{ notice.meta || `${notice.items_count} producto(s)` }}</em>
          </div>
          <button class="notice-close" title="Cerrar" @click="dismissCommandNotification(notice.local_id)">×</button>
        </header>
        <div class="notice-items">
          <div v-for="item in notice.preview_items" :key="`${notice.local_id}-${item.id || item.name}`" class="notice-item">
            <span>{{ item.quantity }} × {{ item.name }}</span>
            <small v-if="item.notes">{{ item.notes }}</small>
          </div>
          <div v-if="notice.extra_items > 0" class="notice-more">+ {{ notice.extra_items }} producto(s) más</div>
        </div>
        <div class="notice-actions">
          <button class="pos-btn ghost" @click="openCommandNotificationTicket(notice)">Abrir cuenta</button>
          <button v-if="notice.kind === 'account_print'" class="pos-btn primary" @click="printAccountNotification(notice)">Imprimir cuenta</button>
          <button v-else-if="notice.kind === 'ticket_update'" class="pos-btn primary" @click="openUpdateAndSendCommand(notice)">Enviar comanda</button>
          <button v-else class="pos-btn primary" @click="printCommandNotification(notice)">Imprimir comandas</button>
        </div>
      </article>
    </div>

    <PosTimeNotifications
      :alerts="timeAlerts"
      @acknowledge="acknowledgeTimeAlert"
      @open-ticket="openTimeAlertTicket"
    />

    <div v-if="!registerStatusChecked" class="cash-overlay">
      <div class="cash-card">
        <span class="status-pill warn">Validación de terminal pendiente</span>
        <h1>Conecta para verificar las cajas</h1>
        <p>No se permite asignar cobros a una caja sin confirmar el modo del servidor. Si es tu primer arranque, conéctate una vez.</p>
        <button class="pos-btn primary" @click="loadRegisterOptions">Comprobar de nuevo</button>
      </div>
    </div>
    <div v-else-if="multiRegisterEnabled && !selectedRegisterId" class="cash-overlay">
      <div class="cash-card">
        <span class="status-pill warn">Terminal sin caja asignada</span>
        <h1>Selecciona una caja</h1>
        <p>Esta función es un piloto en línea. No se asignará una caja automáticamente para evitar mezclar cortes.</p>
        <div class="register-picker-list">
          <button v-for="option in registerOptions" :key="option.id" class="pos-btn ghost register-option" @click="chooseRegister(option.id)">
            {{ option.name }} · {{ option.open_session ? 'Abierta' : 'Cerrada' }}
          </button>
        </div>
        <button class="pos-btn ghost" @click="loadRegisterOptions(false)">Actualizar lista</button>
      </div>
    </div>
    <div v-else-if="!cashSession" class="cash-overlay">
      <div class="cash-card">
        <span class="status-pill warn">Caja requerida</span>
        <h1>Abre la caja para iniciar ventas</h1>
        <p>El punto de venta necesita una sesión de caja abierta para crear cuentas y cobrar.</p>
        <div class="cash-form">
          <label>
            <span>Monto inicial</span>
            <input v-model.number="openingCash" type="number" min="0" step="0.01" placeholder="0.00" />
          </label>
          <label v-if="multiRegisterEnabled && canManageRegisters && availableCashiers.length">
            <span>Cajero responsable</span>
            <select v-model.number="assignedCashierId">
              <option v-for="cashier in availableCashiers" :key="cashier.id" :value="Number(cashier.id)">{{ cashier.name }}</option>
            </select>
          </label>
          <button :aria-busy="posBusy.openCash || undefined" :disabled="posBusy.openCash || (multiRegisterEnabled && !register)" class="pos-btn primary" @click="openCash">Abrir caja</button>
        </div>
      </div>
    </div>

    <main class="pos-main" :class="{ blocked: !cashSession }">
      <section class="catalog-zone">
        <div class="catalog-toolbar">
          <div class="catalog-title">
            <button v-if="activeCategory" class="round-icon" title="Volver a categorías (Backspace)" @click="activeCategory = null">←</button>
            <div>
              <h1>{{ activeCategory ? activeCategory.name : 'Categorías' }}</h1>
              <small>{{ activeCategory ? 'Selecciona productos para la cuenta activa' : 'Elige una categoría para comenzar' }}</small>
            </div>
          </div>
          <div class="search-box">
            <input v-model="search" placeholder="Buscar producto" @keyup.enter="activeCategory = null" />
            <button v-if="search" class="clear-search" @click="search = ''">×</button>
          </div>
        </div>

        <nav class="product-quickbar" aria-label="Accesos rápidos a productos">
          <button class="quickbar-button" type="button" :class="{ selected: productQuickFilter === 'all' }" @click="productQuickFilter = 'all'">Categorías</button>
          <button class="quickbar-button" type="button" :class="{ selected: productQuickFilter === 'favorites' }" @click="productQuickFilter = 'favorites'; activeCategory = null">★ Favoritos <span v-if="favoriteProductIds.length">{{ favoriteProductIds.length }}</span></button>
          <button class="quickbar-button" type="button" :class="{ selected: productQuickFilter === 'recent' }" @click="productQuickFilter = 'recent'; activeCategory = null">Recientes</button>
          <button v-if="recentProductIds.length" type="button" class="quickbar-button repeat-last" title="Abrir último producto capturado" @click="selectLastProduct">↺ Último</button>
        </nav>
        <div v-if="!activeCategory && !search && productQuickFilter === 'all'" class="category-grid">
          <button v-for="category in categoriesWithCounts" :key="category.id" class="category-tile" :style="tileStyle(category)" @click="activeCategory = category">
            <span>{{ category.name }}</span>
            <strong>{{ category.count }}</strong>
            <small>productos</small>
          </button>
        </div>

        <div v-else class="product-grid">
          <ProductAvailabilityCard v-for="product in visibleProducts" :key="productQuickKey(product)" :style="tileStyle(product)" :name="product.name" :enabled="!product.availability_status || product.availability_status==='visible'" :busy="availabilityBusy===product.id" :linked="!!product.menu_linked" :pending="!!product.menu_sync_pending" :manageable="!product.is_virtual_extra" @change="setProductAvailability(product,$event)">
          <div class="product-tile-wrap">
            <button type="button" class="product-tile" :style="tileStyle(product)" :disabled="stockBlocked(product) || posBusy.addProductFromModal" :class="{ 'extra-product-tile': product.is_virtual_extra, 'stock-empty': stockEmpty(product) }" @click="selectProduct(product)">
              <div>
                <strong>{{ product.name }}</strong>
                <small>{{ product.is_virtual_extra ? (product.short_description || 'Extra independiente') : (product.category_name || 'Sin categoría') }}</small>
              </div>
              <span>{{ money(product.sale_price) }}</span><small v-if="hasStock(product)" class="stock-label">{{ Number(product.stock_available || 0) <= 0 ? "Agotado · " + product.stock_available : product.stock_available + " disponibles" }}</small>
              <em v-if="Number(product.config?.time_minutes || 0) > 0">+{{ product.config.time_minutes }} min</em>
              <em v-else-if="product.has_options">Opciones</em>
              <em v-else-if="product.is_virtual_extra">Extra</em>
            </button>
            <button type="button" class="favorite-pin" :class="{ selected: isFavoriteProduct(product) }" :aria-label="isFavoriteProduct(product) ? `Quitar ${product.name} de favoritos` : `Agregar ${product.name} a favoritos`" :title="isFavoriteProduct(product) ? 'Quitar de favoritos' : 'Agregar a favoritos'" @click="toggleFavoriteProduct(product)">{{ isFavoriteProduct(product) ? '★' : '☆' }}</button>
          </div>
          </ProductAvailabilityCard>
          <div v-if="!visibleProducts.length" class="empty-box">
            <strong>No hay productos para mostrar.</strong>
            <p>Revisa la categoría, la búsqueda o administra el catálogo desde el menú del PV.</p>
          </div>
        </div>
      </section>

      <aside class="order-zone">
        <section class="ticket-card">
          <div class="ticket-head">
            <div>
              <strong>{{ activeTicket ? ticketLabel(activeTicket) : 'Sin cuenta activa' }}</strong>
              <small>{{ activeTicket?.ticket_number || 'Selecciona o crea una cuenta' }}</small>
            </div>
            <div v-if="activeTicket" class="ticket-head-actions">
              <span class="status-pill" :class="activeTicket.command_status === 'sent' ? 'ok' : 'warn'">
                {{ activeTicket.command_status === 'sent' ? 'Comanda enviada' : 'Sin comanda' }}
              </span>
              <button
                class="round-icon ticket-delete-button"
                type="button"
                title="Eliminar cuenta"
                aria-label="Eliminar cuenta"
                :aria-busy="posBusy.cancelActiveTicket || undefined"
                :disabled="posBusy.cancelActiveTicket || Number(activeTicket.paid_total || 0) > 0"
                @click="cancelActiveTicket"
              >🗑</button>
            </div>
          </div>

          <div v-if="activeTicket" class="ticket-edit-row">
            <select v-model="ticketMeta.service_type" @change="saveTicketMeta">
              <option value="takeaway">Para llevar</option>
              <option value="onsite">Mesa</option>
              <option value="delivery">Entrega</option>
            </select>
            <input v-model="ticketMeta.order_label" placeholder="Nombre / nota de cuenta" @blur="saveTicketMeta" />
          </div>

          <div v-if="activeTicket" class="ticket-context">
            <button v-if="eligibleAccountTargets.length && !activeTicket._pending" type="button" class="context-chip" @click="openMergeDialog">⇄ Unir cuentas</button>
            <button class="context-chip" @click="showTables = true">{{ activeTicket.table_name ? `Mesa ${activeTicket.table_name}` : 'Asignar mesa' }}</button>
            <button class="context-chip" @click="showCustomers = true">{{ activeTicket.customer_name || 'Asignar cliente' }}</button>
            <button v-if="activeTicket.customer_id" class="context-chip danger" title="Quitar cliente de esta cuenta" @click="unlinkActiveTicketCustomer">Quitar cliente</button>
            <button v-if="couponEnabled && activeTicket && !activeTicket._pending" class="context-chip" type="button" @click="openCouponModal">🎟️ Cupón</button>
          </div>

          <PosTimePanel v-if="activeTimer" :timer="activeTimer" @control="controlActiveTimer" />

          <div v-if="activeTicket" class="ticket-items">
            <div v-for="item in activeItems" :key="item.id" class="ticket-item">
              <div class="item-main">
                <strong>{{ item.quantity }} × {{ item.name }}</strong>
                <small v-for="modifier in normalizedModifiers(item)" :key="modifier.option_id || modifier.name">
                  {{ modifier.preparation_note || modifier.name }} <span v-if="Number(modifier.price_delta)">+{{ money(modifier.price_delta) }}</span>
                </small>
                <small v-if="item.notes">Nota: {{ item.notes }}</small>
                <small v-if="timeMinutesForItem(item)" class="time-item-state">🎮 Aporta {{ timeMinutesForItem(item) }} min por unidad al tiempo de la cuenta</small>
                <small class="kitchen-state">{{ kitchenStateLabel(item) }}</small>
                <div v-if="canQuickEditItem(item)" class="ticket-quantity-controls" :aria-label="`Ajustar cantidad de ${item.name}`">
                  <button type="button" :disabled="quickItemBusy || Number(item.quantity) <= 1" :aria-label="`Reducir ${item.name}`" @click="changeItemQuantity(item, -1)">−</button>
                  <strong>{{ Number(item.quantity) }}</strong>
                  <button type="button" :disabled="quickItemBusy" :aria-label="`Aumentar ${item.name}`" @click="changeItemQuantity(item, 1)">+</button>
                  <button type="button" class="item-note-shortcut" :disabled="quickItemBusy" @click="beginItemNoteEdit(item)">✎ Nota</button>
                </div>
                <div v-if="editingItemId === Number(item.id)" class="inline-note-editor">
                  <textarea v-model="editingItemNotes" maxlength="500" rows="2" placeholder="Indicaciones para este producto (opcional)"></textarea>
                  <div><button type="button" :disabled="quickItemBusy" @click="saveItemQuickNotes(item)">Guardar</button><button type="button" class="subtle" @click="editingItemId = null">Cancelar</button></div>
                </div>
                <div v-if="!canQuickEditItem(item)" class="sent-item-actions">
                  <small class="quantity-lock-note">{{ activeTicket?._pending ? 'Sincroniza esta cuenta para editar' : 'Comanda enviada o artículo no editable' }}</small>
                  <button v-if="item.product_id && item.kitchen_status === 'sent'" type="button" @click="repeatSentItem(item)">＋ Repetir como nueva línea</button>
                </div>
              </div>
              <div class="item-actions">
                <strong>{{ money(item.line_total) }}</strong>
                <button class="item-discount-btn" title="Descuento a este producto" @click="openItemDiscountModal(item)">%</button>
                <button v-if="canQuickMoveItem(item)" type="button" class="item-move-btn" title="Mover este producto a otra cuenta" :disabled="arrangingAccounts" @click="openMoveDialog(item)">⇄</button>
                <button :aria-busy="posBusy.removeItem || undefined" :disabled="posBusy.removeItem" title="Quitar" @click="removeItem(item)">×</button>
              </div>
            </div>
            <div v-if="!activeItems.length" class="empty-mini large">Agrega productos desde el panel izquierdo.</div>
          </div>

          <div v-if="activeTicket" class="totals-box">
            <div><span>Subtotal</span><strong>{{ money(activeTicket.subtotal) }}</strong></div>
            <div v-if="Number(activeTicket.ticket_discount_amount || 0) > 0"><span>{{ activeTicket.ticket_discount_name || 'Descuento cuenta' }}</span><strong>-{{ money(activeTicket.ticket_discount_amount) }}</strong></div>
            <div v-if="activeTicket.coupon"><span>Cupón {{ activeTicket.coupon.status === 'redeemed' ? 'canjeado' : 'reservado' }}</span><small>{{ activeTicket.coupon.campaign_name }}</small></div>
            <!-- Reservado para una futura preferencia de visibilidad por PV. No altera el cálculo fiscal. -->
            <div v-if="showPosTaxSummary"><span>IVA</span><strong>{{ money(activeTicket.tax_total) }}</strong></div>
            <div class="total"><span>Total</span><strong>{{ money(activeTicket.total) }}</strong></div>
            <div><span>Pagado</span><strong>{{ money(activeTicket.paid_total) }}</strong></div>
          </div>

          <div v-if="activeTicket" class="ticket-actions">
            <button :aria-busy="posBusy.sendCommand || undefined" class="pos-btn ghost" @click="sendCommand" :disabled="(!activeTicket) || posBusy.sendCommand">{{ pendingKitchenItems.length ? 'Enviar comanda' : 'Reimprimir comanda' }}</button>
            <button class="pos-btn ghost" @click="printActiveAccount" :disabled="!activeTicket || !activeItems.length">Imprimir cuenta</button>
            <button class="pos-btn ghost" @click="openDiscountModal" :disabled="!activeTicket">Descuento</button>
            <button class="pos-btn primary" @click="openPaymentModal" :disabled="Number(activeTicket.total) <= 0">Cobrar</button>
          </div>

          <button v-else type="button" class="empty-box compact empty-account-action" @click="openAccountsModal">
            <strong>{{ tickets.length ? `Ver ${tickets.length} cuenta(s) pendiente(s)` : 'Ver cuentas pendientes' }}</strong>
            <small>{{ tickets.length ? 'Toca para seleccionar una cuenta' : 'También puedes crear una desde Nueva' }}</small>
          </button>
        </section>
      </aside>
    </main>

    <div v-if="showNewTicketModal" class="modal-backdrop" @click.self.stop.prevent>
      <section class="modal new-ticket-modal">
        <div class="modal-head">
          <div><small>Nueva cuenta</small><h2>¿Cómo se atenderá?</h2></div>
          <button class="round-icon" @click="showNewTicketModal = false">×</button>
        </div>

        <div class="service-picker">
          <button class="service-card" :class="{ active: newTicketMode === 'takeaway' }" @click="selectNewTicketMode('takeaway')">
            <strong>Para llevar</strong>
            <small>Mostrador rápido</small>
          </button>
          <button class="service-card" :class="{ active: newTicketMode === 'onsite' }" @click="selectNewTicketMode('onsite')">
            <strong>Mesa</strong>
            <small>Elegir mesa en un toque</small>
          </button>
          <button class="service-card" :class="{ active: newTicketMode === 'customer' }" @click="selectNewTicketMode('customer')">
            <strong>Cliente / QR</strong>
            <small>Buscar o escanear lealtad</small>
          </button>
        </div>

        <div v-if="newTicketMode === 'takeaway'" class="service-flow">
          <label>
            <span>Nombre opcional para llamar la orden</span>
            <input v-model="newTicketForm.order_label" placeholder="Ej. Juan, pedido mostrador..." @keyup.enter="startTakeawayTicket" />
          </label>
          <button :aria-busy="posBusy.createTicket || undefined" :disabled="posBusy.createTicket" class="pos-btn primary full" @click="startTakeawayTicket">Crear cuenta para llevar</button>
        </div>

        <div v-if="newTicketMode === 'onsite'" class="service-flow">
          <div class="table-grid touch-grid">
            <button :aria-busy="posBusy.createTicket || undefined" :disabled="posBusy.createTicket" v-for="table in tables" :key="table.id" class="table-card" :class="table.status" @click="startTableTicket(table)">
              <strong>{{ table.name }}</strong>
              <small>{{ table.status === 'occupied' ? 'Ocupada / abrir' : table.status }}</small>
            </button>
          </div>
          <div class="inline-form">
            <input v-model="newTable.name" placeholder="Nueva mesa" />
            <input v-model.number="newTable.capacity" type="number" min="1" placeholder="Cap." />
            <button class="pos-btn primary" @click="createTable">Crear</button>
          </div>
        </div>

        <div v-if="newTicketMode === 'customer'" class="service-flow">
          <div class="flow-note loyalty-flow-note">
            <strong>Cuenta ligada a cliente</strong>
            <span>Busca por nombre, correo o teléfono para clientes frecuentes. También puedes escanear su wallet aquí; al cobrar se sumarán puntos automáticamente a esta cuenta.</span>
          </div>
          <div class="customer-search customer-search-expanded">
            <input v-model="customerSearch" placeholder="Nombre, teléfono, correo o QR" @keyup.enter="loadCustomers" />
            <button class="pos-btn ghost" @click="loadCustomers">Buscar local</button>
            <button class="pos-btn ghost" @click="openQrScanner('newTicketCustomer')">📷 Escanear QR</button>
            <button class="pos-btn ghost" @click="lookupCustomerQrForNewTicket">Usar código</button>
            <button class="pos-btn ghost" :disabled="loyaltyExternalLoading" @click="openExternalCustomerSearchFromPos">
              {{ loyaltyExternalLoading ? 'Buscando...' : 'Buscar en Afrogato' }}
            </button>
          </div>
          <div class="customer-list touch-list">
            <button :aria-busy="posBusy.createTicket || undefined" :disabled="posBusy.createTicket" v-for="customer in customers" :key="customer.id" class="customer-card" @click="startCustomerTicket(customer)">
              <strong>{{ customer.name }}</strong>
              <small>{{ customer.email || customer.phone || customer.qr_token }}</small>
              <span>{{ formatPoints(customer.points_balance || 0) }} pts</span>
            </button>
          </div>
          <div class="new-customer">
            <strong>Alta rápida</strong>
            <input v-model="newCustomer.name" placeholder="Nombre" />
            <input v-model="newCustomer.phone" placeholder="Teléfono" />
            <input v-model="newCustomer.email" placeholder="Correo" />
            <button class="pos-btn primary" @click="createCustomerFromNewTicket">Crear cuenta con cliente</button>
          </div>
        </div>
      </section>
    </div>

    <div v-if="productModal" class="modal-backdrop" @click.self.stop.prevent>
      <section class="modal product-modal" ref="productModalBox" tabindex="-1">
        <div class="modal-head">
          <div>
            <small>Agregar producto</small>
            <h2>{{ productModal.name }}</h2>
          </div>
          <button class="round-icon" @click="closeProductModal">×</button>
        </div>

        <div class="qty-row">
          <span>Cantidad</span>
          <div>
            <button @click="productForm.quantity = Math.max(1, productForm.quantity - 1)">−</button>
            <strong>{{ productForm.quantity }}</strong>
            <button @click="productForm.quantity += 1">+</button>
          </div>
        </div>

        <div v-if="Number(productModal.config?.time_minutes || 0) > 0" class="product-time-note">
          <strong>🎮 Servicio de tiempo</strong>
          <span>Cada unidad agrega {{ productModal.config.time_minutes }} minutos a la cuenta.</span>
        </div>

        <div v-if="modifierPricingRule(productModal)" class="product-pricing-note">
          <strong>{{ includedCountLabel(modifierPricingRule(productModal).included_count) }}</strong>
          <span>Desde la selección {{ Number(modifierPricingRule(productModal).included_count || 0) + 1 }}, cada ingrediente agrega {{ money(modifierPricingRule(productModal).extra_price) }}.</span>
        </div>

        <div v-for="group in productModal.option_groups" :key="group.id" class="option-group">
          <div class="option-title">
            <strong>{{ group.name }}</strong>
            <small>{{ group.is_required ? 'Obligatorio' : 'Opcional' }}</small>
          </div>
          <div class="option-list">
            <label v-for="option in group.options" :key="option.id" class="option-card" :class="{ checked: optionSelected(group, option), unavailable: option.availability_status === 'sold_out' }">
              <input
                :type="group.selection_type === 'multiple' ? 'checkbox' : 'radio'"
                :name="`group-${group.id}`"
                :checked="optionSelected(group, option)"
                :disabled="option.availability_status === 'sold_out'"
                @change="toggleOption(group, option, $event.target.checked)"
              />
              <span>{{ option.name }}</span>
              <small>{{ option.availability_status === 'sold_out' ? 'Agotado' : optionPriceLabel(group, option, productModal) }}</small>
            </label>
          </div>
        </div>

        <label class="notes-box">
          <span>Nota para preparación</span>
          <textarea v-model="productForm.notes" placeholder="Ej. sin azúcar, menos hielo, bien caliente..."></textarea>
        </label>

        <div class="modal-actions">
          <strong>{{ money(productPreviewTotal) }}</strong>
          <button :aria-busy="posBusy.addProductFromModal || undefined" :disabled="posBusy.addProductFromModal" class="pos-btn primary" @click="addProductFromModal">Agregar</button>
        </div>
      </section>
    </div>

    <div v-if="showArrangeDialog" class="modal-backdrop" @click.self.stop.prevent>
      <section class="modal account-arrange-modal" role="dialog" aria-modal="true" aria-label="Organizar cuentas">
        <div class="modal-head">
          <div><small>Organización de cuentas</small><h2>{{ arrangeMode === 'merge' ? 'Unir cuentas' : 'Mover producto' }}</h2></div>
          <button type="button" class="round-icon" :disabled="arrangingAccounts" @click="showArrangeDialog = false">×</button>
        </div>
        <p class="arrange-info">{{ arrangeMode === 'merge'
          ? 'Se trasladarán todos los productos pendientes a la cuenta destino. La cuenta de origen quedará archivada.'
          : `Mover ${arrangeItem?.name || 'producto'} conservando precio y opciones. Solo artículos no enviados a preparación.` }}</p>
        <label v-if="arrangeMode === 'item'" class="arrange-field"><span>Unidades a mover</span>
          <input v-model.number="arrangeQuantity" type="number" min="1" :max="Math.floor(Number(arrangeItem?.quantity || 1))" step="1" />
        </label>
        <label class="arrange-field"><span>Cuenta de destino</span>
          <select v-model.number="arrangeTargetId">
            <option :value="0" disabled>Selecciona cuenta</option>
            <option v-for="ticket in eligibleAccountTargets" :key="ticket.id" :value="Number(ticket.id)">{{ ticketLabel(ticket) }} · {{ ticket.ticket_number }} · {{ money(ticket.total) }}</option>
          </select>
        </label>
        <small class="arrange-warning">Por seguridad, esta operación no mueve anticipos, descuentos, puntos, promociones ni comandas ya enviadas. Necesita conexión.</small>
        <div class="modal-actions">
          <button type="button" class="pos-btn ghost" :disabled="arrangingAccounts" @click="showArrangeDialog = false">Cancelar</button>
          <button type="button" class="pos-btn primary" :disabled="arrangingAccounts || !arrangeTargetId" @click="submitArrangeAccounts">
            {{ arrangingAccounts ? 'Guardando…' : arrangeMode === 'merge' ? 'Unir cuentas' : 'Mover producto' }}
          </button>
        </div>
      </section>
    </div>

    <div v-if="showPayment" class="modal-backdrop" @click.self.stop.prevent>
      <section class="modal small-modal">
        <div class="modal-head">
          <div><small>Cobro</small><h2>{{ money(balanceDue) }}</h2></div>
          <button class="round-icon" @click="showPayment = false">×</button>
        </div>
        <div class="payment-method-panel">
          <span class="field-label">Método</span>
          <div class="payment-method-grid">
            <button
              v-for="method in paymentMethods"
              :key="method.code"
              type="button"
              class="payment-method-btn"
              :class="{ active: paymentForm.payment_method_code === method.code }"
              @click="selectPaymentMethod(method.code)"
            >
              <strong>{{ method.name }}</strong>
              <small v-if="truthy(method.requires_reference)">Pide referencia</small>
              <small v-else-if="['transfer', 'card'].includes(method.code)">Referencia opcional</small>
              <small v-else>{{ truthy(method.affects_cash) ? 'Afecta efectivo' : 'Pago externo' }}</small>
            </button>
          </div>
        </div>
        <label>
          <span>Monto</span>
          <input ref="paymentAmountInput" v-model.number="paymentForm.amount" type="number" min="0" step="0.01" @keydown.enter.prevent.stop="focusNextPaymentField('amount')" />
        </label>
        <button
          v-if="selectedPaymentSupportsReference && !selectedPaymentRequiresReference"
          type="button"
          class="payment-reference-toggle"
          :aria-expanded="paymentReferenceExpanded"
          @click="paymentReferenceExpanded = !paymentReferenceExpanded"
        >
          <span>Referencia (opcional)</span><span aria-hidden="true">{{ paymentReferenceExpanded ? '⌃' : '⌄' }}</span>
        </button>
        <label v-if="selectedPaymentReferenceVisible">
          <span>Referencia{{ selectedPaymentRequiresReference ? '' : ' (opcional)' }}</span>
          <input ref="paymentReferenceInput" v-model="paymentForm.reference" placeholder="Referencia de pago" @keydown.enter.prevent.stop="focusNextPaymentField('reference')" />
        </label>
        <label v-if="paymentForm.payment_method_code === 'cash'">
          <span>Recibido</span>
          <input ref="paymentReceivedInput" v-model.number="paymentForm.received_amount" type="number" min="0" step="0.01" @keydown.enter.prevent.stop="focusNextPaymentField('received')" />
        </label>
        <div v-if="paymentForm.payment_method_code === 'cash'" class="change-box" :class="{ warn: cashReceivedMissing }">
          <span>{{ cashReceivedMissing ? 'Falta recibir' : 'Cambio' }}</span>
          <strong>{{ money(cashReceivedMissing ? cashMissingAmount : paymentChange) }}</strong>
        </div>
        <label class="switch-row switch-visual">
          <input v-model="paymentPrintRequested" type="checkbox" :disabled="posBusy.payTicket" />
          <i aria-hidden="true"></i>
          <span>Imprimir ticket</span>
        </label>
        <button :aria-busy="posBusy.payTicket || undefined" :disabled="posBusy.payTicket" ref="paymentSubmitButton" class="pos-btn primary full" @click="payTicket">Registrar pago</button>
      </section>
    </div>

    <div v-if="showDiscounts" class="modal-backdrop" @click.self.stop.prevent>
      <section class="modal discount-modal">
        <div class="modal-head">
          <div><small>Descuentos</small><h2>{{ activeTicket ? 'Aplicar descuento a cuenta' : 'Administrar descuentos' }}</h2></div>
          <button class="round-icon" @click="showDiscounts = false">×</button>
        </div>

        <div v-if="activeTicket && Number(activeTicket.ticket_discount_amount || 0) > 0" class="active-discount-card">
          <div>
            <strong>{{ activeTicket.ticket_discount_name }}</strong>
            <small>{{ discountText(activeTicket) }} · -{{ money(activeTicket.ticket_discount_amount) }}</small>
          </div>
          <button class="pos-btn ghost danger" @click="clearTicketDiscount" :disabled="Number(activeTicket.paid_total || 0) > 0">Quitar</button>
        </div>

        <label>
          <span>Motivo / autorización opcional</span>
          <input v-model="discountForm.reason" placeholder="Ej. empleado, locatario, vecino..." />
        </label>

        <div class="discount-grid">
          <article v-for="discount in discounts" :key="discount.id" class="discount-card discount-card-admin">
            <button class="discount-main" @click="activeTicket ? applyDiscount(discount) : editDiscount(discount)" :disabled="activeTicket && Number(activeTicket?.paid_total || 0) > 0">
              <strong>{{ discount.name }}</strong>
              <span>{{ discountText(discount) }}</span>
              <small v-if="discount.requires_reason">Requiere motivo</small>
            </button>
            <div class="discount-card-actions">
              <button class="mini-action" type="button" @click="editDiscount(discount)">Editar</button>
              <button class="mini-action danger" type="button" @click="deleteDiscount(discount)">Eliminar</button>
            </div>
          </article>
          <div v-if="!discounts.length" class="empty-mini large">Aún no hay descuentos registrados.</div>
        </div>

        <div class="discount-editor-card">
          <div class="section-title-row">
            <strong>{{ newDiscountForm.id ? 'Editar descuento' : 'Crear descuento rápido' }}</strong>
            <small>Los cambios aplican solo a descuentos futuros.</small>
          </div>
          <div class="editor-grid two-cols">
            <label>
              <span>Nombre</span>
              <input v-model="newDiscountForm.name" placeholder="Ej. Descuento cortesía" />
            </label>
            <label>
              <span>Tipo</span>
              <select v-model="newDiscountForm.discount_type">
                <option value="percent">Porcentaje</option>
                <option value="amount">Monto fijo</option>
              </select>
            </label>
            <label>
              <span>Valor</span>
              <input v-model.number="newDiscountForm.discount_value" type="number" min="0" step="0.01" />
            </label>
            <label class="switch-row compact-switch">
              <input v-model="newDiscountForm.requires_reason" type="checkbox" />
              <span>Requiere motivo</span>
            </label>
          </div>
          <button class="pos-btn primary full" @click="saveDiscount">Guardar descuento</button>
        </div>
      </section>
    </div>




    <div v-if="showCashExpense" class="modal-backdrop" @click.self.stop.prevent>
      <section class="modal expense-modal">
        <div class="modal-head">
          <div><small>Caja</small><h2>Salidas y gastos</h2></div>
          <button class="round-icon" @click="showCashExpense = false">×</button>
        </div>

        <div class="expense-layout">
          <section class="expense-form-card">
            <div class="section-title-row">
              <strong>Registrar salida</strong>
              <small>Se descuenta del efectivo esperado cuando el método afecta caja.</small>
            </div>
            <label>
              <span>Concepto</span>
              <input v-model="expenseForm.concept" placeholder="Ej. compra de leche, gas, propina proveedor" />
            </label>
            <div class="editor-grid two-cols">
              <label>
                <span>Monto</span>
                <input v-model.number="expenseForm.amount" type="number" min="0" step="0.01" />
              </label>
              <label>
                <span>Método</span>
                <select v-model="expenseForm.payment_method_code">
                  <option v-for="method in paymentMethods" :key="method.code" :value="method.code">{{ method.name }}</option>
                </select>
              </label>
              <label>
                <span>Categoría</span>
                <input v-model="expenseForm.category" placeholder="general, insumos, servicios..." />
              </label>
              <label>
                <span>Pagado a</span>
                <input v-model="expenseForm.paid_to" placeholder="Proveedor / persona" />
              </label>
              <label>
                <span>Folio / comprobante</span>
                <input v-model="expenseForm.receipt_number" placeholder="Opcional" />
              </label>
            </div>
            <label>
              <span>Notas</span>
              <textarea v-model="expenseForm.notes" placeholder="Opcional"></textarea>
            </label>
            <div class="modal-actions">
              <button class="pos-btn ghost" @click="loadCashExpenses">Recargar</button>
              <button :aria-busy="posBusy.saveCashExpense || undefined" :disabled="posBusy.saveCashExpense" class="pos-btn primary" @click="saveCashExpense">Registrar e imprimir</button>
            </div>
          </section>

          <section class="expense-list-card">
            <div class="sales-summary-grid expense-summary-grid">
              <div><span>Salidas</span><strong>{{ expenseSummary.expenses_count || 0 }}</strong></div>
              <div><span>Total</span><strong>{{ money(expenseSummary.expenses_total || 0) }}</strong></div>
              <div><span>Efectivo</span><strong>{{ money(expenseSummary.cash_expenses_total || 0) }}</strong></div>
              <div><span>Otros</span><strong>{{ money(expenseSummary.non_cash_expenses_total || 0) }}</strong></div>
            </div>
            <div class="sales-list expense-list">
              <article v-for="expense in cashExpenses" :key="expense.id" class="sale-ticket-card expense-card">
                <header>
                  <div>
                    <strong>{{ expense.concept }}</strong>
                    <small>{{ expense.category }} · {{ expense.payment_method_name || expense.payment_method_code }} · {{ shortTime(expense.created_at) }}</small>
                  </div>
                  <span>{{ money(expense.amount) }}</span>
                </header>
                <div class="account-summary-meta">
                  <span v-if="expense.paid_to">{{ expense.paid_to }}</span>
                  <span v-if="expense.receipt_number">Folio {{ expense.receipt_number }}</span>
                  <span>{{ truthy(expense.affects_cash) ? 'Salida de efectivo' : 'No afecta efectivo' }}</span>
                </div>
                <footer>
                  <button class="pos-btn ghost" @click="printCashExpense(expense)">Imprimir ticket</button>
                  <button class="pos-btn ghost danger-soft" @click="voidCashExpense(expense)">Cancelar</button>
                </footer>
              </article>
              <div v-if="!cashExpenses.length && !expenseLoading" class="empty-box compact">
                <strong>No hay salidas registradas.</strong>
                <p>Cuando captures gastos de caja aparecerán aquí y también en el corte.</p>
              </div>
              <div v-if="expenseLoading" class="empty-box compact"><strong>Cargando salidas...</strong></div>
            </div>
          </section>
        </div>
      </section>
    </div>

    <div v-if="showOpenAccounts" class="modal-backdrop" @click.self.stop.prevent>
      <section class="modal open-accounts-modal">
        <div class="modal-head">
          <div><small>Cuentas pendientes</small><h2>Mesas y cuentas abiertas</h2></div>
          <button class="round-icon" @click="showOpenAccounts = false">×</button>
        </div>

        <div class="accounts-summary-grid">
          <article v-for="ticket in tickets" :key="ticket.id" class="account-summary-card" :class="{ active: activeTicket && Number(activeTicket.id) === Number(ticket.id) }">
            <header>
              <div>
                <strong>{{ ticketLabel(ticket) }}</strong>
                <small>{{ ticket.ticket_number }}</small>
              </div>
              <span>{{ money(ticket.total) }}</span>
            </header>
            <div class="account-summary-meta">
              <span>{{ ticket.items_count || 0 }} producto(s)</span>
              <span>{{ ticket.service_type === 'onsite' ? 'Mesa' : ticket.service_type === 'delivery' ? 'Entrega' : 'Para llevar' }}</span>
              <span v-if="ticket.customer_name">{{ ticket.customer_name }}</span>
            </div>
            <div v-if="ticket.timer" class="account-time-summary" :class="`is-${ticket.timer.status}`">
              <span>🎮 {{ timerStatusLabel(ticket.timer.status) }}</span>
              <strong>{{ formatRemainingTime(ticket.timer) }}</strong>
            </div>
            <footer>
              <button class="pos-btn ghost" @click="printAccountFromSummary(ticket)">Imprimir cuenta</button>
              <button class="pos-btn primary" @click="openTicketFromSummary(ticket)">Abrir</button>
            </footer>
          </article>
          <div v-if="!tickets.length" class="empty-box">
            <strong>No hay cuentas abiertas.</strong>
            <p>Cuando guardes una mesa o una cuenta pendiente aparecerá aquí.</p>
            <button type="button" class="pos-btn primary" @click="showOpenAccounts = false; openNewTicketModal()">Crear nueva cuenta</button>
          </div>
        </div>
      </section>
    </div>

    <div v-if="showSales" class="modal-backdrop" @click.self.stop.prevent>
      <section class="modal sales-modal">
        <div class="modal-head">
          <div><small>{{ salesHistoryMode ? 'Caja' : 'Ventas' }}</small><h2>{{ salesHistoryMode ? 'Historial de cortes' : 'Tickets cobrados' }}</h2></div>
          <button class="round-icon" @click="showSales = false">×</button>
        </div>

        <div class="sales-filters">
          <label>
            <span>Vista</span>
            <select v-model="salesFilters.scope" @change="loadSales">
              <option value="today">Día</option>
              <option value="range">Rango de fechas</option>
              <option value="cash_session">Caja actual</option>
              <option value="cut">Corte seleccionado</option>
            </select>
          </label>
          <label v-if="salesFilters.scope === 'today'">
            <span>Fecha</span>
            <input v-model="salesFilters.date" type="date" @change="loadSales" />
          </label>
          <label v-if="salesFilters.scope === 'range'">
            <span>Desde</span>
            <input v-model="salesFilters.date_from" type="date" @change="loadSales" />
          </label>
          <label v-if="salesFilters.scope === 'range'">
            <span>Hasta</span>
            <input v-model="salesFilters.date_to" type="date" @change="loadSales" />
          </label>
          <label v-if="salesFilters.scope === 'cut'">
            <span>Corte</span>
            <select v-model="salesFilters.cut_id" @change="loadSales">
              <option value="">Selecciona corte</option>
              <option v-for="cut in salesCuts" :key="cut.id" :value="cut.id">{{ cut.cut_type }} · {{ shortDateTime(cut.created_at) }} · {{ money(cut.paid_total) }}</option>
            </select>
          </label>
          <button class="pos-btn ghost" @click="loadSales">Actualizar</button>
        </div>

        <div class="sales-summary-grid">
          <div><span>Tickets</span><strong>{{ salesSummary.tickets_count || 0 }}</strong></div>
          <div><span>Total</span><strong>{{ money(salesSummary.total || 0) }}</strong></div>
          <div><span>Efectivo</span><strong>{{ money(salesSummary.cash_total || 0) }}</strong></div>
          <div><span>Otros pagos</span><strong>{{ money(salesSummary.non_cash_total || 0) }}</strong></div>
          <button class="sales-summary-button" type="button" @click="salesShowExpenses = !salesShowExpenses"><span>Salidas</span><strong>{{ money(salesSummary.expenses_total || (salesActiveCut && salesActiveCut.expenses_total) || 0) }}</strong></button>
        </div>
        <div v-if="salesShowExpenses" class="sales-expense-list">
          <strong>Detalle de salidas / retiros</strong>
          <div v-for="expense in salesExpenseDetails" :key="expense.id">
            <span>{{ shortTime(expense.created_at) }} · {{ expense.concept || expense.category }}</span>
            <small>{{ expense.payment_method_name || expense.payment_method_code }}</small>
            <strong>{{ money(expense.amount) }}</strong>
          </div>
          <p v-if="!salesExpenseDetails.length" class="empty-mini">No hay salidas en este filtro.</p>
        </div>
        <div v-if="salesActiveCut" class="sales-cut-actions">
          <span>Corte {{ salesActiveCut.cut_type }} · {{ shortTime(salesActiveCut.created_at) }}</span>
          <button class="pos-btn ghost" @click="printCashCut(salesActiveCut)">Imprimir corte seleccionado</button>
        </div>
        <div v-if="salesActiveCut" class="area-payment-matrix">
          <strong>Distribución por área de ventas liquidadas</strong>
          <article v-for="area in cashCutAreaPaymentRows(salesActiveCut)" :key="`history-area-payment-${area.area_key}`">
            <header>
              <span>{{ area.area_name }} <small>· total del área</small></span>
              <strong>{{ money(area.total_amount) }}</strong>
            </header>
            <div v-for="method in area.payment_breakdown" :key="`${area.area_key}-${method.code}`">
              <span>{{ method.name }}</span>
              <strong>{{ money(method.total_amount) }}</strong>
            </div>
          </article>
          <div class="store-sales-total">
            <span>Total venta de la tienda · Corte {{ salesActiveCut.cut_type }}</span>
            <strong>{{ money(cashCutStoreTotal(salesActiveCut)) }}</strong>
          </div>
        </div>

        <div class="sales-list">
          <article v-for="ticket in salesTickets" :key="ticket.id" class="sale-ticket-card">
            <header>
              <div>
                <strong>{{ ticketLabel(ticket) }}</strong>
                <small>{{ ticket.ticket_number }} · {{ shortTime(ticket.closed_at || ticket.created_at) }}</small>
              </div>
              <span>{{ money(ticket.total) }}</span>
            </header>
            <div class="account-summary-meta">
              <span>{{ ticket.items_count || 0 }} producto(s)</span>
              <span v-if="ticket.customer_name">{{ ticket.customer_name }}</span>
              <span>{{ ticket.service_type === 'onsite' ? 'Mesa' : ticket.service_type === 'delivery' ? 'Entrega' : 'Para llevar' }}</span>
            </div>
            <footer>
              <button class="pos-btn ghost" @click="printSaleTicket(ticket)">Reimprimir</button>
              <button v-if="cashSession && ticket.status === 'paid'" class="pos-btn ghost" @click="openRefundModal(ticket)">Devolver pago</button>
              <button class="pos-btn primary" @click="openSaleTicket(ticket)">Ver</button>
            </footer>
          </article>
          <div v-if="!salesLoading && !salesTickets.length" class="empty-box">
            <strong>No hay ventas para este filtro.</strong>
            <p>Cuando cobres tickets aparecerán aquí para consulta y reimpresión.</p>
          </div>
          <div v-if="salesLoading" class="empty-box compact"><strong>Cargando ventas...</strong></div>
        </div>
      </section>
    </div>

    <div v-if="showProductReport" class="modal-backdrop" @click.self.stop.prevent>
      <section class="modal product-report-modal">
        <div class="modal-head">
          <div><small>Ventas</small><h2>Productos vendidos por día</h2></div>
          <button class="round-icon" @click="showProductReport = false">×</button>
        </div>

        <div class="product-report-filters">
          <label>
            <span>Producto</span>
            <select v-model="productReportFilters.product_id">
              <option value="">Selecciona un producto</option>
              <option v-for="product in reportableProducts" :key="product.id" :value="product.id">{{ product.name }}</option>
            </select>
          </label>
          <label>
            <span>Desde</span>
            <input v-model="productReportFilters.date_from" type="date" />
          </label>
          <label>
            <span>Hasta</span>
            <input v-model="productReportFilters.date_to" type="date" />
          </label>
          <button class="pos-btn primary" :disabled="productReportLoading || !productReportFilters.product_id" @click="loadProductReport">
            {{ productReportLoading ? 'Consultando...' : 'Consultar' }}
          </button>
        </div>

        <div class="sales-summary-grid product-report-summary">
          <div><span>Unidades vendidas</span><strong>{{ formatQuantity(productReportSummary.units_count) }}</strong></div>
          <div><span>Días consultados</span><strong>{{ productReportRows.length }}</strong></div>
          <div><span>Venta antes de descuentos</span><strong>{{ money(productReportSummary.gross_total) }}</strong></div>
          <div><span>Venta después de descuentos</span><strong>{{ money(productReportSummary.net_total) }}</strong></div>
        </div>

        <div class="product-report-table-wrap">
          <table class="product-report-table">
            <thead><tr><th>Fecha</th><th>Unidades</th><th>Tickets</th><th>Venta neta</th></tr></thead>
            <tbody>
              <tr v-for="day in productReportRows" :key="day.date">
                <td>{{ shortDate(day.date) }}</td>
                <td>{{ formatQuantity(day.units_count) }}</td>
                <td>{{ day.tickets_count }}</td>
                <td>{{ money(day.net_total) }}</td>
              </tr>
              <tr v-if="!productReportLoading && !productReportRows.length"><td colspan="4">Selecciona un producto para consultar sus ventas.</td></tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div v-if="showCashCut" class="modal-backdrop" @click.self.stop.prevent>
      <section class="modal small-modal cash-cut-modal">
        <div class="modal-head">
          <div><small>Caja</small><h2>Corte de caja</h2></div>
          <button class="round-icon" @click="showCashCut = false">×</button>
        </div>

        <div class="cut-type-row">
          <button class="service-card" :class="{ active: cashCutForm.cut_type === 'X' }" @click="cashCutForm.cut_type = 'X'">
            <strong>Corte X</strong>
            <small>Consulta sin cerrar caja</small>
          </button>
          <button class="service-card danger-soft" :class="{ active: cashCutForm.cut_type === 'Z' }" @click="cashCutForm.cut_type = 'Z'">
            <strong>Corte Z</strong>
            <small>Cierra turno/caja</small>
          </button>
        </div>

        <div class="cash-counter">
          <div class="counter-head">
            <div>
              <strong>Conteo de efectivo</strong>
              <small>Captura monedas y billetes; el sistema calcula el total.</small>
            </div>
            <strong>{{ money(cashCutDeclaredTotal) }}</strong>
          </div>
          <div class="denomination-grid">
            <label v-for="denom in cashDenominations" :key="denom.key">
              <span>{{ denom.label }}</span>
              <input v-model.number="cashCutForm.denomination_counts[denom.key]" type="number" min="0" step="1" />
              <small>{{ money((cashCutForm.denomination_counts[denom.key] || 0) * denom.value) }}</small>
            </label>
          </div>
        </div>
        <label>
          <span>Notas</span>
          <textarea v-model="cashCutForm.notes" placeholder="Opcional"></textarea>
        </label>
        <p v-if="cashCutError" class="cash-cut-error" role="alert">{{ cashCutError }}</p>

        <div v-if="cashCutResult" class="cut-summary">
          <div><span>Ventas liquidadas</span><strong>{{ money(cashCutResult.paid_total) }}</strong></div>
          <div><span>Cobros recibidos en esta caja (incluye abonos)</span><strong>{{ money(cashCutResult.collections_total || 0) }}</strong></div>
          <div><span>Abonos de cuentas aún abiertas</span><strong>{{ money(cashCutResult.open_account_collections || 0) }}</strong></div>
          <div><span>Devoluciones registradas</span><strong>-{{ money(cashCutResult.refunds_total || 0) }}</strong></div>
          <div><span>Salidas / gastos</span><strong>{{ money(cashCutResult.expenses_total || 0) }}</strong></div>
          <div><span>Efectivo esperado</span><strong>{{ money(cashCutResult.expected_cash_amount) }}</strong></div>
          <div><span>Efectivo contado</span><strong>{{ money(cashCutResult.declared_cash_amount) }}</strong></div>
          <div class="total"><span>Diferencia</span><strong>{{ money(cashCutResult.difference_amount) }}</strong></div>
          <small>{{ cashCutResult.tickets_count }} ticket(s), {{ cashCutResult.payments_count }} pago(s)</small>
          <div class="area-payment-matrix compact-matrix">
            <strong>Distribución por área de ventas liquidadas</strong>
            <article v-for="area in cashCutAreaPaymentRows(cashCutResult)" :key="`cut-area-payment-${area.area_key}`">
              <header>
                <span>{{ area.area_name }} <small>· total del área</small></span>
                <strong>{{ money(area.total_amount) }}</strong>
              </header>
              <div v-for="method in area.payment_breakdown" :key="`${area.area_key}-${method.code}`">
                <span>{{ method.name }}</span>
                <strong>{{ money(method.total_amount) }}</strong>
              </div>
            </article>
            <div class="store-sales-total">
              <span>Total venta de la tienda · Corte {{ cashCutResult.cut_type }}</span>
              <strong>{{ money(cashCutStoreTotal(cashCutResult)) }}</strong>
            </div>
          </div>
          <div class="breakdown-list">
            <strong>Entradas por método</strong>
            <div v-for="method in normalizedArray(cashCutResult.payment_breakdown)" :key="method.code">
              <span>{{ method.name }}</span>
              <strong>{{ money(method.total_amount) }}</strong>
            </div>
          </div>
          <div class="breakdown-list">
            <strong>Salidas por categoría</strong>
            <div v-for="expense in normalizedArray(cashCutResult.expense_breakdown)" :key="`${expense.category}-${expense.payment_method_code}`">
              <span>{{ expense.category }} · {{ expense.payment_method_name }}</span>
              <strong>{{ money(expense.total_amount) }}</strong>
            </div>
          </div>
          <div v-if="normalizedArray(cashCutResult.expenses_detail).length" class="breakdown-list expense-detail-list">
            <strong>Detalle de retiros</strong>
            <div v-for="expense in normalizedArray(cashCutResult.expenses_detail)" :key="expense.id">
              <span>{{ shortTime(expense.created_at) }} · {{ expense.concept }} <em v-if="expense.paid_to">/ {{ expense.paid_to }}</em></span>
              <strong>{{ money(expense.amount) }}</strong>
            </div>
          </div>
        </div>

        <div class="modal-actions">
          <button class="pos-btn ghost" @click="showCashCut = false">Cerrar</button>
          <button v-if="cashCutResult" class="pos-btn ghost" @click="printCashCut(cashCutResult)">Imprimir corte</button>
          <button type="button" :aria-busy="posBusy.performCashCut || undefined" :disabled="posBusy.performCashCut" class="pos-btn primary" @click="performCashCut">{{ posBusy.performCashCut ? 'Generando corte…' : `Generar corte ${cashCutForm.cut_type}` }}</button>
        </div>
      </section>
    </div>

    <div v-if="showReceiptSettings" class="modal-backdrop" @click.self.stop.prevent>
      <section class="modal receipt-settings-modal">
        <div class="modal-head">
          <div><small>Personalización</small><h2>Formato de ticket</h2></div>
          <button class="round-icon" @click="showReceiptSettings = false">×</button>
        </div>

        <div class="settings-grid">
          <label>
            <span>Nombre comercial</span>
            <input v-model="receiptSettings.business_name" placeholder="Nombre impreso" />
          </label>
          <label>
            <span>Ancho de rollo</span>
            <select v-model.number="receiptSettings.ticket_width_mm">
              <option :value="58">58 mm / mini ticket</option>
              <option :value="72">72 mm / rollo delgado</option>
              <option :value="80">80 mm</option>
            </select>
            <small class="thermal-tip">Para impresoras de 58 mm usa escala 100%, márgenes ninguno y papel continuo/receipt en Windows.</small>
          </label>
          <label>
            <span>Logo URL</span>
            <input v-model="receiptSettings.logo_url" placeholder="https://..." />
          </label>
          <label class="switch-row setting-switch">
            <input v-model="receiptSettings.show_logo" type="checkbox" />
            <span>Mostrar logo</span>
          </label>
          <label class="full-field">
            <span>Texto superior</span>
            <textarea v-model="receiptSettings.header_text" placeholder="Dirección, teléfono, RFC..."></textarea>
          </label>
          <label class="full-field">
            <span>Pie de ticket</span>
            <textarea v-model="receiptSettings.footer_text" placeholder="Gracias por su compra, redes sociales, políticas..."></textarea>
          </label>
          <label class="full-field">
            <span>Pie de comanda</span>
            <textarea v-model="receiptSettings.kitchen_footer_text" placeholder="Notas internas para cocina/barra"></textarea>
          </label>
          <label class="switch-row setting-switch">
            <input v-model="receiptSettings.show_tax" type="checkbox" />
            <span>Mostrar IVA</span>
          </label>
          <label class="switch-row setting-switch">
            <input v-model="receiptSettings.show_customer" type="checkbox" />
            <span>Mostrar cliente</span>
          </label>
          <label class="switch-row setting-switch">
            <input v-model="receiptSettings.auto_print_paid_ticket" type="checkbox" />
            <span>Auto imprimir al pagar</span>
          </label>
        </div>

        <div class="modal-actions">
          <button class="pos-btn ghost" @click="printReceiptPreview">Vista previa</button>
          <button class="pos-btn primary" @click="saveReceiptSettings(true)">Guardar formato</button>
        </div>
      </section>
    </div>

    <div v-if="showTables" class="modal-backdrop" @click.self.stop.prevent>
      <section class="modal side-modal">
        <div class="modal-head"><div><small>Mesas</small><h2>Selecciona una mesa</h2></div><button class="round-icon" @click="showTables = false">×</button></div>
        <div class="table-grid">
          <button v-for="table in tables" :key="table.id" class="table-card" :class="table.status" @click="assignTable(table)">
            <strong>{{ table.name }}</strong>
            <small>{{ table.status }}</small>
          </button>
        </div>
        <div class="inline-form">
          <input v-model="newTable.name" placeholder="Nueva mesa" />
          <input v-model.number="newTable.capacity" type="number" min="1" placeholder="Cap." />
          <button class="pos-btn primary" @click="createTable">Crear</button>
        </div>
      </section>
    </div>

    <div v-if="showCustomers" class="modal-backdrop" @click.self.stop.prevent>
      <section class="modal side-modal customer-assign-modal">
        <div class="modal-head"><div><small>Clientes</small><h2>Asignar cliente</h2></div><button class="round-icon" @click="showCustomers = false">×</button></div>
        <router-link v-if="isPosAdmin" to="/pv/clientes-beneficios" class="pos-btn ghost" @click="showCustomers = false">👥 Administrar descuentos permanentes</router-link>
        <div class="flow-note loyalty-flow-note compact-note">
          <strong>Cliente de la cuenta</strong>
          <span>Al quedar asignado antes de cobrar, el PV suma puntos automáticamente al pagar. Si el cliente no aparece localmente, usa Buscar en Afrogato.</span>
        </div>
        <div class="customer-search customer-search-expanded">
          <input v-model="customerSearch" placeholder="Nombre, correo, teléfono o QR" @keyup.enter="loadCustomers" />
          <button class="pos-btn ghost" @click="loadCustomers">Buscar local</button>
          <button class="pos-btn ghost" @click="openQrScanner('customer')">📷 Escanear QR</button>
          <button class="pos-btn ghost" @click="lookupCustomerQr">Usar código</button>
          <button class="pos-btn ghost" :disabled="loyaltyExternalLoading" @click="openExternalCustomerSearchFromPos">
            {{ loyaltyExternalLoading ? 'Buscando...' : 'Buscar en Afrogato' }}
          </button>
        </div>
        <div class="customer-list">
          <button v-for="customer in customers" :key="customer.id" class="customer-card" @click="assignCustomer(customer)">
            <strong>{{ customer.name }}</strong>
            <small>{{ customer.email || customer.phone || customer.qr_token }}</small>
            <span>{{ formatPoints(customer.points_balance || 0) }} pts</span>
          </button>
        </div>
        <div class="new-customer">
          <strong>Alta rápida</strong>
          <input v-model="newCustomer.name" placeholder="Nombre" />
          <input v-model="newCustomer.phone" placeholder="Teléfono" />
          <input v-model="newCustomer.email" placeholder="Correo" />
          <button class="pos-btn primary" @click="createCustomer">Crear y asignar</button>
        </div>
      </section>
    </div>


    <div v-if="showPointAdjust" class="modal-backdrop" @click.self.stop.prevent>
      <section class="modal point-adjust-modal">
        <div class="modal-head">
          <div><small>Lealtad</small><h2>Asignar puntos manuales</h2></div>
          <button class="round-icon" @click="showPointAdjust = false">×</button>
        </div>
        <p class="admin-note">Usa esta opción solo para cortesías o correcciones. Queda registrado en auditoría y en la wallet como puntos asignados por el negocio.</p>
        <div class="customer-search customer-search-expanded">
          <input v-model="pointAdjustSearch" placeholder="Nombre, correo o teléfono" @keyup.enter="searchPointCustomers" />
          <button class="pos-btn ghost" @click="searchPointCustomers">Buscar</button>
        </div>
        <div class="customer-list point-customer-list">
          <button v-for="customer in pointAdjustResults" :key="customer.id" class="customer-card" :class="{ active: pointAdjustCustomer && Number(pointAdjustCustomer.id) === Number(customer.id) }" @click="pointAdjustCustomer = customer">
            <strong>{{ customer.name }}</strong>
            <small>{{ customer.email || customer.phone }}</small>
            <span>{{ formatPoints(customer.points_balance || 0) }} pts</span>
          </button>
        </div>
        <div class="editor-grid two-cols point-form-grid">
          <label>
            <span>Puntos a asignar</span>
            <input v-model.number="pointAdjustForm.points" type="number" min="0" step="0.01" />
          </label>
          <label>
            <span>Motivo</span>
            <input v-model.trim="pointAdjustForm.reason" placeholder="Ej. cortesía, compensación, ajuste" />
          </label>
        </div>
        <div class="modal-actions">
          <button class="pos-btn ghost" @click="showPointAdjust = false">Cancelar</button>
          <button class="pos-btn primary" :disabled="!pointAdjustCustomer" @click="submitPointAdjustment">Asignar puntos</button>
        </div>
      </section>
    </div>

    <div v-if="showItemDiscount" class="modal-backdrop" @click.self.stop.prevent>
      <section class="modal small-modal">
        <div class="modal-head">
          <div><small>Descuento por producto</small><h2>{{ itemDiscountTarget?.name }}</h2></div>
          <button class="round-icon" @click="showItemDiscount = false">×</button>
        </div>
        <label>
          <span>Porcentaje</span>
          <input v-model.number="itemDiscountForm.percent" type="number" min="0" max="100" step="0.01" placeholder="Ej. 10" />
        </label>
        <label>
          <span>O monto fijo</span>
          <input v-model.number="itemDiscountForm.amount" type="number" min="0" step="0.01" placeholder="Ej. 15.00" />
        </label>
        <label>
          <span>Motivo</span>
          <input v-model.trim="itemDiscountForm.reason" placeholder="Ej. cortesía por incidente" />
        </label>
        <div class="modal-actions">
          <button class="pos-btn ghost" @click="showItemDiscount = false">Cancelar</button>
          <button class="pos-btn primary" @click="applyItemDiscount">Aplicar descuento</button>
        </div>
      </section>
    </div>


    <div v-if="showLoyaltyScan" class="modal-backdrop" @click.self.stop.prevent>
      <section class="modal loyalty-modal">
        <div class="modal-head">
          <div><small>Lealtad</small><h2>Escanear wallet o canje</h2></div>
          <button class="round-icon" @click="showLoyaltyScan = false">×</button>
        </div>
        <div class="loyalty-hero">
          <span>⭐</span>
          <div>
            <strong>Wallet, cliente o cupón de canje</strong>
            <small>Úsalo cuando el cliente muestre su QR al cobrar, cuando quieras buscarlo por teléfono/correo o cuando llegue con un cupón de puntos.</small>
          </div>
        </div>
        <div class="loyalty-flow-grid">
          <div><strong>Wallet</strong><span>Asigna el cliente a la cuenta activa y al pagar suma puntos.</span></div>
          <div><strong>Canje</strong><span>Convierte el QR de recompensa en una orden con 100% de descuento.</span></div>
          <div><strong>Ticket después</strong><span>Si no trae wallet, puede reclamar puntos desde Afrogato con el UUID del ticket.</span></div>
        </div>
        <div class="customer-search loyalty-search-box loyalty-search-actions">
          <input ref="loyaltyScanInput" v-model="loyaltyScanCode" placeholder="QR, nombre, correo o teléfono" @keyup.enter="scanLoyaltyCode" />
          <button class="pos-btn primary" @click="scanLoyaltyCode">Validar</button>
          <button class="pos-btn ghost" @click="openQrScanner('loyalty')">📷 Cámara QR</button>
          <button class="pos-btn ghost" :disabled="loyaltyExternalLoading" @click="searchExternalLoyaltyCustomers">
            {{ loyaltyExternalLoading ? 'Buscando...' : 'Buscar en Afrogato' }}
          </button>
        </div>
        <article v-if="loyaltyExternalResults.length || loyaltyExternalErrors.length" class="loyalty-result-card external-result">
          <small>Búsqueda externa bajo demanda</small>
          <h3>Resultados de Afrogato</h3>
          <p>Solo se consulta cuando presionas el botón para evitar peticiones innecesarias.</p>
          <div v-if="loyaltyExternalErrors.length" class="loyalty-external-errors">
            <div v-for="err in loyaltyExternalErrors" :key="err.site_id || err.site_name">⚠ {{ err.site_name || 'Sitio externo' }}: {{ err.error }}</div>
          </div>
          <div class="loyalty-customer-results">
            <button v-for="customer in loyaltyExternalResults" :key="`${customer.external_site_id}-${customer.external_user_id}`" class="loyalty-customer-row external" @click="importExternalLoyaltyCustomer(customer)">
              <span>{{ customer.name }}</span>
              <small>{{ customer.email || customer.phone || customer.wallet_public_id }} · {{ customer.external_site_name }}</small>
              <strong>{{ formatPoints(customer.points_balance || 0) }} pts · {{ customer.match_score || 0 }}%</strong>
            </button>
          </div>
        </article>
        <article v-if="loyaltyCustomerResults.length" class="loyalty-result-card wallet-result">
          <small>Clientes encontrados</small>
          <div class="loyalty-customer-results">
            <button v-for="customer in loyaltyCustomerResults" :key="customer.id" class="loyalty-customer-row" @click="applyCustomerFromLoyalty(customer)">
              <span>{{ customer.name }}</span>
              <small>{{ customer.email || customer.phone || customer.wallet_public_id || customer.qr_token }}</small>
              <strong>{{ formatPoints(customer.points_balance || 0) }} pts</strong>
            </button>
          </div>
        </article>
        <article v-if="loyaltyScanResult && loyaltyScanResult.type === 'wallet'" class="loyalty-result-card wallet-result">
          <small>Wallet encontrada</small>
          <h3>{{ loyaltyScanResult.customer.name }}</h3>
          <p>{{ loyaltyScanResult.customer.email || loyaltyScanResult.customer.phone || 'Cliente sin contacto' }}</p>
          <div class="points-big">{{ formatPoints(loyaltyScanResult.customer.points_balance || 0) }} pts</div>
          <button class="pos-btn primary full" @click="applyScannedCustomer">Asignar a la cuenta activa</button>
        </article>
        <article v-if="loyaltyScanResult && loyaltyScanResult.type === 'redemption'" class="loyalty-result-card redemption-result">
          <small>Canje validado</small>
          <h3>{{ loyaltyScanResult.redemption.customer_name || 'Cliente con wallet' }}</h3>
          <p>{{ formatPoints(loyaltyScanResult.redemption.points_spent) }} puntos · {{ money(loyaltyScanResult.redemption.discount_amount) }} en productos</p>
          <div class="redemption-items">
            <div v-for="item in loyaltyScanResult.redemption.items" :key="item.product_token || item.product_name || item.name">
              <span>
                <b>{{ item.quantity || 1 }} × {{ item.product_name || item.name }}</b>
                <em v-for="modifier in item.modifiers || []" :key="modifier.option_id || modifier.name">{{ modifier.preparation_note || modifier.name }}</em>
              </span>
              <small>{{ formatPoints(item.unit_points || item.unit_mxn_value || 0) }} pts</small>
            </div>
          </div>
          <button class="pos-btn primary full" @click="redeemScannedReward">Crear orden 100% descuento</button>
        </article>
      </section>
    </div>


    <div v-if="showCoupon" class="modal-backdrop" @click.self.stop.prevent>
      <section class="modal discount-modal coupon-modal" role="dialog" aria-modal="true" aria-label="Aplicar cupón único">
        <div class="modal-head">
          <div><small>Descuento por código</small><h2>Escanear cupón</h2></div>
          <button class="round-icon" :disabled="couponBusy" @click="showCoupon=false">×</button>
        </div>
        <p>El código se reserva en esta cuenta; se consume al registrar el primer abono. Los códigos no se acumulan con descuentos manuales ni promociones automáticas.</p>
        <div v-if="activeTicket?.coupon" class="coupon-current">
          <strong>{{ activeTicket.coupon.campaign_name }}</strong>
          <span>{{ activeTicket.coupon.status==='redeemed' ? 'Ya canjeado' : 'Reservado' }} · -{{ money(activeTicket.coupon.discount_amount) }}</span>
          <button v-if="activeTicket.coupon.status==='reserved' && Number(activeTicket.paid_total||0)===0" class="pos-btn ghost danger" :disabled="couponBusy" @click="removeCoupon">Quitar cupón</button>
        </div>
        <template v-else>
          <div class="coupon-input-row">
            <input ref="couponCodeInput" v-model.trim="couponCode" placeholder="Escanea el QR o escribe el código" autocomplete="off" maxlength="500" @keyup.enter="previewCoupon" />
            <button class="pos-btn ghost" :disabled="couponBusy" @click="openQrScanner('coupon')">📷 Escanear</button>
            <button class="pos-btn primary" :disabled="couponBusy || !couponCode" @click="previewCoupon">Validar</button>
          </div>
          <div v-if="couponPreview" class="coupon-preview">
            <h3>{{ couponPreview.campaign.name }}</h3>
            <p>{{ couponPreview.campaign.discount_type==='percent' ? couponPreview.campaign.discount_value+'%' : money(couponPreview.campaign.discount_value)+' por unidad' }} · {{ couponPreview.campaign.application_mode==='choose_one' ? 'Selecciona un producto' : 'Todos los productos válidos dentro del límite' }}</p>
            <p v-if="couponPreview.campaign.requires_customer && !activeTicket.customer_id" class="coupon-warning">Este cupón requiere identificar primero al cliente. Usa «Asignar cliente» o escanea su Wallet.</p>
            <label v-if="couponPreview.campaign.application_mode==='choose_one'">Producto elegible
              <select v-model.number="couponSelectedItemId">
                <option :value="0">Selecciona un producto</option>
                <option v-for="it in couponPreview.eligible_items" :key="it.id" :value="it.id">{{ it.name }} · {{ money(it.discount_one) }} de descuento por unidad</option>
              </select>
            </label>
            <label v-if="couponPreview.campaign.application_mode==='choose_one'">Unidades a descontar
              <input type="number" min="1" :max="couponQuantityLimit" step="1" v-model.number="couponQuantity" />
              <small>Máximo {{ couponQuantityLimit }} unidad(es) para el artículo elegido.</small>
            </label>
            <div v-if="couponPreview.campaign.application_mode==='all_eligible'" class="coupon-eligible-list">
              <span v-for="it in couponPreview.eligible_items" :key="it.id">{{ it.name }} × {{ it.quantity }}</span>
              <strong>Descuento estimado {{ money(couponPreview.discount_estimate) }}</strong>
            </div>
            <button class="pos-btn primary full" :disabled="couponBusy || (couponPreview.campaign.application_mode==='choose_one' && (!couponSelectedItemId || couponQuantity<1 || couponQuantity>couponQuantityLimit))" @click="applyCoupon">Reservar y aplicar cupón</button>
          </div>
        </template>
        <p v-if="couponError" class="coupon-warning" role="alert">{{ couponError }}</p>
        <small>El descuento se verifica de nuevo en el servidor al cobrar. Una cuenta con pagos ya no permite cambiar el cupón.</small>
      </section>
    </div>

    <div v-if="showQrScanner" class="modal-backdrop" @click.self.stop.prevent>
      <section class="modal qr-scanner-modal">
        <div class="modal-head">
          <div><small>Escáner QR</small><h2>{{ qrScannerTitle() }}</h2></div>
          <button class="round-icon" @click="closeQrScanner">×</button>
        </div>
        <div class="qr-scanner-stage">
          <div id="gt-qr-reader" class="qr-reader-box"></div>
          <div class="qr-scanner-frame"></div>
        </div>
        <p class="qr-scanner-status" :class="qrScannerError ? 'error' : ''">
          {{ qrScannerError || qrScannerStatus || 'Preparando cámara...' }}
        </p>
        <div class="manual-qr-fallback">
          <input v-model="qrScannerManualCode" placeholder="También puedes pegar o escribir el código QR" @keyup.enter="applyManualQrCode" />
          <button class="pos-btn primary" @click="applyManualQrCode">Usar código</button>
        </div>
        <small class="qr-help">La cámara usa un lector QR compatible con navegadores móviles. Si falla el permiso, puedes pegar el código o usar un lector QR físico.</small>
      </section>
    </div>


    <div v-if="showOptionAdmin" class="modal-backdrop" @click.self.stop.prevent>
      <section class="modal options-admin-modal">
        <div class="modal-head">
          <div><small>Catálogo PV</small><h2>Productos, categorías y extras</h2></div>
          <button class="round-icon" @click="showOptionAdmin = false">×</button>
        </div>

        <div class="catalog-tabs">
          <button class="pos-btn" :class="catalogAdminTab === 'products' ? 'primary' : 'ghost'" @click="catalogAdminTab = 'products'">Productos y categorías</button>
          <button class="pos-btn" :class="catalogAdminTab === 'extras' ? 'primary' : 'ghost'" @click="catalogAdminTab = 'extras'">Extras compartidos</button>
        </div>

        <div v-if="catalogAdminTab === 'products'" class="catalog-admin-layout">
          <aside class="catalog-side-panel">
            <div class="section-title-row">
              <strong>Categorías</strong>
              <button class="pos-btn ghost" @click="startNewCategory">Nueva</button>
            </div>
            <div class="category-admin-list">
              <button v-for="category in optionAdminCategories" :key="category.id" class="option-group-card" :class="{ active: Number(categoryForm.id) === Number(category.id) }" @click="editCategory(category)">
                <strong>{{ category.name }}</strong>
                <small>{{ category.category_type || 'pos' }} · orden {{ category.sort_order || 0 }}</small>
              </button>
            </div>
            <div class="catalog-helper-card">
              <strong>{{ categoryForm.id ? 'Categoría seleccionada' : 'Categorías' }}</strong>
              <p>{{ categoryForm.id ? 'Da click de nuevo o usa Editar para modificarla en una ventana clara.' : 'Crea o edita categorías desde una ventana dedicada para evitar confusiones.' }}</p>
              <button class="pos-btn primary full" @click="showCategoryEditor = true">{{ categoryForm.id ? 'Editar categoría' : 'Nueva categoría' }}</button>
            </div>
          </aside>

          <section class="product-admin-panel">
            <div class="section-title-row">
              <div>
                <strong>{{ productAdminForm.id ? 'Editar producto' : 'Nuevo producto' }}</strong>
                <small>Desde aquí puedes darlo de alta dentro de una categoría y asignarle extras.</small>
              </div>
              <button class="pos-btn ghost" @click="startNewProduct">Nuevo producto</button>
            </div>

            <div class="catalog-helper-card product-helper">
              <strong>{{ productAdminForm.id ? 'Producto seleccionado' : 'Productos' }}</strong>
              <p>{{ productAdminForm.id ? 'Edita este producto en una ventana dedicada para ver claramente qué estás modificando.' : 'Presiona Nuevo producto o selecciona uno de la lista para abrir su editor.' }}</p>
              <button class="pos-btn primary" @click="showProductEditor = true">{{ productAdminForm.id ? 'Editar producto' : 'Nuevo producto' }}</button>
            </div>

            <div class="product-admin-list">
              <article v-for="category in optionAdminCategories" :key="`cat-${category.id}`" class="product-category-section">
                <header><strong>{{ category.name }}</strong><small>{{ optionAdminProducts.filter((product) => Number(product.category_id) === Number(category.id)).length }} producto(s)</small></header>
                <button v-for="product in optionAdminProducts.filter((item) => Number(item.category_id) === Number(category.id))" :key="product.id" class="product-admin-row" @click="editProductAdmin(product)">
                  <span><strong>{{ product.name }}</strong><small>{{ money(product.sale_price) }} · {{ (product.group_ids || []).length }} grupo(s) de extras</small></span>
                  <em>{{ product.active_pos ? 'PV' : 'Oculto PV' }}</em>
                </button>
              </article>
              <article v-if="optionAdminProducts.some((product) => !product.category_id)" class="product-category-section">
                <header><strong>Sin categoría</strong><small>{{ optionAdminProducts.filter((product) => !product.category_id).length }} producto(s)</small></header>
                <button v-for="product in optionAdminProducts.filter((item) => !item.category_id)" :key="product.id" class="product-admin-row" @click="editProductAdmin(product)">
                  <span><strong>{{ product.name }}</strong><small>{{ money(product.sale_price) }} · {{ (product.group_ids || []).length }} grupo(s) de extras</small></span>
                  <em>{{ product.active_pos ? 'PV' : 'Oculto PV' }}</em>
                </button>
              </article>
            </div>
          </section>
        </div>

        <div v-if="showCategoryEditor" class="nested-modal-backdrop" @click.self="showCategoryEditor = false">
          <section class="modal nested-editor-modal">
            <div class="modal-head">
              <div><small>Catálogo PV</small><h2>{{ categoryForm.id ? 'Editar categoría' : 'Nueva categoría' }}</h2></div>
              <button class="round-icon" @click="showCategoryEditor = false">×</button>
            </div>
            <div class="editor-grid">
              <label class="full-field">
                <span>Nombre categoría</span>
                <input v-model="categoryForm.name" placeholder="Ej. Bebidas" />
              </label>
              <label>
                <span>Orden</span>
                <input v-model.number="categoryForm.sort_order" type="number" step="1" />
              </label>
              <label>
                <span>Tipo</span>
                <select v-model="categoryForm.category_type">
                  <option value="pos">PV</option>
                  <option value="menu">Menú digital</option>
                  <option value="general">General</option>
                  <option value="extra">Extras</option>
                </select>
              </label>
              <label><span>Color suave</span><input v-model="categoryForm.color" type="color" /></label>
              <label><span>Tamaño de tarjeta</span><select v-model="categoryForm.tile_size"><option value="small">Compacto</option><option value="normal">Normal</option><option value="large">Grande</option></select></label>
              <label><span>Estado</span><select v-model="categoryForm.status"><option value="active">Activa</option><option value="inactive">Archivada</option></select></label>
              <label class="full-field">
                <span>Descripción</span>
                <textarea v-model="categoryForm.description" placeholder="Opcional"></textarea>
              </label>
            </div>
            <div class="modal-actions">
              <button class="pos-btn ghost" @click="showCategoryEditor = false">Cancelar</button>
              <button v-if="categoryForm.id" class="pos-btn ghost" @click="archiveCatalog('categories', categoryForm.id)">Archivar categoría</button>
              <button :aria-busy="posBusy.saveCategory || undefined" :disabled="posBusy.saveCategory" class="pos-btn primary" @click="saveCategory">Guardar categoría</button>
            </div>
          </section>
        </div>

        <div v-if="showProductEditor" class="nested-modal-backdrop" @click.self="showProductEditor = false">
          <section class="modal nested-editor-modal product-editor-modal">
            <div class="modal-head">
              <div><small>Catálogo PV</small><h2>{{ productAdminForm.id ? 'Editar producto' : 'Nuevo producto' }}</h2></div>
              <button class="round-icon" @click="showProductEditor = false">×</button>
            </div>
            <div class="product-editor-grid">
              <label class="full-field">
                <span>Nombre producto</span>
                <input v-model="productAdminForm.name" placeholder="Ej. Latte vainilla" />
              </label>
              <label>
                <span>Categoría</span>
                <select v-model="productAdminForm.category_id">
                  <option :value="null">Sin categoría</option>
                  <option v-for="category in optionAdminCategories" :key="category.id" :value="category.id">{{ category.name }}</option>
                </select>
              </label>
              <label>
                <span>Área preparación</span>
                <select v-model="productAdminForm.preparation_area_id">
                  <option :value="null">Sin área</option>
                  <option v-for="area in optionAdminPreparationAreas" :key="area.id" :value="area.id">{{ area.name }}</option>
                </select>
              </label>
              <label>
                <span>SKU</span>
                <input v-model="productAdminForm.sku" placeholder="Opcional" />
              </label>
              <label>
                <span>Código barras</span>
                <input v-model="productAdminForm.barcode" placeholder="Opcional" />
              </label>
              <label>
                <span>Precio venta</span>
                <input v-model.number="productAdminForm.sale_price" type="number" min="0" step="0.01" />
              </label>
              <label>
                <span>Costo</span>
                <input v-model.number="productAdminForm.cost" type="number" min="0" step="0.01" />
              </label>
              <label class="switch-row setting-switch"><input v-model="productAdminForm.track_inventory" type="checkbox"/><span>Controlar unidades en almacén</span></label>
              <label class="switch-row setting-switch"><input v-model="productAdminForm.allow_fraction" type="checkbox"/><span>Permitir fracciones</span></label>
              <label><span>Stock insuficiente</span><select v-model="productAdminForm.stock_policy"><option value="inherit">Usar configuración general</option><option value="warn">Avisar y permitir</option><option value="block">Bloquear venta</option></select></label>
              <label><span>Color del producto</span><input v-model="productAdminForm.color" type="color"/><button type="button" @click="productAdminForm.color = ''">Usar color de categoría</button></label>
              <label><span>Estado</span><select v-model="productAdminForm.status"><option value="active">Activo</option><option value="inactive">Archivado</option></select></label>
              <label>
                <span>Tipo producto</span>
                <select v-model="productAdminForm.product_type">
                  <option value="sellable">Vendible</option>
                  <option value="ingredient">Ingrediente</option>
                  <option value="recipe">Receta</option>
                  <option value="both">Vendible + ingrediente</option>
                  <option value="timed_service">Servicio de tiempo</option>
                </select>
              </label>
              <label>
                <span>Minutos que agrega</span>
                <input v-model.number="productAdminForm.time_minutes" type="number" min="0" step="1" placeholder="0 = sin tiempo" />
              </label>
              <label>
                <span>IVA / impuesto</span>
                <input v-model.number="productAdminForm.tax_rate" type="number" min="0" step="0.0001" placeholder="0.1600" />
              </label>
              <label class="switch-row setting-switch">
                <input v-model="productAdminForm.has_tax" type="checkbox" />
                <span>Aplicar impuesto</span>
              </label>
              <label class="switch-row setting-switch">
                <input v-model="productAdminForm.active_pos" type="checkbox" />
                <span>Visible en PV</span>
              </label>
              <label class="switch-row setting-switch">
                <input v-model="productAdminForm.active_menu" type="checkbox" />
                <span>Visible en menú digital</span>
              </label>
              <label v-if="Number(productAdminForm.time_minutes || 0) > 0" class="switch-row setting-switch">
                <input v-model="productAdminForm.send_to_kitchen" type="checkbox" />
                <span>También enviar a comanda</span>
              </label>
            </div>
            <div class="product-linker">
              <div class="section-title-row">
                <strong>Extras/modificadores para este producto</strong>
                <small>{{ productAdminForm.group_ids.length }} grupo(s)</small>
              </div>
              <div class="product-check-grid compact">
                <label v-for="group in optionAdminGroups" :key="group.id" class="product-check">
                  <input v-model="productAdminForm.group_ids" type="checkbox" :value="group.id" />
                  <span>{{ group.name }} <small>{{ group.selection_type === 'multiple' ? 'Múltiple' : 'Único' }}</small></span>
                </label>
              </div>
            </div>
            <div class="modal-actions">
              <button class="pos-btn ghost" @click="showProductEditor = false">Cancelar</button>
              <button v-if="productAdminForm.id" class="pos-btn ghost" @click="archiveCatalog('products', productAdminForm.id)">Archivar producto</button>
              <button :aria-busy="posBusy.saveProductAdmin || undefined" :disabled="posBusy.saveProductAdmin" class="pos-btn primary" @click="saveProductAdmin">Guardar producto</button>
            </div>
          </section>
        </div>

        <div v-if="catalogAdminTab === 'extras'" class="options-admin-layout">
          <aside class="option-group-panel">
            <button class="pos-btn primary full" @click="startNewOptionGroup">Nuevo grupo</button>
            <input v-model="extrasSearch" placeholder="Buscar grupo u opción" aria-label="Buscar extras" /><button v-for="group in filteredOptionGroups" :key="group.id" class="option-group-card" :class="{ active: Number(optionGroupForm.id) === Number(group.id) }" @click="editOptionGroup(group)">
              <strong>{{ group.name }}</strong>
              <small>{{ group.selection_type === 'multiple' ? 'Múltiple' : 'Único' }} · {{ (group.product_ids || []).length }} producto(s)</small>
            </button>
          </aside>

          <section class="option-editor">
            <div class="editor-grid">
              <label>
                <span>Nombre del grupo</span>
                <input v-model="optionGroupForm.name" placeholder="Ej. Tipo de leche" />
              </label>
              <label>
                <span>Código</span>
                <input v-model="optionGroupForm.code" placeholder="tipo_de_leche" />
              </label>
              <label class="switch-row setting-switch"><input v-model="optionGroupForm.sell_standalone" type="checkbox"/><span>Vender opciones como extras independientes</span></label>
              <label><span>Área de extras</span><select v-model="optionGroupForm.standalone_area_id"><option :value="null">General</option><option v-for="area in optionAdminPreparationAreas" :key="area.id" :value="area.id">{{ area.name }}</option></select></label>
              <label><span>Mínimo de opciones</span><input v-model.number="optionGroupForm.min_select" type="number" min="0"/></label>
              <label><span>Máximo (vacío: sin límite)</span><input v-model.number="optionGroupForm.max_select" type="number" min="0"/></label>
              <label><span>Estado del grupo</span><select v-model="optionGroupForm.status"><option value="active">Activo</option><option value="inactive">Archivado</option></select></label>
              <label>
                <span>Selección</span>
                <select v-model="optionGroupForm.selection_type">
                  <option value="single">Una opción</option>
                  <option value="multiple">Varias opciones</option>
                </select>
              </label>
              <label class="switch-row setting-switch">
                <input v-model="optionGroupForm.is_required" type="checkbox" />
                <span>Obligatorio</span>
              </label>
            </div>

            <div class="option-rows">
              <div class="section-title-row">
                <strong>Opciones compartidas</strong>
                <button class="pos-btn ghost" @click="addOptionRow">Agregar opción</button>
              </div>
              <div v-for="(option, index) in optionGroupForm.options" :key="option.local_key || option.id || index" class="option-row-edit">
                <input v-model="option.name" placeholder="Nombre" />
                <input v-model.number="option.price_delta" type="number" step="0.01" placeholder="$" />
                <input v-model="option.preparation_note" placeholder="Texto para comanda" />
                <select v-model="option.availability_status" aria-label="Disponibilidad"><option value="visible">Disponible</option><option value="sold_out">Agotado</option><option value="hidden">Oculto</option></select>
                <select v-model="option.status" aria-label="Estado"><option value="active">Activo</option><option value="inactive">Archivado</option></select>
                <label class="tiny-check"><input v-model="option.is_default" type="checkbox"/> predeterminada</label>
                <label class="tiny-check"><input v-model="option.affects_price" type="checkbox" /> cobra</label>
                <button class="round-icon danger" @click="removeOptionRow(index)">×</button>
              </div>
            </div>

            <div class="product-linker">
              <div class="section-title-row">
                <strong>Activar este grupo en productos</strong>
                <small>{{ optionGroupForm.product_ids.length }} seleccionado(s)</small>
              </div>
              <div class="product-check-grid">
                <label v-for="product in optionAdminProducts" :key="product.id" class="product-check">
                  <input v-model="optionGroupForm.product_ids" type="checkbox" :value="product.id" />
                  <span>{{ product.name }} <small>{{ product.category_name || 'Sin categoría' }}</small></span>
                </label>
              </div>
            </div>

            <div class="modal-actions">
              <button class="pos-btn ghost" @click="loadOptionAdmin">Recargar</button>
              <button v-if="optionGroupForm.id" class="pos-btn ghost" @click="archiveCatalog('option-groups', optionGroupForm.id)">Archivar grupo</button>
              <button :aria-busy="posBusy.saveOptionGroup || undefined" :disabled="posBusy.saveOptionGroup" class="pos-btn primary" @click="saveOptionGroup">Guardar extras</button>
            </div>
          </section>
        </div>
      </section>
    </div>

    <div v-if="showRefundModal" class="modal-backdrop" @click.self.stop.prevent>
      <section class="modal">
        <div class="modal-head">
          <div><small>Devolución autorizada</small><h2>Devolver un pago</h2></div>
          <button class="round-icon" @click="showRefundModal = false">×</button>
        </div>
        <p>El pago original quedará intacto. Solo registra la devolución cuando hayas entregado el efectivo o completado el reembolso externo.</p>
        <label>Movimiento original
          <select v-model.number="refundForm.payment_id" @change="selectRefundPayment">
            <option v-for="payment in refundablePayments" :key="payment.id" :value="Number(payment.id)">
              #{{ payment.id }} · {{ payment.payment_method_code }} · Disponible {{ money(refundAvailable(payment)) }}
            </option>
          </select>
        </label>
        <label>Importe a devolver
          <input v-model.number="refundForm.amount" type="number" min="0.01" step="0.01" />
        </label>
        <label>Motivo (obligatorio)
          <textarea v-model="refundForm.reason" placeholder="Explica por qué se devuelve el dinero" />
        </label>
        <label v-if="!isPosAdmin">PIN de administrador
          <input v-model="refundForm.admin_pin" type="password" inputmode="numeric" autocomplete="off" />
        </label>
        <label v-if="refundSelectedPayment && refundSelectedPayment.payment_method_code !== 'cash'" class="check">
          <input v-model="refundForm.external_refund_confirmed" type="checkbox" /> Confirmo que el reembolso externo YA se realizó
        </label>
        <p><strong>Atención:</strong> revisa manualmente los puntos de lealtad y el inventario. Registrar una devolución monetaria no repone productos ni revierte la wallet.</p>
        <div class="modal-actions">
          <button class="pos-btn ghost" @click="showRefundModal = false">Cancelar</button>
          <button class="pos-btn primary" :disabled="refundSaving || !refundablePayments.length" @click="submitRefund">{{ refundSaving ? 'Registrando...' : 'Confirmar devolución' }}</button>
        </div>
      </section>
    </div>

    <div v-if="kitchenTickets.length || receiptPrintJobs.length" class="modal-backdrop" @click.self.stop.prevent>
      <section class="modal command-modal">
        <div class="modal-head">
          <div><small>Cola persistente de impresión</small><h2>{{ kitchenTickets.length }} comanda(s) · {{ receiptPrintJobs.length }} comprobante(s)</h2></div>
          <button class="round-icon" @click="kitchenTickets = []; receiptPrintJobs = []">×</button>
        </div>
        <p>“Enviada a Chrome” NO demuestra que haya salido papel. Confirma cada documento después de verificarlo físicamente. Esta cola se recupera desde el servidor.</p>
        <div v-if="receiptPrintJobs.length" class="command-list">
          <article v-for="job in receiptPrintJobs" :key="`receipt-${job.ticket_id}`" class="command-ticket">
            <header><strong>Ticket #{{ job.ticket_id }}</strong><small>{{ kitchenPrintStatusLabel(job.print_status) }} · {{ Number(job.print_attempts || 0) }} intento(s)</small></header>
            <small v-if="job.print_error">{{ job.print_error }}</small>
            <div class="modal-actions">
              <button class="pos-btn ghost" :disabled="job.print_busy" @click="printPendingReceipt(job)">Imprimir / reintentar</button>
              <button v-if="job.print_status !== 'printed'" class="pos-btn primary" @click="confirmReceiptPrinted(job)">Confirmar papel impreso</button>
            </div>
          </article>
        </div>
        <div class="command-list">
          <article v-for="command in kitchenTickets" :key="command.id" class="command-ticket">
            <header>
              <strong>{{ command.area_name }}</strong>
              <small>{{ command.order_label }} · {{ shortTime(command.created_at) }} · {{ kitchenPrintStatusLabel(command.print_status) }} · {{ Number(command.print_attempts || 0) }} intento(s)</small>
              <small v-if="command.type === 'cancellation'">CANCELACIÓN · {{ command.reason }}</small>
              <small v-if="command.print_error">{{ command.print_error }}</small>
            </header>
            <div v-for="item in command.items" :key="item.id" class="command-item">
              <span>{{ item.quantity }} × {{ item.name }}</span>
              <small v-for="mod in item.modifiers" :key="mod.option_id || mod.name">{{ mod.preparation_note || mod.name }}</small>
              <small v-if="item.notes">Nota: {{ item.notes }}</small>
            </div>
            <div class="modal-actions">
              <button :disabled="command.print_busy" class="pos-btn ghost" @click="printCommand(command)">{{ command.print_status === 'printed' ? 'Reimprimir copia' : 'Imprimir / reintentar' }}</button>
              <button v-if="command.print_status !== 'printed' && /^\d+$/.test(String(command.id))" class="pos-btn primary" @click="confirmCommandPrinted(command)">Confirmar papel impreso</button>
            </div>
          </article>
        </div>
      </section>
    </div>
  </section>
</template>

<script>
import api, { posScope, syncPos, posConnection, requireEmptyQueueBeforeRegisterSwitch, refreshPosPendingStatus } from '@/services/posApi';
import rawApi from '@/services/api';
import {registerIdentity,getSelectedRegister,setSelectedRegister,setMultiRegisterEnabled,isMultiRegisterEnabled,isRegisterModeKnown} from '@/services/pos/registerSelection.mjs';
import { productKey, readQuickList, toggleQuickKey, rememberQuickKey } from '@/services/posQuickAccess.mjs';
import ProductAvailabilityCard from '@/components/pos/ProductAvailabilityCard.vue';
import posInteraction from '@/services/posInteraction';
import PosSyncStatus from '@/components/pos/PosSyncStatus.vue';
import { printDocument, qrDataUrl } from '@/services/posPrint';
import { promptText, confirmAction } from '@/services/dialog';
import { Html5Qrcode } from 'html5-qrcode';
import { getSocket, getOrConnectSocket } from '@/services/websocket';
import TimeClockModal from '@/components/hr/TimeClockModal.vue';
import PosTimePanel from '@/components/pos/PosTimePanel.vue';
import PosTimeNotifications from '@/components/pos/PosTimeNotifications.vue';

const POS_TIME_ZONE = 'America/Mexico_City';

function normalizePosDateString(value) {
  let normalized = String(value || '').trim().replace(' ', 'T');
  normalized = normalized.replace(/\.(\d{3})\d+/, '.$1');
  normalized = normalized.replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
  normalized = normalized.replace(/([+-]\d{2})$/, '$1:00');
  if (!/[zZ]$|[+-]\d{2}:\d{2}$/.test(normalized)) {
    normalized += 'Z';
  }
  return normalized;
}

function parsePosDate(value) {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  const parsed = new Date(normalizePosDateString(value));
  return Number.isNaN(parsed.getTime()) ? new Date(value) : parsed;
}

function localDateKey(value = new Date()) {
  const date = parsePosDate(value);
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: POS_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date).reduce((carry, part) => {
    carry[part.type] = part.value;
    return carry;
  }, {});
  return `${parts.year}-${parts.month}-${parts.day}`;
}

function localDateDaysAgo(days) {
  const value = new Date();
  value.setUTCDate(value.getUTCDate() - Number(days || 0));
  return localDateKey(value);
}

export default {
  mixins: [posInteraction],
  name: 'POSView',
  components: { ProductAvailabilityCard, PosSyncStatus, TimeClockModal, PosTimePanel, PosTimeNotifications },
  data() {
    return {
      message: '',
      messageType: 'success',
      register: null,
      cashSession: null,
      multiRegisterEnabled: false,
      registerStatusChecked: false,
      selectedRegisterId: 0,
      registerOptions: [],
      availableCashiers: [],
      assignedCashierId: 0,
      canManageRegisters: false,
      canOperateCash: true,
      showRegisterModal: false,
      registerBusy: false,
      newRegisterName: '',
      openingCash: 0,
      categories: [],
      products: [],
      tables: [],
      customers: [],
      paymentMethods: [],
      tickets: [],
      activeTicket: null,
      items: [],
      payments: [],
      activeTimer: null,
      timeAlerts: [],
      timePollTimer: null,
      timePollBusy: false,
      timerDisplayInterval: null,
      timerDisplayTick: Date.now(),
      activeCategory: null,
      search: '',
      productQuickFilter: 'all',
      favoriteProductIds: [],
      recentProductIds: [],
      quickItemBusy: false,
      editingItemId: null,
      editingItemNotes: '',
      showArrangeDialog: false,
      arrangeMode: 'item',
      arrangeItem: null,
      arrangeTargetId: 0,
      arrangeQuantity: 1,
      arrangingAccounts: false,
      searchBackspaceHits: [],
      productModal: null,
      productForm: { quantity: 1, notes: '', selections: {} },
      ticketMeta: { service_type: 'takeaway', order_label: '' },
      paymentForm: { payment_method_code: 'cash', amount: 0, received_amount: null, reference: '' },
      showPayment: false,
      paymentReferenceExpanded: false,
      paymentPrintRequested: false,
      paymentInFlight: false,
      receiptPrintJobs: [],
      showRefundModal: false,
      refundSaving: false,
      refundPayments: [],
      refundHistory: [],
      refundForm: { ticket_id: 0, payment_id: 0, amount: 0, reason: '', admin_pin: '', external_refund_confirmed: false, request_key: '' },
      couponEnabled: false,
      showCoupon: false,
      couponCode: '',
      couponPreview: null,
      couponSelectedItemId: 0,
      couponQuantity: 1,
      couponBusy: false,
      couponError: '',
      showDiscounts: false,
      discounts: [],
      discountForm: { reason: '' },
      newDiscountForm: { id: null, name: '', discount_type: 'percent', discount_value: 0, requires_reason: false },
      showNewTicketModal: false,
      newTicketMode: 'takeaway',
      newTicketForm: { order_label: '' },
      queuedProduct: null,
      showCashCut: false,
      cashDenominations: [
        { key: 'coin_1', label: 'Monedas $1', value: 1 },
        { key: 'coin_2', label: 'Monedas $2', value: 2 },
        { key: 'coin_5', label: 'Monedas $5', value: 5 },
        { key: 'coin_10', label: 'Monedas $10', value: 10 },
        { key: 'bill_20', label: 'Billetes $20', value: 20 },
        { key: 'bill_50', label: 'Billetes $50', value: 50 },
        { key: 'bill_100', label: 'Billetes $100', value: 100 },
        { key: 'bill_200', label: 'Billetes $200', value: 200 },
        { key: 'bill_500', label: 'Billetes $500', value: 500 },
      ],
      cashCutForm: { cash_session_id: null, cut_type: 'X', closing_cash_amount: 0, denomination_counts: {}, notes: '' },
      cashCutResult: null,
      cashCutError: '',
      showCashExpense: false,
      expenseLoading: false,
      cashExpenses: [],
      expenseSummary: {},
      expenseForm: { cash_session_id: null, amount: 0, payment_method_code: 'cash', category: 'general', concept: '', paid_to: '', receipt_number: '', notes: '' },
      showSales: false,
      salesHistoryMode: false,
      salesLoading: false,
      salesFilters: { scope: 'today', date: localDateKey(), date_from: localDateKey(), date_to: localDateKey(), cut_id: '' },
      salesShowExpenses: false,
      salesExpenseDetails: [],
      salesTickets: [],
      salesCuts: [],
      salesSummary: {},
      salesActiveCut: null,
      showProductReport: false,
      productReportLoading: false,
      productReportFilters: { product_id: '', date_from: localDateDaysAgo(29), date_to: localDateKey() },
      productReportRows: [],
      productReportSummary: {},
      showOptionAdmin: false,
      showCategoryEditor: false,
      showProductEditor: false,
      posMenuOpen: false,
      showTimeClock: false,
      optionAdminLoading: false,
      catalogAdminTab: 'products',
      optionAdminCategories: [],
      optionAdminPreparationAreas: [],
      optionAdminGroups: [],
      optionAdminProducts: [],
      categoryForm: { id: null, name: '', description: '', category_type: 'pos', sort_order: 0 },
      productAdminForm: { id: null, name: '', sku: '', barcode: '', category_id: null, preparation_area_id: null, sale_price: 0, cost: 0, product_type: 'sellable', time_minutes: 0, send_to_kitchen: true, tax_rate: 0, has_tax: false, active_pos: true, active_menu: false, group_ids: [] },
      optionGroupForm: { id: null, name: '', code: '', selection_type: 'single', is_required: false, min_select: 0, max_select: null, position: 0, options: [], product_ids: [] },
      showTables: false,
      showCustomers: false,
      showLoyaltyScan: false,
      showPointAdjust: false,
      pointAdjustSearch: '',
      pointAdjustResults: [],
      pointAdjustCustomer: null,
      pointAdjustForm: { points: 0, reason: '' },
      showItemDiscount: false,
      itemDiscountTarget: null,
      itemDiscountForm: { percent: null, amount: null, reason: '' },
      loyaltyScanCode: '',
      loyaltyScanResult: null,
      loyaltyCustomerResults: [],
      loyaltyExternalResults: [],
      loyaltyExternalErrors: [],
      loyaltyExternalLoading: false,
      showQrScanner: false,
      qrScannerTarget: 'loyalty',
      qrScannerStatus: '',
      qrScannerError: '',
      qrScannerManualCode: '',
      qrScannerStream: null,
      qrScannerTimer: null,
      qrScannerDetector: null,
      qrScannerInstance: null,
      showOpenAccounts: false,
      showReceiptSettings: false,
      showPosTaxSummary: false,
      receiptSettings: {
        business_name: '',
        logo_url: '',
        show_logo: false,
        header_text: '',
        footer_text: 'Gracias por su compra.',
        kitchen_footer_text: '',
        ticket_width_mm: 58,
        show_tax: true,
        show_customer: true,
        auto_print_paid_ticket: false,
      },
      newTable: { name: '', capacity: 4 },
      customerSearch: '',
      newCustomer: { name: '', phone: '', email: '' },
      kitchenTickets: [],
      commandNotifications: [],
      posSocketBound: false,
      socketBindTimer: null,
    };
  },
  computed: {
    quickAccessScope() { return posScope(); },
    couponQuantityLimit() { const item = this.couponPreview?.eligible_items?.find(i => Number(i.id)===Number(this.couponSelectedItemId)); return Math.max(0, Math.min(Math.floor(Number(item?.quantity||0)), Number(this.couponPreview?.campaign?.max_units_per_ticket||100))); },
    contextLabel() {
      const context = this.$store.state.context;
      const company = context?.company?.name || context?.company?.commercial_name || 'Empresa';
      const branch = context?.branch?.name ? ` · ${context.branch.name}` : '';
      return `${company}${branch}`;
    },
    categoriesWithCounts() {
      return this.categories
        .map((category) => ({ ...category, count: this.products.filter((product) => Number(product.category_id) === Number(category.id)).length }))
        .filter((category) => category.count > 0);
    },
    visibleProducts() {
      const q = this.search.trim().toLowerCase();
      const favorites = new Set(this.favoriteProductIds);
      const recents = new Set(this.recentProductIds);
      const products = this.products.filter((product) => {
        const key = productKey(product);
        const byCategory = this.activeCategory && this.productQuickFilter === 'all'
          ? Number(product.category_id) === Number(this.activeCategory.id) : true;
        const byQuick = this.productQuickFilter === 'favorites' ? favorites.has(key)
          : this.productQuickFilter === 'recent' ? recents.has(key) : true;
        const bySearch = q ? `${product.name} ${product.sku || ''} ${product.barcode || ''} ${product.category_name || ''}`.toLowerCase().includes(q) : true;
        return byCategory && byQuick && bySearch;
      });
      if (this.productQuickFilter === 'recent') {
        return products.sort((a, b) => this.recentProductIds.indexOf(productKey(a)) - this.recentProductIds.indexOf(productKey(b)));
      }
      return products;
    },
    activeItems() {
      return this.items.filter((item) => item.status === 'active');
    },
    eligibleAccountTargets() {
      if (!this.activeTicket || Number(this.activeTicket.paid_total || 0) > 0
          || Number(this.activeTicket.discount_total || 0) > 0 || this.activeTicket._pending) return [];
      return this.tickets.filter((ticket) =>
        Number(ticket.id) !== Number(this.activeTicket.id)
        && ticket.status === 'open'
        && !ticket._pending
        && Number(ticket.paid_total || 0) === 0
        && Number(ticket.discount_total || 0) === 0
        && String(ticket.branch_id || '') === String(this.activeTicket.branch_id || '')
        && String(ticket.customer_id || '') === String(this.activeTicket.customer_id || '')
        && ['pos', ''].includes(String(ticket.origin_channel || 'pos'))
      );
    },
    pendingKitchenItems() {
      return this.activeItems.filter((item) => item.kitchen_status !== 'sent');
    },
    balanceDue() {
      if (!this.activeTicket) return 0;
      return Math.max(0, Number(this.activeTicket.total || 0) - Number(this.activeTicket.paid_total || 0));
    },
    refundablePayments() {
      return this.refundPayments.filter(p => this.refundAvailable(p) > 0 && p.payment_method_code !== 'customer_credit');
    },
    refundSelectedPayment() {
      return this.refundPayments.find(p => Number(p.id) === Number(this.refundForm.payment_id)) || null;
    },
    selectedPaymentMethod() {
      return this.paymentMethods.find((method) => method.code === this.paymentForm.payment_method_code);
    },
    selectedPaymentRequiresReference() {
      const value = this.selectedPaymentMethod?.requires_reference;
      return [true, 1, '1', 't', 'true'].includes(value);
    },
    selectedPaymentSupportsReference() {
      return ['transfer', 'card'].includes(this.paymentForm.payment_method_code) || this.selectedPaymentRequiresReference;
    },
    selectedPaymentReferenceVisible() {
      return this.selectedPaymentRequiresReference || (this.selectedPaymentSupportsReference && this.paymentReferenceExpanded);
    },
    paymentChange() {
      if (this.paymentForm.payment_method_code !== 'cash') return 0;
      return Math.max(0, Number(this.paymentForm.received_amount || 0) - Number(this.paymentForm.amount || this.balanceDue || 0));
    },
    cashMissingAmount() {
      if (this.paymentForm.payment_method_code !== 'cash') return 0;
      return Math.max(0, Number(this.paymentForm.amount || this.balanceDue || 0) - Number(this.paymentForm.received_amount || 0));
    },
    cashReceivedMissing() {
      return this.paymentForm.payment_method_code === 'cash' && Number(this.cashMissingAmount) > 0;
    },
    cashCutDeclaredTotal() {
      return this.cashDenominations.reduce((sum, denom) => sum + (Number(this.cashCutForm.denomination_counts?.[denom.key] || 0) * Number(denom.value || 0)), 0);
    },
    productPreviewTotal() {
      if (!this.productModal) return 0;
      return this.productUnitPrice(this.productModal) * Number(this.productForm.quantity || 1);
    },
    reportableProducts() {
      return this.products
        .filter((product) => !product.is_virtual_extra && Number(product.id) > 0)
        .slice()
        .sort((left, right) => String(left.name || '').localeCompare(String(right.name || ''), 'es'));
    },
  },
  watch: {
    quickAccessScope() {
      this.loadProductQuickAccess();
      this.productQuickFilter = 'all';
      this.activeCategory = null;
      this.loadRegisterOptions();
    },
  },
  mounted() {
    // Keep browser pull-to-refresh disabled without swallowing catalog swipes.
    this.viewportOverscroll=[document.documentElement,document.body].map(el=>({el,value:el.style.overscrollBehaviorY}));
    this.viewportOverscroll.forEach(({el})=>{el.style.overscrollBehaviorY='none';});
    window.addEventListener('keydown', this.handlePosKeyboard);
    this.loadProductQuickAccess();
    this.loadRegisterOptions();
    this.bindPosSocketEvents();
    this.socketBindTimer = window.setInterval(this.bindPosSocketEvents, 1500);
    this.timePollTimer = window.setInterval(this.pollTimeState, 15000);
    this.timerDisplayInterval = window.setInterval(() => { this.timerDisplayTick = Date.now(); }, 1000);
  },
  beforeUnmount() {
    this.viewportOverscroll?.forEach(({el,value})=>{el.style.overscrollBehaviorY=value;});
    window.removeEventListener('keydown', this.handlePosKeyboard);
    if (this.socketBindTimer) window.clearInterval(this.socketBindTimer);
    if (this.timePollTimer) window.clearInterval(this.timePollTimer);
    if (this.timerDisplayInterval) window.clearInterval(this.timerDisplayInterval);
    const socket = getSocket();
    if (socket && this.posSocketBound) {
      socket.off('pos.ticket.updated', this.handleTicketUpdated);
      socket.off('pos.cash.updated', this.loadCash);
      socket.off('pos.command.created', this.handleIncomingCommand);
      socket.off('pos.account.print.request', this.handleAccountPrintRequest);
      socket.off('pos.ticket.comandero.updated', this.handleComanderoTicketUpdate);
    }
    this.closeQrScanner(false);
    this.posSocketBound = false;
  },
  methods: {
    async loadRegisterOptions(loadRest = true) {
      try {
        const {data} = await rawApi.get('/api/pos/cash/registers', {timeout:12000});
        const context=registerIdentity(this.$store.state);
        const enabled=!!data.multi_register_enabled;
        // Preflight the legacy device queue BEFORE changing its storage namespace.
        // If the server flag was enabled too early, fail closed (no new sales).
        if(enabled && !isMultiRegisterEnabled(context)) {
          await requireEmptyQueueBeforeRegisterSwitch();
        }
        setMultiRegisterEnabled(context, enabled);
        this.multiRegisterEnabled=enabled;
        this.registerStatusChecked=true;
        this.registerOptions=data.registers||[];
        this.canManageRegisters=!!data.can_manage_registers;
        let selection=enabled?getSelectedRegister(context):0;
        if(enabled && !this.registerOptions.some(row=>Number(row.id)===selection)) {
          // An inactive/foreign register cannot remain selected after a role change.
          if(selection) setSelectedRegister(context,0);
          selection=0;
        }
        this.selectedRegisterId=selection;
        if(enabled && this.canManageRegisters) {
          // Missing cashier directory must not make the POS unusable.
          try {
            const response=await rawApi.get('/api/pos/cash/cashiers',{timeout:12000});
            this.availableCashiers=response.data.cashiers||[];
          }catch(_error){ this.availableCashiers=[]; }
        }
        this.assignedCashierId=Number(this.$store.state.user?.id||0);
        if(!enabled || selection) {
          if(enabled) await refreshPosPendingStatus();
          if(loadRest) await this.loadAll();
        } else {
          this.cashSession=null;this.register=null;this.clearTicket();
        }
      } catch(error) {
        const ctx=registerIdentity(this.$store.state);
        this.multiRegisterEnabled=isMultiRegisterEnabled(ctx);
        this.registerStatusChecked=error.code==='pos_register_pending_operations'
          ? false : (isRegisterModeKnown(ctx) && !this.multiRegisterEnabled);
        this.selectedRegisterId=this.multiRegisterEnabled?getSelectedRegister(ctx):0;
        // Offline operation is possible only after the server previously confirmed SINGLE-register mode.
        this.showError(error.response?.data?.error||'No se pudieron consultar las cajas. Comprueba tu conexión.');
        if(this.registerStatusChecked && loadRest)await this.loadAll();
      }
    },
    async chooseRegister(registerId) {
      const id=Number(registerId);
      if(!this.registerOptions.some(row=>Number(row.id)===id))return this.showError('Caja no disponible.');
      try {
        await requireEmptyQueueBeforeRegisterSwitch(this.registerOptions.map(row=>row.id));
        setSelectedRegister(registerIdentity(this.$store.state),id);
        // Reload guarantees no ticket, cache or payment modal survives a device rebind.
        window.location.reload();
      } catch(error) {
        this.showError(error.response?.data?.error||error.message||'No se pudo cambiar de caja.');
      }
    },
    async createRegisterRecord() {
      if(!this.canManageRegisters || !this.newRegisterName || this.registerBusy)return;
      this.registerBusy=true;
      try {
        const {data}=await rawApi.post('/api/pos/cash/registers',{name:this.newRegisterName});
        this.newRegisterName='';await this.loadRegisterOptions(false);
        this.showSuccess(data.message||'Caja creada.');
      } catch(error){this.showError(error.response?.data?.error||'No se pudo crear caja.');}
      finally{this.registerBusy=false;}
    },
    productQuickKey(product) { return productKey(product); },
    quickStorageKey(type) { return `gt-pos-${type}-${this.quickAccessScope}`; },
    loadProductQuickAccess() {
      this.favoriteProductIds = readQuickList(this.quickStorageKey('favorites'));
      this.recentProductIds = readQuickList(this.quickStorageKey('recent'));
    },
    isFavoriteProduct(product) { return this.favoriteProductIds.includes(productKey(product)); },
    toggleFavoriteProduct(product) {
      const key = productKey(product);
      this.favoriteProductIds = toggleQuickKey(this.favoriteProductIds, key, 150);
      try { localStorage.setItem(this.quickStorageKey('favorites'), JSON.stringify(this.favoriteProductIds)); } catch (_error) { this.showError('El navegador no permite guardar favoritos.'); }
    },
    rememberRecentProduct(product) {
      if (!product) return;
      this.recentProductIds = rememberQuickKey(this.recentProductIds, productKey(product), 12);
      try { localStorage.setItem(this.quickStorageKey('recent'), JSON.stringify(this.recentProductIds)); } catch (_error) { /* Puede vender sin historial local. */ }
    },
    selectLastProduct() {
      const key = this.recentProductIds[0];
      const product = this.products.find((row) => productKey(row) === key);
      if (product) this.selectProduct(product);
      else this.showError('El ultimo producto ya no esta en el catalogo actual.');
    },
    canQuickEditItem(item) {
      if (!this.activeTicket || this.activeTicket.status !== 'open' || this.activeTicket._pending) return false;
      if (!/^\d+$/.test(String(item.id)) || !Number.isInteger(Number(item.quantity))) return false;
      if (item.kitchen_status !== 'sent') return true;
      const snapshot = item.product_snapshot || {};
      return snapshot.send_to_kitchen === false;
    },
    repeatSentItem(item) {
      if (this.activeTicket?._pending || this.quickItemBusy) return this.showError('Primero sincroniza la cuenta.');
      const product = this.products.find((row) => Number(row.id) === Number(item.product_id) && !row.is_virtual_extra);
      if (!product) return this.showError('El artículo ya no está disponible para agregarlo de nuevo.');
      this.selectProduct(product);
      if (!this.productModal) return;
      const modifiers = this.normalizedModifiers(item);
      const selections = {};
      for (const group of product.option_groups || []) {
        const selected = modifiers.filter((mod) => Number(mod.group_id) === Number(group.id)).map((mod) => Number(mod.option_id));
        selections[group.id] = group.selection_type === 'multiple' ? selected : (selected[0] || null);
      }
      this.productForm = { quantity: 1, notes: String(item.notes || ''), selections };
      this.showSuccess('Revisa la configuración del producto y pulsa Agregar. La nueva unidad se enviará en otra comanda.');
    },
    beginItemNoteEdit(item) {
      if (!this.canQuickEditItem(item)) return;
      this.editingItemId = Number(item.id);
      this.editingItemNotes = String(item.notes || '');
    },
    async saveItemQuickNotes(item) {
      if (this.editingItemId !== Number(item.id)) return;
      const saved = await this.updateQuickItem(item, Number(item.quantity), { notes: this.editingItemNotes.trim() });
      if (saved) this.editingItemId = null;
    },
    async changeItemQuantity(item, direction) {
      const next = Number(item.quantity) + Number(direction);
      if (next < 1) return this.showError('Para retirar la ultima unidad utiliza Quitar, con autorizacion.');
      await this.updateQuickItem(item, next);
    },
    async updateQuickItem(item, quantity, extra = {}) {
      if (this.quickItemBusy || !this.canQuickEditItem(item)) return false;
      if (!navigator.onLine) { this.showError('La edicion de productos necesita conexion y cuenta sincronizada.'); return false; }
      this.quickItemBusy = true;
      try {
        const ticketId = this.activeTicket.id;
        const { data } = await api.put(`/api/pos/tickets/${ticketId}/items/${item.id}`, {
          quantity, expected_quantity: Number(item.quantity), ...extra,
        });
        if (Number(this.activeTicket?.id) === Number(ticketId)) {
          this.activeTicket = data.ticket;
          this.items = data.items || [];
          this.payments = data.payments || [];
          this.activeTimer = data.timer ? { ...data.timer, _received_at: Date.now() } : null;
          this.paymentForm.amount = Number(this.balanceDue.toFixed(2));
        }
        await this.loadTickets();
        this.emitModuleEvent('pos.ticket.updated', { ticket: data.ticket });
        this.showSuccess('Producto actualizado.');
        return true;
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo editar el producto. Sincroniza y vuelve a intentarlo.');
        return false;
      } finally { this.quickItemBusy = false; }
    },
    canQuickMoveItem(item) {
      if (!this.canQuickEditItem(item) || !this.eligibleAccountTargets.length) return false;
      if (!Number.isInteger(Number(item.quantity)) || Number(item.quantity) < 1) return false;
      if (Number(this.activeTicket.discount_total || 0) > 0 || Number(this.activeTicket.paid_total || 0) > 0) return false;
      if (this.activeTimer) return false;
      const snapshot = item.product_snapshot || {};
      return !Number(snapshot.time_minutes || 0) && !snapshot.is_timed_service;
    },
    openMoveDialog(item) {
      if (!this.canQuickMoveItem(item)) return;
      this.arrangeMode = 'item';
      this.arrangeItem = { ...item };
      this.arrangeQuantity = Number(item.quantity);
      this.arrangeTargetId = 0;
      this.showArrangeDialog = true;
    },
    openMergeDialog() {
      if (!this.eligibleAccountTargets.length || this.activeTimer) return;
      this.arrangeMode = 'merge';
      this.arrangeItem = null;
      this.arrangeTargetId = 0;
      this.showArrangeDialog = true;
    },
    async submitArrangeAccounts() {
      if (this.arrangingAccounts || !this.activeTicket || !this.arrangeTargetId) return;
      if (!navigator.onLine) return this.showError('Esta operación necesita conexión con el servidor.');
      const ticketId = Number(this.activeTicket.id);
      const targetId = Number(this.arrangeTargetId);
      if (ticketId === targetId) return this.showError('Selecciona otra cuenta.');
      const item = this.arrangeItem;
      if (this.arrangeMode === 'item' && (!item || !Number.isInteger(Number(this.arrangeQuantity))
          || this.arrangeQuantity < 1 || this.arrangeQuantity > Number(item.quantity))) {
        return this.showError('Selecciona una cantidad entera valida.');
      }
      if (this.arrangeMode === 'merge' && !await confirmAction('¿Unir la cuenta activa a la cuenta seleccionada? Solo se admiten cuentas sin pagos y sin productos enviados.')) return;
      this.arrangingAccounts = true;
      try {
        await syncPos();
        if (posConnection.pending > 0 || posConnection.conflicts > 0) {
          throw new Error('Hay operaciones offline pendientes. Resuélvelas antes de mover cuentas.');
        }
        const endpoint = this.arrangeMode === 'merge'
          ? `/api/pos/tickets/${ticketId}/merge`
          : `/api/pos/tickets/${ticketId}/items/${item.id}/move`;
        const payload = { target_ticket_id: targetId };
        if (this.arrangeMode === 'item') {
          payload.quantity = Number(this.arrangeQuantity);
          payload.expected_quantity = Number(item.quantity);
        }
        const { data } = await api.post(endpoint, payload);
        this.showArrangeDialog = false;
        this.arrangeItem = null;
        await this.loadTickets();
        await this.loadTicket(targetId);
        this.emitModuleEvent('pos.ticket.updated', { ticket_id: ticketId });
        this.emitModuleEvent('pos.ticket.updated', { ticket_id: targetId });
        this.showSuccess(data.message || 'Cuentas actualizadas.');
      } catch (error) {
        this.showError(error.response?.data?.error || error.message || 'No se pudo mover la cuenta.');
      } finally { this.arrangingAccounts = false; }
    },
    async archiveCatalog(kind, id) {
      if (!await confirmAction('¿Archivar este elemento? Seguirá disponible en el editor y se conservará el historial.')) return;
      try { await api.delete(`/api/pos/catalog/${kind}/${id}`); await this.loadOptionAdmin(); await this.loadCatalog(); this.showCategoryEditor = false; this.showProductEditor = false; this.showSuccess('Elemento archivado.'); } catch (e) { this.showError(e.response?.data?.error || 'No se pudo archivar.'); }
    },
    handleTimeClockSuccess(data) {
      const action = data?.action === 'clock_out' ? 'Salida' : 'Entrada';
      const name = data?.employee?.name || 'Empleado';
      this.showSuccess(`${action} registrada para ${name}.`);
    },
    bindPosSocketEvents() {
      if (this.posSocketBound) return;
      const socket = getOrConnectSocket(this.$store.state.context, this.$store.state.user);
      if (!socket) return;
      socket.on('pos.ticket.updated', this.handleTicketUpdated);
      socket.on('pos.cash.updated', this.loadCash);
      socket.on('pos.command.created', this.handleIncomingCommand);
      socket.on('pos.account.print.request', this.handleAccountPrintRequest);
      socket.on('pos.ticket.comandero.updated', this.handleComanderoTicketUpdate);
      this.posSocketBound = true;
    },
    async handleTicketUpdated(payload = {}) {
      const ticketId = payload.ticket?.id || payload.ticket_id;
      await this.loadTickets();
      if (ticketId && this.activeTicket && Number(this.activeTicket.id) === Number(ticketId)) {
        await this.loadTicket(ticketId);
      }
    },
    handlePosKeyboard(event) {
      const target = event.target;
      const tagName = target?.tagName?.toLowerCase();
      const isEditable = target?.isContentEditable || ['input', 'textarea', 'select'].includes(tagName);

      if (this.productModal && event.key === 'Enter') {
        if (tagName === 'textarea' || tagName === 'button' || event.shiftKey || event.ctrlKey || event.metaKey || event.altKey) return;
        event.preventDefault();
        this.addProductFromModal();
        return;
      }

      if (this.showPayment && event.key === 'Enter') {
        if (isEditable || tagName === 'button' || event.shiftKey || event.ctrlKey || event.metaKey || event.altKey) return;
        if (!['button'].includes(tagName)) {
          event.preventDefault();
          this.focusNextPaymentField();
        }
        return;
      }

      const modalOpen = Boolean(
        this.showNewTicketModal ||
        this.productModal ||
        this.showPayment ||
        this.showDiscounts ||
        this.showCashExpense ||
        this.showOpenAccounts ||
        this.showSales ||
        this.showProductReport ||
        this.showCashCut ||
        this.showReceiptSettings ||
        this.showTables ||
        this.showCustomers ||
        this.showOptionAdmin ||
        this.showCategoryEditor ||
        this.showProductEditor ||
        this.showPointAdjust ||
        this.showItemDiscount ||
        this.posMenuOpen ||
        this.kitchenTickets.length
      );
      if (modalOpen || isEditable) return;

      if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
        if (/\s/.test(event.key) && !this.search) return;
        event.preventDefault();
        this.search += event.key;
        this.searchBackspaceHits = [];
        return;
      }

      if (['Backspace', 'Delete'].includes(event.key)) {
        event.preventDefault();
        if (this.search) {
          this.handleSearchBackspace();
          return;
        }
        if (this.activeCategory) {
          this.activeCategory = null;
        }
        return;
      }

      if (event.key === 'Escape') {
        if (this.search) {
          event.preventDefault();
          this.search = '';
          this.searchBackspaceHits = [];
        } else if (this.activeCategory) {
          event.preventDefault();
          this.activeCategory = null;
        }
      }
    },
    handleSearchBackspace() {
      const now = Date.now();
      this.searchBackspaceHits = [...this.searchBackspaceHits.filter((time) => now - time <= 650), now];
      if (this.searchBackspaceHits.length >= 3) {
        this.search = '';
        this.searchBackspaceHits = [];
        return;
      }
      this.search = this.search.slice(0, -1);
    },
    emptyDenominationCounts() {
      return this.cashDenominations.reduce((carry, denom) => ({ ...carry, [denom.key]: 0 }), {});
    },
    cashCutDenominationRows() {
      return this.cashDenominations.map((denom) => {
        const count = Math.max(0, Number(this.cashCutForm.denomination_counts?.[denom.key] || 0));
        return {
          key: denom.key,
          label: denom.label,
          value: denom.value,
          count,
          total: Number((count * denom.value).toFixed(2)),
        };
      });
    },
    emptyExpenseForm() {
      return { cash_session_id: this.cashSession?.id || null, amount: 0, payment_method_code: 'cash', category: 'general', concept: '', paid_to: '', receipt_number: '', notes: '' };
    },
    emptyOptionGroupForm() {
      return { id: null, sell_standalone: false, standalone_area_id: null, status: 'active', name: '', code: '', selection_type: 'single', is_required: false, min_select: 0, max_select: null, position: 0, options: [], product_ids: [] };
    },
    emptyCategoryForm() {
      return { id: null, color: '#edf2f7', tile_size: 'normal', status: 'active', name: '', description: '', category_type: 'pos', sort_order: (this.optionAdminCategories.length + 1) * 10 };
    },
    emptyProductAdminForm() {
      return {
        id: null, track_inventory: false, allow_fraction: false, color: '', stock_policy: 'inherit', status: 'active', config: {},
        name: '',
        sku: '',
        barcode: '',
        category_id: this.activeCategory?.id || this.optionAdminCategories[0]?.id || null,
        preparation_area_id: this.optionAdminPreparationAreas[0]?.id || null,
        sale_price: 0,
        cost: 0,
        product_type: 'sellable',
        time_minutes: 0,
        send_to_kitchen: true,
        tax_rate: 0,
        has_tax: false,
        active_pos: true,
        active_menu: false,
        group_ids: [],
      };
    },
    normalizedArray(value) {
      if (Array.isArray(value)) return value;
      if (typeof value === 'string' && value.trim()) {
        try {
          const parsed = JSON.parse(value);
          return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
          return [];
        }
      }
      return [];
    },
    cashCutAreaKey(area = {}) {
      const code = String(area.area_code || area.code || '').trim().toLowerCase();
      if (['crepas', 'barra', 'alimentos', 'promociones'].includes(code)) return code;
      const name = String(area.area_name || area.name || '')
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
      if (name.includes('crepa')) return 'crepas';
      if (name.includes('barra')) return 'barra';
      if (name.includes('alimento') || name.includes('cocina')) return 'alimentos';
      if (name.includes('promo')) return 'promociones';
      if (code) return code;
      if (area.area_id || area.id) return `id:${area.area_id || area.id}`;
      return `name:${name || 'general'}`;
    },
    cashCutAreaDisplayName(key, fallback = '') {
      const names = { crepas: 'Crepas', barra: 'Barra', alimentos: 'Alimentos', promociones: 'Promociones' };
      return names[key] || fallback || 'General';
    },
    cashCutAreaPaymentRows(cut = {}) {
      const rawAreaPayments = this.normalizedArray(cut.area_payment_breakdown);
      const methodDefinitions = new Map();
      const rememberMethod = (method = {}) => {
        const code = String(method.code || method.payment_method_code || '').trim();
        if (!code || methodDefinitions.has(code)) return;
        methodDefinitions.set(code, {
          code,
          name: method.name || method.payment_method_name || code,
          affects_cash: Boolean(method.affects_cash),
          payments_count: 0,
          total_amount: 0,
        });
      };
      this.paymentMethods.forEach(rememberMethod);
      this.normalizedArray(cut.payment_breakdown).forEach(rememberMethod);
      rawAreaPayments.forEach((area) => this.normalizedArray(area.payment_breakdown).forEach(rememberMethod));

      const areaMap = new Map();
      const ensureArea = (raw = {}, forcedKey = '') => {
        const key = forcedKey || this.cashCutAreaKey(raw);
        if (!areaMap.has(key)) {
          areaMap.set(key, {
            area_key: key,
            area_id: raw.area_id || null,
            area_code: raw.area_code || (['crepas', 'barra', 'alimentos', 'promociones'].includes(key) ? key : ''),
            area_name: this.cashCutAreaDisplayName(key, raw.area_name),
            tickets_count: 0,
            units_count: 0,
            total_amount: 0,
            method_values: new Map(),
          });
        }
        return areaMap.get(key);
      };

      ['crepas', 'barra', 'alimentos', 'promociones'].forEach((key) => ensureArea({}, key));
      this.normalizedArray(cut.area_breakdown).forEach((raw) => {
        const area = ensureArea(raw);
        area.area_id = raw.area_id || area.area_id;
        area.tickets_count = Number(raw.tickets_count || 0);
        area.units_count = Number(raw.units_count || 0);
        area.total_amount = Number(raw.total_amount || 0);
      });
      rawAreaPayments.forEach((raw) => {
        const area = ensureArea(raw);
        area.area_id = raw.area_id || area.area_id;
        area.total_amount = Number(raw.total_amount || 0);
        this.normalizedArray(raw.payment_breakdown).forEach((method) => {
          const code = String(method.code || method.payment_method_code || '').trim();
          if (code) area.method_values.set(code, method);
        });
      });

      const preferredOrder = { crepas: 10, barra: 20, alimentos: 30, promociones: 40 };
      return Array.from(areaMap.values())
        .sort((left, right) => {
          const leftOrder = preferredOrder[left.area_key] || 1000;
          const rightOrder = preferredOrder[right.area_key] || 1000;
          return leftOrder - rightOrder || left.area_name.localeCompare(right.area_name, 'es');
        })
        .map((area) => ({
          area_key: area.area_key,
          area_id: area.area_id,
          area_code: area.area_code,
          area_name: area.area_name,
          tickets_count: area.tickets_count,
          units_count: area.units_count,
          total_amount: Number(area.total_amount || 0),
          payment_breakdown: Array.from(methodDefinitions.values()).map((definition) => {
            const value = area.method_values.get(definition.code) || {};
            return {
              ...definition,
              ...value,
              code: definition.code,
              name: value.name || value.payment_method_name || definition.name,
              payments_count: Number(value.payments_count || 0),
              total_amount: Number(value.total_amount || 0),
            };
          }),
        }));
    },
    cashCutStoreTotal(cut = {}) {
      const value = cut.sales_total ?? cut.paid_total;
      if (value !== undefined && value !== null && Number.isFinite(Number(value))) return Number(value);
      return this.cashCutAreaPaymentRows(cut).reduce((sum, area) => sum + Number(area.total_amount || 0), 0);
    },
    async loadAll() {
      if(!this.registerStatusChecked || (this.multiRegisterEnabled&&!this.selectedRegisterId))return;
      await Promise.all([this.loadCash(), this.loadCatalog(), this.loadDiscounts(), this.loadPaymentMethods(), this.loadTickets(), this.loadTables(), this.loadCustomers(), this.loadReceiptSettings(), this.loadTimeAlerts()]);
    },
    async loadCash() {
      try {
        const { data } = await api.get('/api/pos/cash/current');
        this.register = data.register;
        this.cashSession = data.cash_session;
        this.canOperateCash=!!data.can_operate_cash;
        if(this.multiRegisterEnabled && data.requires_selection){
          this.showRegisterModal=true;
          this.cashSession=null;
        }
      } catch (error) {
        // Fail closed: never keep a previously loaded cash session after an error.
        this.cashSession=null;
        this.register=null;
        this.canOperateCash=false;
        this.showError(error.response?.data?.error || 'No se pudo cargar caja.');
      }
    },
    async loadCatalog() {
      try {
        const { data } = await api.get('/api/pos/catalog');
        this.categories = data.categories || [];
        this.couponEnabled = !!data.coupons?.enabled;
        this.stockSettings = data.stock_settings || this.stockSettings;
        this.products = data.products || [];
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo cargar catálogo PV.');
      }
    },
    async loadDiscounts() {
      try {
        const { data } = await api.get('/api/pos/discounts');
        this.discounts = data.discounts || [];
      } catch (_error) {
        this.discounts = [];
      }
    },
    async loadPaymentMethods() {
      try {
        const { data } = await api.get('/api/pos/payment-methods');
        this.paymentMethods = data.payment_methods || [];
        if (this.paymentMethods.length && !this.paymentMethods.find((item) => item.code === this.paymentForm.payment_method_code)) {
          this.paymentForm.payment_method_code = this.paymentMethods.find((item) => item.code === 'cash')?.code || this.paymentMethods[0].code;
        }
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudieron cargar métodos de pago.');
      }
    },
    async loadTickets() {
      try {
        const { data } = await api.get('/api/pos/tickets', { params: { status: 'open' } });
        this.tickets = (data.tickets || []).map((ticket) => ({
          ...ticket,
          timer: ticket.timer ? { ...ticket.timer, _received_at: Date.now() } : null,
        }));
        if (this.activeTicket) {
          const stillOpen = this.tickets.find((ticket) => Number(ticket.id) === Number(this.activeTicket.id));
          if (stillOpen) await this.loadTicket(this.activeTicket.id);
          else this.clearTicket();
        }
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudieron cargar cuentas.');
      }
    },
    async loadTimeAlerts() {
      try {
        const knownIds = new Set(this.timeAlerts.map((alert) => Number(alert.id)));
        const { data } = await api.get('/api/pos/time-alerts');
        const alerts = data.alerts || [];
        if (alerts.some((alert) => !knownIds.has(Number(alert.id)))) this.playTimeAlertTone();
        this.timeAlerts = alerts;
      } catch (_error) {
        // El resto del PV sigue disponible si el sondeo de avisos falla momentáneamente.
      }
    },
    async pollTimeState() {
      if (this.timePollBusy) return;
      this.timePollBusy = true;
      try {
        await Promise.all([this.loadTimeAlerts(), this.loadTickets()]);
      } finally {
        this.timePollBusy = false;
      }
    },
    playTimeAlertTone() {
      try {
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        if (!AudioContextClass) return;
        const context = new AudioContextClass();
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        oscillator.frequency.value = 880;
        gain.gain.setValueAtTime(0.0001, context.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.16, context.currentTime + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.38);
        oscillator.connect(gain);
        gain.connect(context.destination);
        oscillator.start();
        oscillator.stop(context.currentTime + 0.4);
        oscillator.onended = () => context.close();
      } catch (_error) {
        // Algunos navegadores bloquean audio hasta que exista interacción del usuario.
      }
    },
    async loadTicket(id) {
      if (Number(this.activeTicket?.id) !== Number(id)) this.editingItemId = null;
      const requestId = this.ticketLoadSequence = (this.ticketLoadSequence || 0) + 1;
      const { data } = await api.get(`/api/pos/tickets/${id}`);
      if (requestId !== this.ticketLoadSequence) return;
      this.activeTicket = data.ticket;
      this.items = data.items || [];
      this.payments = data.payments || [];
      this.activeTimer = data.timer ? { ...data.timer, _received_at: Date.now() } : null;
      this.ticketMeta.service_type = this.activeTicket.service_type || 'takeaway';
      this.ticketMeta.order_label = this.activeTicket.order_label || '';
      if (!this.showPayment) this.paymentForm.amount = Number(this.balanceDue.toFixed(2));
      if (!this.showPayment && this.paymentForm.payment_method_code === 'cash') {
        this.paymentForm.received_amount = Number(this.balanceDue.toFixed(2));
      }
    },
    async loadReceiptSettings() {
      try {
        const { data } = await api.get('/api/pos/receipt-settings');
        this.receiptSettings = { ...this.receiptSettings, ...(data.settings || {}) };
      } catch (_error) {
        // El PV puede seguir operando con formato local si la configuración aún no existe.
      }
    },
    async saveReceiptSettings(showToast = true) {
      try {
        const { data } = await api.put('/api/pos/receipt-settings', this.receiptSettings);
        this.receiptSettings = { ...this.receiptSettings, ...(data.settings || {}) };
        if (showToast) this.showSuccess('Formato de ticket guardado.');
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo guardar el formato de ticket.');
      }
    },
    async loadTables() {
      try {
        const { data } = await api.get('/api/pos/tables');
        this.tables = data.tables || [];
      } catch (_error) {
        this.tables = [];
      }
    },
    async loadCustomers() {
      try {
        const params = {};
        if (this.customerSearch) params.q = this.customerSearch;
        const { data } = await api.get('/api/pos/customers', { params });
        this.customers = data.customers || [];
      } catch (_error) {
        this.customers = [];
      }
    },
    async openCash() {
      try {
        const { data } = await api.post('/api/pos/cash/open', {
          opening_cash_amount: this.openingCash,
          ...(this.multiRegisterEnabled ? {register_id:this.selectedRegisterId,cashier_user_id:this.assignedCashierId || this.$store.state.user?.id} : {}),
        });
        this.cashSession = data.cash_session;
        await this.loadCash();
        this.showSuccess(data.message || 'Caja abierta.');
        this.emitModuleEvent('pos.cash.updated', { cash_session: data.cash_session });
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo abrir caja.');
      }
    },
    openNewTicketModal() {
      if (!this.cashSession) {
        this.showError('Abre caja antes de crear una cuenta.');
        return;
      }
      this.newTicketMode = 'takeaway';
      this.newTicketForm = { order_label: '' };
      this.showNewTicketModal = true;
      this.loadTables();
      this.loadCustomers();
    },
    selectNewTicketMode(mode) {
      this.newTicketMode = mode;
      if (mode === 'onsite') this.loadTables();
      if (mode === 'customer') this.loadCustomers();
    },
    async startTakeawayTicket() {
      await this.createTicket({ service_type: 'takeaway', order_label: this.newTicketForm.order_label || '' });
      this.showNewTicketModal = false;
    },
    async startTableTicket(table) {
      const openTicket = this.tickets.find((ticket) => Number(ticket.table_id) === Number(table.id));
      if (table.status === 'occupied' && openTicket) {
        await this.loadTicket(openTicket.id);
        this.showNewTicketModal = false;
        return;
      }
      await this.createTicket({ service_type: 'onsite', table_id: table.id, order_label: `Mesa ${table.name}` });
      this.showNewTicketModal = false;
      await this.loadTables();
    },
    async startCustomerTicket(customer) {
      await this.createTicket({ service_type: 'takeaway', customer_id: customer.id, order_label: customer.name });
      this.showNewTicketModal = false;
    },
    async createTicket(payload = {}) {
      try {
        const { data } = await api.post('/api/pos/tickets', payload);
        await this.loadTicket(data.ticket.id);
        await this.loadTickets();
        this.showSuccess('Cuenta creada.');
        this.emitModuleEvent('pos.ticket.updated', { ticket: data.ticket });
        const queued = this.queuedProduct;
        this.queuedProduct = null;
        if (queued) this.$nextTick(() => this.selectProduct(queued));
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo crear cuenta.');
      }
    },
    async ensureTicket() {
      if (this.activeTicket) return true;
      this.openNewTicketModal();
      return false;
    },
    selectProduct(product) {
      if (this.stockBlocked(product) || this.posBusy.addProductFromModal) return;
      if (!this.cashSession) return;
      if (!this.activeTicket) {
        this.queuedProduct = product;
        this.openNewTicketModal();
        return;
      }
      this.productModal = product;
      const selections = {};
      (product.option_groups || []).forEach((group) => {
        const availableOptions = (group.options || []).filter((option) => option.availability_status !== 'sold_out');
        if (group.selection_type === 'multiple') {
          selections[group.id] = availableOptions.filter((option) => option.is_default).map((option) => option.id);
        } else {
          const defaultOption = availableOptions.find((option) => option.is_default) || (group.is_required ? availableOptions[0] : null);
          selections[group.id] = defaultOption ? defaultOption.id : null;
        }
      });
      this.productForm = { quantity: 1, notes: '', selections };
    },
    closeProductModal() {
      this.productModal = null;
      this.productForm = { quantity: 1, notes: '', selections: {} };
    },
    optionSelected(group, option) {
      const selected = this.productForm.selections[group.id];
      return group.selection_type === 'multiple' ? (selected || []).includes(option.id) : Number(selected) === Number(option.id);
    },
    toggleOption(group, option, checked) {
      if (option.availability_status === 'sold_out') return;
      if (group.selection_type === 'multiple') {
        const current = [...(this.productForm.selections[group.id] || [])];
        if (checked && !current.includes(option.id)) current.push(option.id);
        if (!checked && current.includes(option.id)) current.splice(current.indexOf(option.id), 1);
        this.productForm.selections[group.id] = current.filter(Boolean);
      } else {
        this.productForm.selections[group.id] = checked ? option.id : null;
      }
    },
    selectedOptions(product = this.productModal) {
      if (!product) return [];
      const selected = [];
      (product.option_groups || []).forEach((group) => {
        const value = this.productForm.selections[group.id];
        const ids = group.selection_type === 'multiple' ? value || [] : [value].filter(Boolean);
        group.options.filter((option) => option.availability_status !== 'sold_out').forEach((option) => {
          if (ids.map(Number).includes(Number(option.id))) selected.push({
            ...option,
            option_id: option.id,
            option_code: option.code,
            group_id: group.id,
            group_name: group.name,
            group_code: group.code,
          });
        });
      });
      const rule = this.modifierPricingRule(product);
      if (!rule) return selected;
      const groupCodes = (Array.isArray(rule.group_codes) ? rule.group_codes : []).map((code) => String(code).toLowerCase());
      const includedCount = Math.max(0, Number(rule.included_count || 0));
      const extraPrice = Math.max(0, Number(rule.extra_price || 0));
      let used = 0;
      return selected.map((option) => {
        if (!groupCodes.includes(String(option.group_code || '').toLowerCase())) return option;
        const included = used < includedCount;
        used += 1;
        return { ...option, catalog_price_delta: Number(option.price_delta || 0), price_delta: included ? 0 : extraPrice, included_in_base: included };
      });
    },
    modifierPricingRule(product = this.productModal) {
      const rule = product?.config?.modifier_pricing;
      return rule && rule.type === 'included_count' ? rule : null;
    },
    modifierSelectionError(product = this.productModal) {
      const rule = this.modifierPricingRule(product);
      if (!rule) return '';
      const groupCodes = (Array.isArray(rule.group_codes) ? rule.group_codes : []).map((code) => String(code).toLowerCase());
      const count = this.selectedOptions(product).filter((item) => groupCodes.includes(String(item.group_code || '').toLowerCase())).length;
      const minimum = Math.max(0, Number(rule.min_select_total || 0));
      const maximum = rule.max_select_total === null || rule.max_select_total === undefined ? null : Math.max(0, Number(rule.max_select_total));
      if (count < minimum) return `Selecciona al menos ${minimum} ingrediente.`;
      if (maximum !== null && count > maximum) return `Puedes seleccionar como maximo ${maximum} ingredientes.`;
      return '';
    },
    includedCountLabel(value) {
      const count = Number(value || 0);
      return count === 1 ? '1 ingrediente incluido' : `${count} ingredientes incluidos`;
    },
    optionPriceLabel(group, option, product = this.productModal) {
      const rule = this.modifierPricingRule(product);
      const groupCodes = (Array.isArray(rule?.group_codes) ? rule.group_codes : []).map((code) => String(code).toLowerCase());
      if (rule && groupCodes.includes(String(group.code || '').toLowerCase())) {
        const selectedOption = this.selectedOptions(product).find((item) => Number(item.option_id) === Number(option.id));
        if (selectedOption) return selectedOption.included_in_base ? 'Incluido' : `+${this.money(selectedOption.price_delta)}`;
        const selectedCount = this.selectedOptions(product).filter((item) => groupCodes.includes(String(item.group_code || '').toLowerCase())).length;
        return selectedCount < Number(rule.included_count || 0) ? 'Incluido' : `+${this.money(rule.extra_price)}`;
      }
      return Number(option.price_delta || 0) ? `+${this.money(option.price_delta)}` : 'Sin costo';
    },
    productUnitPrice(product) {
      const modifiersTotal = this.selectedOptions(product).reduce((sum, option) => sum + Number(option.price_delta || 0), 0);
      return Number(product.sale_price || 0) + modifiersTotal;
    },
    async addProductFromModal() {
      if (!this.productModal) return;
      const ok = await this.ensureTicket();
      if (!ok) return;

      const missing = (this.productModal.option_groups || []).find((group) => {
        if (!group.is_required) return false;
        const value = this.productForm.selections[group.id];
        return group.selection_type === 'multiple' ? !(value || []).length : !value;
      });
      if (missing) {
        this.showError(`Selecciona ${missing.name}.`);
        return;
      }
      const modifierError = this.modifierSelectionError(this.productModal);
      if (modifierError) {
        this.showError(modifierError);
        return;
      }

      try {
        const payload = {
          quantity: this.productForm.quantity,
          notes: this.productForm.notes,
          modifiers: this.selectedOptions(),
        };
        if (this.productModal.is_virtual_extra) {
          payload.extra_option_id = this.productModal.extra_option_id;
          payload.name = this.productModal.name;
          payload.unit_price = Number(this.productModal.sale_price || 0);
          payload.modifiers = [];
        } else {
          payload.product_id = this.productModal.id;
        }

        const { data } = await api.post(`/api/pos/tickets/${this.activeTicket.id}/items`, payload);
        this.activeTicket = data.ticket;
        this.items = data.items || [];
        this.payments = data.payments || [];
        this.activeTimer = data.timer ? { ...data.timer, _received_at: Date.now() } : null;
        this.paymentForm.amount = Number(this.balanceDue.toFixed(2));
        this.rememberRecentProduct(this.productModal);
        this.closeProductModal();
        await this.loadTickets();
        this.emitModuleEvent('pos.ticket.updated', { ticket: data.ticket });
        const queued = this.queuedProduct;
        this.queuedProduct = null;
        if (queued) this.$nextTick(() => this.selectProduct(queued));
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo agregar producto.');
      }
    },
    async saveTicketMeta() {
      if (!this.activeTicket) return;
      try {
        const { data } = await api.put(`/api/pos/tickets/${this.activeTicket.id}`, this.ticketMeta);
        this.activeTicket = data.ticket;
        this.items = data.items || [];
        this.activeTimer = data.timer ? { ...data.timer, _received_at: Date.now() } : null;
        await this.loadTickets();
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo actualizar la cuenta.');
      }
    },
    async assignTable(table) {
      if (!this.activeTicket) await this.createTicket({ service_type: 'onsite', table_id: table.id, order_label: `Mesa ${table.name}` });
      else {
        this.ticketMeta.service_type = 'onsite';
        await api.put(`/api/pos/tickets/${this.activeTicket.id}`, { service_type: 'onsite', table_id: table.id, order_label: `Mesa ${table.name}` });
        await this.loadTicket(this.activeTicket.id);
        await this.loadTables();
        await this.loadTickets();
      }
      this.showTables = false;
    },
    async createTable() {
      if (!this.newTable.name) return;
      try {
        await api.post('/api/pos/tables', this.newTable);
        this.newTable = { name: '', capacity: 4 };
        await this.loadTables();
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo crear mesa.');
      }
    },
    openLoyaltyScan() {
      this.showLoyaltyScan = true;
      this.loyaltyScanCode = '';
      this.loyaltyScanResult = null;
      this.loyaltyCustomerResults = [];
      this.loyaltyExternalResults = [];
      this.loyaltyExternalErrors = [];
      this.loyaltyExternalLoading = false;
      this.$nextTick(() => this.$refs.loyaltyScanInput?.focus?.());
    },
    async openExternalCustomerSearchFromPos() {
      const q = String(this.customerSearch || '').trim();
      if (!q) {
        this.showError('Escribe nombre, teléfono, correo o QR antes de buscar en Afrogato.');
        return;
      }
      this.showNewTicketModal = false;
      this.showCustomers = false;
      this.openLoyaltyScan();
      await this.$nextTick();
      this.loyaltyScanCode = q;
      await this.searchExternalLoyaltyCustomers();
    },
    qrScannerTitle() {
      if (this.qrScannerTarget === 'newTicketCustomer') return 'Escanear wallet para nueva cuenta';
      if (this.qrScannerTarget === 'customer') return 'Escanear wallet para asignar cliente';
      if (this.qrScannerTarget === 'coupon') return 'Escanear código único de cupón';
      return 'Escanear wallet o cupón';
    },
    loadQrLibrary() {
      // html5-qrcode queda empaquetado en el build; no se carga desde CDN.
      return Promise.resolve(Html5Qrcode);
    },
    async openQrScanner(target = 'loyalty') {
      this.qrScannerTarget = target;
      this.qrScannerStatus = 'Solicitando permiso de cámara...';
      this.qrScannerError = '';
      this.qrScannerManualCode = '';
      this.showQrScanner = true;
      await this.$nextTick();

      if (!window.isSecureContext) {
        this.qrScannerError = 'La cámara requiere HTTPS. Abre el PV desde https://sistema.gestiontotal.app.';
        return;
      }
      if (!navigator.mediaDevices?.getUserMedia) {
        this.qrScannerError = 'Este navegador no permite usar cámara desde esta página. Pega el código o usa un lector QR físico.';
        return;
      }

      try {
        await this.stopQrScannerRuntime();
        await this.loadQrLibrary();
        await this.$nextTick();
        const readerId = 'gt-qr-reader';
        this.qrScannerInstance = new Html5Qrcode(readerId, false);
        this.qrScannerStatus = 'Apunta la cámara al QR. Se validará automáticamente.';
        await this.qrScannerInstance.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: (viewfinderWidth, viewfinderHeight) => {
            const minEdge = Math.min(viewfinderWidth, viewfinderHeight);
            const size = Math.max(180, Math.floor(minEdge * 0.72));
            return { width: size, height: size };
          } },
          async (decodedText) => {
            if (!decodedText || !this.showQrScanner) return;
            await this.applyQrScannerValue(decodedText);
          },
          () => {}
        );
      } catch (error) {
        const name = error?.name || '';
        if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
          this.qrScannerError = 'Permiso de cámara denegado. Actívalo en el navegador o pega el código manualmente.';
        } else if (name === 'NotFoundError') {
          this.qrScannerError = 'No se encontró cámara disponible en este dispositivo.';
        } else {
          this.qrScannerError = error?.message || 'No se pudo abrir el lector QR.';
        }
      }
    },
    async stopQrScannerRuntime() {
      if (this.qrScannerTimer) window.clearTimeout(this.qrScannerTimer);
      this.qrScannerTimer = null;
      if (this.qrScannerInstance) {
        try {
          const state = this.qrScannerInstance.getState ? this.qrScannerInstance.getState() : null;
          if (state !== 1) await this.qrScannerInstance.stop();
        } catch (_error) {
          // El lector ya estaba detenido.
        }
        try {
          await this.qrScannerInstance.clear();
        } catch (_error) {
          // El contenedor ya estaba limpio.
        }
      }
      this.qrScannerInstance = null;
      if (this.qrScannerStream) {
        this.qrScannerStream.getTracks().forEach((track) => track.stop());
      }
      this.qrScannerStream = null;
      this.qrScannerDetector = null;
    },
    async scanQrFrame() {
      // Conservado por compatibilidad con entregas previas; el scanner real ahora usa html5-qrcode.
    },
    async closeQrScanner(hide = true) {
      await this.stopQrScannerRuntime();
      if (hide) this.showQrScanner = false;
    },
    async applyManualQrCode() {
      const value = String(this.qrScannerManualCode || '').trim();
      if (!value) {
        this.showError('Escribe o pega el código QR.');
        return;
      }
      await this.applyQrScannerValue(value);
    },
    async applyQrScannerValue(value) {
      const code = String(value || '').trim();
      if (!code) return;
      const target = this.qrScannerTarget;
      await this.closeQrScanner(true);
      if (target === 'newTicketCustomer') {
        this.customerSearch = code;
        await this.lookupCustomerQrForNewTicket();
        return;
      }
      if (target === 'customer') {
        this.customerSearch = code;
        await this.lookupCustomerQr();
        return;
      }
      if (target === 'coupon') { this.couponCode = code; this.showCoupon = true; await this.previewCoupon(); return; }
      this.showLoyaltyScan = true;
      await this.$nextTick();
      this.loyaltyScanCode = code;
      await this.scanLoyaltyCode();
    },
    async scanLoyaltyCode() {
      const code = String(this.loyaltyScanCode || '').trim();
      if (!code) {
        this.showError('Escanea o pega un QR de wallet o canje.');
        return;
      }
      try {
        const { data } = await api.get('/api/pos/loyalty/scan', { params: { code } });
        this.loyaltyScanResult = data;
        this.loyaltyCustomerResults = [];
        if (data.type === 'wallet') this.showSuccess('Wallet encontrada.');
        if (data.type === 'redemption') this.showSuccess('Canje validado.');
      } catch (error) {
        this.loyaltyScanResult = null;
        try {
          const { data } = await api.get('/api/pos/loyalty/customers/search', { params: { q: code } });
          this.loyaltyCustomerResults = data.customers || [];
          if (this.loyaltyCustomerResults.length) this.showSuccess('Selecciona el cliente encontrado.');
          else this.showError('No encontré wallet, canje ni cliente con ese dato.');
        } catch (searchError) {
          this.loyaltyCustomerResults = [];
          this.showError(error.response?.data?.error || searchError.response?.data?.error || 'QR de lealtad no valido.');
        }
      }
    },
    async searchExternalLoyaltyCustomers() {
      const q = String(this.loyaltyScanCode || '').trim();
      if (!q) {
        this.showError('Escribe nombre, correo, teléfono o QR para buscar en Afrogato.');
        return;
      }
      this.loyaltyExternalLoading = true;
      this.loyaltyExternalResults = [];
      this.loyaltyExternalErrors = [];
      try {
        const { data } = await api.get('/api/pos/loyalty/external-customers/search', { params: { q } });
        this.loyaltyExternalResults = data.customers || [];
        this.loyaltyExternalErrors = data.errors || [];
        if (this.loyaltyExternalResults.length) {
          this.showSuccess('Encontré clientes externos. Selecciona uno para importarlo.');
        } else if (this.loyaltyExternalErrors.length) {
          this.showError('No se pudo completar la búsqueda externa. Revisa token o conexión.');
        } else {
          this.showError('Afrogato respondió, pero no encontró coincidencias suficientes.');
        }
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo buscar en Afrogato.');
      } finally {
        this.loyaltyExternalLoading = false;
      }
    },
    async importExternalLoyaltyCustomer(customer) {
      try {
        const { data } = await api.post('/api/pos/loyalty/external-customers/import', {
          external_site_id: customer.external_site_id,
          customer,
        });
        const imported = data.customer;
        await this.assignCustomer(imported);
        this.showLoyaltyScan = false;
        this.showSuccess(`Cliente ${imported.name} importado, vinculado y asignado.`);
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo importar el cliente externo.');
      }
    },
    async applyCustomerFromLoyalty(customer) {
      await this.assignCustomer(customer);
      this.showLoyaltyScan = false;
      this.showSuccess(`Cliente ${customer.name} asignado.`);
    },
    async applyScannedCustomer() {
      const customer = this.loyaltyScanResult?.customer;
      if (!customer) return;
      await this.assignCustomer(customer);
      this.showLoyaltyScan = false;
      this.showSuccess(`Cliente ${customer.name} asignado.`);
    },
    async redeemScannedReward() {
      const redemption = this.loyaltyScanResult?.redemption;
      if (!redemption) return;
      try {
        const { data } = await api.post(`/api/pos/loyalty/redemptions/${redemption.id}/redeem`);
        this.showLoyaltyScan = false;
        await Promise.all([this.loadTickets(), this.loadTables()]);
        await this.loadTicket(data.ticket_id);
        this.showSuccess('Canje convertido en orden con 100% de descuento. Envía la comanda cuando esté lista.');
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo canjear el QR.');
      }
    },
    async assignCustomer(customer) {
      if (!this.activeTicket) await this.createTicket({ service_type: 'takeaway', customer_id: customer.id, order_label: customer.name });
      else {
        await api.put(`/api/pos/tickets/${this.activeTicket.id}`, { customer_id: customer.id, order_label: this.activeTicket.order_label || customer.name });
        await this.loadTicket(this.activeTicket.id);
        await this.loadTickets();
      }
      this.showCustomers = false;
    },
    async createCustomer() {
      if (!this.newCustomer.name) return;
      try {
        const { data } = await api.post('/api/pos/customers', this.newCustomer);
        this.newCustomer = { name: '', phone: '', email: '' };
        await this.loadCustomers();
        await this.assignCustomer(data.customer);
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo crear cliente.');
      }
    },
    async lookupCustomerQr() {
      const qr = String(this.customerSearch || '').trim();
      if (!qr) {
        this.showError('Escanea, pega o escribe el QR del cliente.');
        return;
      }
      try {
        const { data } = await api.get('/api/pos/customers/lookup', { params: { qr } });
        this.customers = [data.customer];
      } catch (error) {
        try {
          const { data } = await api.get('/api/pos/loyalty/scan', { params: { code: qr } });
          if (data.type === 'wallet' && data.customer) {
            this.customers = [data.customer];
            this.showSuccess('Wallet encontrada. Selecciona o asigna el cliente.');
            return;
          }
        } catch (_scanError) {
          // Si tampoco es wallet, mostramos el error original.
        }
        this.showError(error.response?.data?.error || 'QR no encontrado.');
      }
    },
    async lookupCustomerQrForNewTicket() {
      const qr = String(this.customerSearch || '').trim();
      if (!qr) {
        this.showError('Escanea, pega o escribe el QR del cliente.');
        return;
      }
      try {
        const { data } = await api.get('/api/pos/customers/lookup', { params: { qr } });
        this.customers = [data.customer];
        await this.startCustomerTicket(data.customer);
      } catch (error) {
        try {
          const { data } = await api.get('/api/pos/loyalty/scan', { params: { code: qr } });
          if (data.type === 'wallet' && data.customer) {
            await this.startCustomerTicket(data.customer);
            this.showSuccess('Cuenta creada y ligada a la wallet.');
            return;
          }
        } catch (_scanError) {
          // Si tampoco es wallet, mostramos el error original.
        }
        this.showError(error.response?.data?.error || 'QR no encontrado.');
      }
    },
    async createCustomerFromNewTicket() {
      if (!this.newCustomer.name) return;
      try {
        const { data } = await api.post('/api/pos/customers', this.newCustomer);
        this.newCustomer = { name: '', phone: '', email: '' };
        await this.loadCustomers();
        await this.startCustomerTicket(data.customer);
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo crear cliente.');
      }
    },
    async requestAdminActionPayload(defaultReason = '') {
      const reason = await promptText('Motivo de la acción', defaultReason);
      if (!reason) return null;
      return { reason };
    },
    async cancelActiveTicket() {
      if (!this.activeTicket) return;
      const payload = await this.requestAdminActionPayload('Error operativo / cuenta duplicada');
      if (!payload) return;
      if (!await confirmAction('Esta acción eliminará la cuenta abierta y liberará la mesa si aplica. ¿Continuar?')) return;
      try {
        const { data } = await api.delete(`/api/pos/tickets/${this.activeTicket.id}`, { data: payload });
        if (data.cancellation_commands?.length) {
          this.kitchenTickets = data.cancellation_commands;
          this.broadcastKitchenCancellation(data.cancellation_commands, this.activeTicket);
        }
        this.showSuccess(data.cancellation_commands?.length ? 'Cuenta eliminada. Imprime avisos de cancelación a preparación.' : 'Cuenta eliminada correctamente.');
        this.clearTicket();
        await Promise.all([this.loadTickets(), this.loadTables()]);
      } catch (error) {
        if (error.response?.data?.code === 'admin_pin_required') {
          const adminPin = await promptText('PIN de administrador');
          if (!adminPin) return;
          try {
            const { data } = await api.delete(`/api/pos/tickets/${this.activeTicket.id}`, { data: { ...payload, admin_pin: adminPin } });
            if (data.cancellation_commands?.length) {
              this.kitchenTickets = data.cancellation_commands;
              this.broadcastKitchenCancellation(data.cancellation_commands, this.activeTicket);
            }
            this.showSuccess(data.cancellation_commands?.length ? 'Cuenta eliminada. Imprime avisos de cancelación a preparación.' : 'Cuenta eliminada correctamente.');
            this.clearTicket();
            await Promise.all([this.loadTickets(), this.loadTables()]);
            return;
          } catch (retryError) {
            this.showError(retryError.response?.data?.error || 'No se pudo eliminar la cuenta.');
            return;
          }
        }
        this.showError(error.response?.data?.error || 'No se pudo eliminar la cuenta.');
      }
    },
    async unlinkActiveTicketCustomer() {
      if (!this.activeTicket || !this.activeTicket.customer_id) return;
      const reason = await promptText('Motivo para quitar el cliente de la cuenta', 'Cliente asignado por error');
      if (!reason) return;
      try {
        const { data } = await api.delete(`/api/pos/tickets/${this.activeTicket.id}/customer`, { data: { reason } });
        this.activeTicket = data.ticket;
        this.items = data.items || [];
        this.payments = data.payments || [];
        this.activeTimer = data.timer ? { ...data.timer, _received_at: Date.now() } : null;
        await this.loadTickets();
        this.showSuccess('Cliente desvinculado de la cuenta.');
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo quitar el cliente.');
      }
    },
    async removeItem(item) {
      const reason = await promptText('Motivo para quitar producto');
      if (!reason) return;
      const adminPin = this.isPosAdmin ? null : await promptText('PIN de administrador');
      if (!this.isPosAdmin && !adminPin) return;
      try {
        const { data } = await api.delete(`/api/pos/tickets/${this.activeTicket.id}/items/${item.id}`, { data: { reason, admin_pin: adminPin } });
        this.activeTicket = data.ticket;
        this.items = data.items || [];
        this.activeTimer = data.timer ? { ...data.timer, _received_at: Date.now() } : null;
        if (data.cancellation_commands?.length) {
          this.kitchenTickets = data.cancellation_commands;
          this.broadcastKitchenCancellation(data.cancellation_commands, data.ticket);
          this.showSuccess('Producto retirado. Imprime la comanda de cancelación para preparación.');
        }
        await this.loadTickets();
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo quitar producto.');
      }
    },
    async sendCommand() {
      if (!this.activeTicket) return;
      if (!this.pendingKitchenItems.length) {
        await this.openReprintCommands(true);
        return;
      }
      try {
        const { data } = await api.post(`/api/pos/tickets/${this.activeTicket.id}/send-command`);
        this.kitchenTickets = data.kitchen_tickets || [];
        await this.loadTicket(this.activeTicket.id);
        await this.loadTickets();
        this.emitModuleEvent('pos.ticket.updated', { ticket: this.activeTicket });
        this.showSuccess(data.message || 'Comandas generadas.');
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo generar comanda.');
      }
    },
    async openReprintCommands(confirmFirst = false) {
      if (!this.activeTicket) return;
      try {
        const { data } = await api.get(`/api/pos/tickets/${this.activeTicket.id}/commands`);
        const commands = data.kitchen_tickets || [];
        if (!commands.length) {
          this.showError('Este ticket aun no tiene comandas impresas.');
          return;
        }
        if (confirmFirst && !await confirmAction('Esta comanda ya fue generada. ¿Quieres reimprimirla?')) return;
        this.kitchenTickets = commands;
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudieron cargar comandas anteriores.');
      }
    },
    async openCashExpenseModal() {
      if (this.multiRegisterEnabled && !this.canOperateCash) return this.showError('Gastos reservados al cajero responsable o gerente.');
      if (!this.cashSession) {
        this.showError('No hay caja abierta.');
        return;
      }
      this.expenseForm = this.emptyExpenseForm();
      this.showCashExpense = true;
      await this.loadCashExpenses();
    },
    async loadCashExpenses() {
      if (!this.cashSession) return;
      this.expenseLoading = true;
      try {
        const { data } = await api.get('/api/pos/cash/expenses', { params: { cash_session_id: this.cashSession.id } });
        this.cashExpenses = data.expenses || [];
        this.expenseSummary = data.summary || {};
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudieron cargar salidas.');
      } finally {
        this.expenseLoading = false;
      }
    },
    async saveCashExpense() {
      if (!this.cashSession) return;
      if (!this.expenseForm.concept.trim()) {
        this.showError('Captura el concepto del gasto.');
        return;
      }
      if (Number(this.expenseForm.amount || 0) <= 0) {
        this.showError('El monto debe ser mayor a cero.');
        return;
      }
      try {
        const payload = { ...this.expenseForm, cash_session_id: this.cashSession.id };
        const { data } = await api.post('/api/pos/cash/expenses', payload);
        this.cashSession = data.cash_session || this.cashSession;
        this.expenseSummary = data.summary || this.expenseSummary;
        this.cashExpenses = [data.expense, ...this.cashExpenses].filter(Boolean);
        this.expenseForm = this.emptyExpenseForm();
        this.showSuccess(data.message || 'Salida registrada.');
        this.emitModuleEvent('pos.cash.updated', { cash_session: this.cashSession, expense: data.expense });
        if (data.expense) this.printCashExpense(data.expense);
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo registrar la salida.');
      }
    },
    async voidCashExpense(expense) {
      if (!expense?.id) return;
      const reason = await promptText('Motivo para cancelar esta salida:') || '';
      if (reason === null) return;
      try {
        const { data } = await api.post(`/api/pos/cash/expenses/${expense.id}/void`, { reason });
        this.cashSession = data.cash_session || this.cashSession;
        this.expenseSummary = data.summary || this.expenseSummary;
        this.cashExpenses = this.cashExpenses.filter((item) => Number(item.id) !== Number(expense.id));
        this.showSuccess(data.message || 'Salida cancelada.');
        this.emitModuleEvent('pos.cash.updated', { cash_session: this.cashSession });
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo cancelar la salida.');
      }
    },
    printCashExpense(expense) {
      const html = this.buildCashExpenseHtml(expense);
      this.printHtml(html);
    },

    openPaymentModal() {
      if (!this.activeTicket) return;
      // This sale owns its print choice; late settings responses must not change it.
      this.paymentPrintRequested = [true, 1, "1", "true"].includes(this.receiptSettings.auto_print_paid_ticket);
      this.resetPaymentForm();
      this.paymentReferenceExpanded = false;
      this.showPayment = true;
      this.$nextTick(() => this.$refs.paymentAmountInput?.focus?.());
    },
    resetPaymentForm() {
      const amount = Number(this.balanceDue.toFixed(2));
      this.paymentForm = {
        payment_method_code: this.paymentMethods.find((item) => item.code === 'cash')?.code || 'cash',
        amount,
        received_amount: amount,
        reference: '',
        request_key: this.newPosRequestKey(),
      };
    },
    selectPaymentMethod(code) {
      this.paymentForm.payment_method_code = code;
      this.paymentForm.reference = '';
      this.paymentReferenceExpanded = false;
      if (code === 'cash') {
        this.paymentForm.received_amount = Number((this.paymentForm.amount || this.balanceDue || 0).toFixed(2));
      } else {
        this.paymentForm.received_amount = null;
      }
      this.$nextTick(() => this.$refs.paymentAmountInput?.focus?.());
    },
    focusNextPaymentField(current = '') {
      const order = ['amount'];
      if (this.selectedPaymentReferenceVisible) order.push('reference');
      if (this.paymentForm.payment_method_code === 'cash') order.push('received');
      const refs = {
        amount: 'paymentAmountInput',
        reference: 'paymentReferenceInput',
        received: 'paymentReceivedInput',
      };
      let currentKey = current;
      if (!currentKey) {
        const active = document.activeElement;
        currentKey = order.find((key) => this.$refs[refs[key]] === active) || order[order.length - 1];
      }
      const index = order.indexOf(currentKey);
      const next = order[index + 1];
      if (next && this.$refs[refs[next]]) {
        this.$refs[refs[next]].focus();
        this.$refs[refs[next]].select?.();
        return;
      }
      this.payTicket();
    },
    async printActiveAccount() {
      if (!this.activeTicket) return;
      try {
        const { data } = await api.get(`/api/pos/tickets/${this.activeTicket.id}`);
        this.printReceipt(data.ticket, data.items || [], data.payments || [], false);
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo imprimir la cuenta.');
      }
    },

    openCashCutModal() {
      if (!this.cashSession) {
        this.showError('No hay caja abierta.');
        return;
      }
      this.cashCutForm = {
        cash_session_id: this.cashSession.id,
        cut_type: 'X',
        closing_cash_amount: 0,
        denomination_counts: this.emptyDenominationCounts(),
        notes: '',
      };
      this.cashCutResult = null;
      this.cashCutError = '';
      this.showCashCut = true;
    },
    async performCashCut() {
      // posInteraction ya activa posBusy.performCashCut antes de llamar este metodo.
      // Consultarlo aqui impedía siempre enviar el corte al servidor.
      if (!this.cashSession) {
        this.cashCutError = 'No hay una caja abierta. Actualiza el estado de caja antes de generar el corte.';
        this.showError(this.cashCutError);
        return;
      }
      if (this.multiRegisterEnabled && !this.canOperateCash) {
        this.cashCutError = 'Corte reservado al cajero responsable o gerente.';
        this.showError(this.cashCutError);
        return;
      }
      this.cashCutError = '';
      const payload = {
        ...this.cashCutForm,
        closing_cash_amount: Number(this.cashCutDeclaredTotal.toFixed(2)),
        denomination_counts: this.cashCutDenominationRows(),
      };
      try {
        let result;
        try { result = await api.post('/api/pos/cash/close', payload); }
        catch (error) {
          if (error.response?.data?.code !== 'open_balances_confirmation_required') throw error;
          const accounts = error.response?.data?.open_accounts || [];
          const total = accounts.reduce((sum, t) => sum + Number(t.paid_total || 0), 0);
          if (!await confirmAction(`Hay ${accounts.length} cuenta(s) abiertas con ${this.money(total)} abonados. Estos anticipos YA se cuentan en el efectivo. ¿Confirmas cerrar caja Z y dejar las cuentas abiertas?`)) return;
          result = await api.post('/api/pos/cash/close', { ...payload, confirm_open_balances: true });
        }
        const data = result.data;
        this.cashCutResult = data.cut;
        this.cashCutError = '';
        this.cashSession = data.cash_session;
        this.showSuccess(data.message || 'Corte generado.');
        this.emitModuleEvent('pos.cash.updated', { cash_session: data.cash_session, cut: data.cut });
        await this.loadCash();
      } catch (error) {
        // Los bloqueos locales de sincronizacion no tienen response HTTP.
        // Mostrar su mensaje evita un fallo silencioso o una alerta generica.
        this.cashCutError = error.response?.data?.error || error.message || 'No se pudo generar corte.';
        this.showError(this.cashCutError);
      }
    },
    async openSalesModal() {
      this.salesHistoryMode = false;
      this.showSales = true;
      if (this.cashSession && !this.salesFilters.cash_session_id) {
        this.salesFilters.cash_session_id = this.cashSession.id;
      }
      await this.loadSales();
    },
    async openCutHistory() {
      this.salesHistoryMode = true;
      this.salesFilters.scope = 'cut';
      this.salesFilters.cut_id = '';
      this.showSales = true;
      await this.loadSales();
    },
    async openProductReport() {
      this.showProductReport = true;
      if (!this.productReportFilters.product_id && this.reportableProducts.length) {
        this.productReportFilters.product_id = this.reportableProducts[0].id;
      }
      if (this.productReportFilters.product_id) await this.loadProductReport();
    },
    async loadProductReport() {
      if (!this.productReportFilters.product_id) return;
      this.productReportLoading = true;
      try {
        const { data } = await api.get('/api/pos/reports/products/daily', { params: this.productReportFilters });
        this.productReportRows = (data.daily || []).slice().reverse();
        this.productReportSummary = data.summary || {};
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo generar el reporte por producto.');
      } finally {
        this.productReportLoading = false;
      }
    },
    async loadSales() {
      this.salesLoading = true;
      try {
        const params = { ...this.salesFilters };
        if (params.scope === 'cash_session') {
          params.cash_session_id = this.cashSession?.id || params.cash_session_id || '';
        }
        if (params.scope !== 'cut') delete params.cut_id;
        const { data } = await api.get('/api/pos/tickets/sales', { params });
        this.salesCuts = data.cuts || [];
        if (this.salesFilters.scope === 'cut' && !this.salesFilters.cut_id && this.salesCuts.length) {
          this.salesFilters.cut_id = this.salesCuts[0].id;
          this.salesLoading = false;
          await this.loadSales();
          return;
        }
        this.salesTickets = data.tickets || [];
        this.salesSummary = data.summary || {};
        this.salesExpenseDetails = data.expenses_detail || (data.active_cut && data.active_cut.expenses_detail) || [];
        this.salesActiveCut = data.active_cut || null;
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudieron cargar las ventas.');
      } finally {
        this.salesLoading = false;
      }
    },
    async openOptionAdmin() {
      this.showOptionAdmin = true;
      await this.loadOptionAdmin();
    },
    async loadOptionAdmin() {
      this.optionAdminLoading = true;
      try {
        const { data } = await api.get('/api/pos/catalog/options-admin');
        this.optionAdminCategories = data.categories || [];
        this.optionAdminPreparationAreas = data.preparation_areas || [];
        this.optionAdminGroups = data.option_groups || [];
        this.optionAdminProducts = data.products || [];
        if (!this.categoryForm.id && this.optionAdminCategories.length) {
          const firstCategory = this.optionAdminCategories[0];
          this.categoryForm = {
            id: firstCategory.id,
            color: firstCategory.color || "#edf2f7",
            tile_size: firstCategory.tile_size || "normal",
            status: firstCategory.status || "active",
            name: firstCategory.name || '',
            description: firstCategory.description || '',
            category_type: firstCategory.category_type || 'pos',
            sort_order: Number(firstCategory.sort_order || 0),
          };
        }
        if (!this.productAdminForm.id) {
          this.productAdminForm = this.emptyProductAdminForm();
        }
        if (!this.optionGroupForm.id && this.optionAdminGroups.length) {
          this.editOptionGroup(this.optionAdminGroups[0]);
        }
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudieron cargar extras.');
      } finally {
        this.optionAdminLoading = false;
      }
    },
    startNewCategory() {
      this.categoryForm = this.emptyCategoryForm();
      this.showCategoryEditor = true;
    },
    editCategory(category) {
      this.categoryForm = {
        id: category.id,
        color: category.color || "#edf2f7",
        tile_size: category.tile_size || "normal",
        status: category.status || "active",
        name: category.name || '',
        description: category.description || '',
        category_type: category.category_type || 'pos',
        sort_order: Number(category.sort_order || 0),
      };
      if (!this.productAdminForm.id) {
        this.productAdminForm.category_id = category.id;
      }
      this.showCategoryEditor = true;
    },
    async saveCategory() {
      if (!this.categoryForm.name.trim()) {
        this.showError('Ponle nombre a la categoría.');
        return;
      }
      try {
        const url = this.categoryForm.id ? `/api/pos/catalog/categories/${this.categoryForm.id}` : '/api/pos/catalog/categories';
        const method = this.categoryForm.id ? 'put' : 'post';
        const { data } = await api[method](url, this.categoryForm);
        this.showSuccess(data.message || 'Categoría guardada.');
        await this.loadOptionAdmin();
        await this.loadCatalog();
        const saved = this.optionAdminCategories.find((category) => Number(category.id) === Number(data.category_id));
        if (saved) {
          this.categoryForm = {
            id: saved.id,
            color: saved.color || "#edf2f7",
            tile_size: saved.tile_size || "normal",
            status: saved.status || "active",
            name: saved.name || '',
            description: saved.description || '',
            category_type: saved.category_type || 'pos',
            sort_order: Number(saved.sort_order || 0),
          };
        }
        this.showCategoryEditor = false;
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo guardar la categoría.');
      }
    },
    startNewProduct() {
      this.productAdminForm = this.emptyProductAdminForm();
      if (this.categoryForm.id) this.productAdminForm.category_id = this.categoryForm.id;
      this.showProductEditor = true;
    },
    editProductAdmin(product) {
      const config = product.config || {};
      this.productAdminForm = {
        id: product.id,
        track_inventory: [true,1,"1","t","true"].includes(product.track_inventory),
        allow_fraction: [true,1,"1","t","true"].includes(product.allow_fraction),
        color: product.config?.color || "",
        stock_policy: product.config?.stock_policy || "inherit",
        status: product.status || "active",
        config: product.config || {},
        name: product.name || '',
        sku: product.sku || '',
        barcode: product.barcode || '',
        category_id: product.category_id || null,
        preparation_area_id: product.preparation_area_id || null,
        sale_price: Number(product.sale_price || 0),
        cost: Number(product.cost || 0),
        product_type: product.product_type || 'sellable',
        time_minutes: Number(config.time_minutes || 0),
        send_to_kitchen: ![false, 0, '0', 'f', 'false'].includes(config.send_to_kitchen),
        tax_rate: Number(product.tax_rate || 0),
        has_tax: [true, 1, '1', 't', 'true'].includes(product.has_tax),
        active_pos: ![false, 0, '0', 'f', 'false'].includes(product.active_pos),
        active_menu: [true, 1, '1', 't', 'true'].includes(product.active_menu),
        group_ids: (product.group_ids || []).map(Number),
      };
      const category = this.optionAdminCategories.find((item) => Number(item.id) === Number(product.category_id));
      if (category) {
        this.categoryForm = {
          id: category.id,
          color: category.color || "#edf2f7",
          tile_size: category.tile_size || "normal",
          status: category.status || "active",
          name: category.name || '',
          description: category.description || '',
          category_type: category.category_type || 'pos',
          sort_order: Number(category.sort_order || 0),
        };
      }
      this.showProductEditor = true;
    },
    async saveProductAdmin() {
      if (!this.productAdminForm.name.trim()) {
        this.showError('Ponle nombre al producto.');
        return;
      }
      try {
        const payload = {
          ...this.productAdminForm,
          category_id: this.productAdminForm.category_id || null,
          preparation_area_id: this.productAdminForm.preparation_area_id || null,
          group_ids: (this.productAdminForm.group_ids || []).map(Number),
          config: {
            ...this.productAdminForm.config,
            color: this.productAdminForm.color || null,
            stock_policy: this.productAdminForm.stock_policy || "inherit",
            time_minutes: Math.max(0, Number(this.productAdminForm.time_minutes || 0)),
            send_to_kitchen: Boolean(this.productAdminForm.send_to_kitchen),
          },
        };
        const url = this.productAdminForm.id ? `/api/pos/catalog/products/${this.productAdminForm.id}` : '/api/pos/catalog/products';
        const method = this.productAdminForm.id ? 'put' : 'post';
        const { data } = await api[method](url, payload);
        this.showSuccess(data.message || 'Producto guardado.');
        await this.loadOptionAdmin();
        await this.loadCatalog();
        const saved = this.optionAdminProducts.find((product) => Number(product.id) === Number(data.product_id));
        if (saved) {
          this.productAdminForm = {
            id: saved.id,
            track_inventory: [true,1,"1","t","true"].includes(saved.track_inventory),
            allow_fraction: [true,1,"1","t","true"].includes(saved.allow_fraction),
            color: saved.config?.color || "",
            stock_policy: saved.config?.stock_policy || "inherit",
            status: saved.status || "active",
            config: saved.config || {},
            name: saved.name || '',
            sku: saved.sku || '',
            barcode: saved.barcode || '',
            category_id: saved.category_id || null,
            preparation_area_id: saved.preparation_area_id || null,
            sale_price: Number(saved.sale_price || 0),
            cost: Number(saved.cost || 0),
            product_type: saved.product_type || 'sellable',
            time_minutes: Number(saved.config?.time_minutes || 0),
            send_to_kitchen: ![false, 0, '0', 'f', 'false'].includes(saved.config?.send_to_kitchen),
            tax_rate: Number(saved.tax_rate || 0),
            has_tax: [true, 1, '1', 't', 'true'].includes(saved.has_tax),
            active_pos: ![false, 0, '0', 'f', 'false'].includes(saved.active_pos),
            active_menu: [true, 1, '1', 't', 'true'].includes(saved.active_menu),
            group_ids: (saved.group_ids || []).map(Number),
          };
        }
        this.showProductEditor = false;
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo guardar el producto.');
      }
    },
    startNewOptionGroup() {
      this.optionGroupForm = this.emptyOptionGroupForm();
      this.addOptionRow();
    },
    editOptionGroup(group) {
      this.optionGroupForm = {
        id: group.id,
        sell_standalone: [true,1,"1","t","true"].includes(group.sell_standalone),
        standalone_area_id: group.standalone_area_id || null,
        status: group.status || "active",
        name: group.name || '',
        code: group.code || '',
        selection_type: group.selection_type || 'single',
        is_required: [true, 1, '1', 't', 'true'].includes(group.is_required),
        min_select: Number(group.min_select || 0),
        max_select: group.max_select ?? null,
        position: Number(group.position || 0),
        options: (group.options || []).map((option) => ({
          id: option.id,
          status: option.status || "active",
          availability_status: option.availability_status || "visible",
          name: option.name || '',
          code: option.code || '',
          price_delta: Number(option.price_delta || 0),
          affects_price: ![false, 0, '0', 'f', 'false'].includes(option.affects_price),
          preparation_note: option.preparation_note || option.name || '',
          is_default: [true, 1, '1', 't', 'true'].includes(option.is_default),
          position: Number(option.position || 0),
        })),
        product_ids: (group.product_ids || []).map(Number),
      };
      if (!this.optionGroupForm.options.length) this.addOptionRow();
    },
    addOptionRow() {
      this.optionGroupForm.options.push({
        local_key: `${Date.now()}-${Math.random()}`,
        name: '', status: 'active', availability_status: 'visible',
        code: '',
        price_delta: 0,
        affects_price: true,
        preparation_note: '',
        is_default: false,
        position: this.optionGroupForm.options.length + 1,
      });
    },
    async removeOptionRow(index) {
      if (!await confirmAction('¿Quitar esta opción del grupo? Se conserva el historial de ventas.')) return;
      this.optionGroupForm.options.splice(index, 1);
    },
    async saveOptionGroup() {
      if (!this.optionGroupForm.name.trim()) {
        this.showError('Ponle nombre al grupo de extras.');
        return;
      }
      try {
        const payload = {
          ...this.optionGroupForm,
          options: this.optionGroupForm.options.filter((option) => option.name && option.name.trim()),
        };
        const url = this.optionGroupForm.id ? `/api/pos/catalog/option-groups/${this.optionGroupForm.id}` : '/api/pos/catalog/option-groups';
        const method = this.optionGroupForm.id ? 'put' : 'post';
        const { data } = await api[method](url, payload);
        this.showSuccess(data.message || 'Extras guardados.');
        await this.loadOptionAdmin();
        await this.loadCatalog();
        const saved = this.optionAdminGroups.find((group) => Number(group.id) === Number(data.option_group_id));
        if (saved) this.editOptionGroup(saved);
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudieron guardar extras.');
      }
    },
    async printSaleTicket(ticket) {
      try {
        const { data } = await api.get(`/api/pos/tickets/${ticket.id}`);
        this.printReceipt(data.ticket, data.items || [], data.payments || [], false);
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo reimprimir el ticket.');
      }
    },
    async openSaleTicket(ticket) {
      await this.loadTicket(ticket.id);
      this.showSales = false;
    },

    sideMenuAction(action) {
      this.posMenuOpen = false;
      if (typeof action === 'function') action.call(this);
    },
    async openPointAdjustModal() {
      this.pointAdjustSearch = '';
      this.pointAdjustResults = [];
      this.pointAdjustCustomer = null;
      this.pointAdjustForm = { points: 0, reason: '' };
      this.showPointAdjust = true;
    },
    async searchPointCustomers() {
      const q = this.pointAdjustSearch.trim();
      if (!q) { this.showError('Escribe nombre, correo o teléfono.'); return; }
      try {
        const { data } = await api.get('/api/pos/loyalty/customers/search', { params: { q } });
        this.pointAdjustResults = data.customers || [];
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo buscar clientes.');
      }
    },
    async submitPointAdjustment() {
      if (!this.pointAdjustCustomer) { this.showError('Selecciona un cliente.'); return; }
      try {
        const { data } = await api.post(`/api/pos/loyalty/customers/${this.pointAdjustCustomer.id}/adjust-points`, this.pointAdjustForm);
        this.showPointAdjust = false;
        this.showSuccess(data.message || 'Puntos asignados correctamente.');
        await this.loadCustomers();
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudieron asignar puntos.');
      }
    },
    openDiscountManager() {
      this.discountForm.reason = '';
      this.newDiscountForm = this.emptyDiscountForm();
      this.showDiscounts = true;
      this.loadDiscounts();
    },
    openItemDiscountModal(item) {
      this.itemDiscountTarget = item;
      this.itemDiscountForm = { percent: null, amount: null, reason: '' };
      this.showItemDiscount = true;
    },
    async applyItemDiscount() {
      if (!this.activeTicket || !this.itemDiscountTarget) return;
      try {
        const { data } = await api.post(`/api/pos/tickets/${this.activeTicket.id}/items/${this.itemDiscountTarget.id}/discount`, this.itemDiscountForm);
        this.activeTicket = data.ticket;
        this.items = data.items || [];
        this.payments = data.payments || [];
        this.activeTimer = data.timer ? { ...data.timer, _received_at: Date.now() } : null;
        this.showItemDiscount = false;
        this.showSuccess('Descuento aplicado al producto.');
        await this.loadTickets();
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo aplicar descuento al producto.');
      }
    },
    async deleteDiscount(discount) {
      if (!discount || !confirm(`¿Eliminar descuento "${discount.name}"? No afectará tickets anteriores.`)) return;
      try {
        await api.delete(`/api/pos/discounts/${discount.id}`);
        await this.loadDiscounts();
        this.showSuccess('Descuento eliminado.');
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo eliminar el descuento.');
      }
    },
    async switchCashier() {
      await this.$store.dispatch('logout');
      this.$router.push('/login');
    },
    openAccountsModal() {
      this.showOpenAccounts = true;
      this.loadTickets();
    },
    async openTicketFromSummary(ticket) {
      await this.loadTicket(ticket.id);
      this.showOpenAccounts = false;
    },
    async printAccountFromSummary(ticket) {
      try {
        const { data } = await api.get(`/api/pos/tickets/${ticket.id}`);
        this.printReceipt(data.ticket, data.items || [], data.payments || [], false);
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo imprimir la cuenta.');
      }
    },
    discountText(discount) {
      const type = discount.ticket_discount_type || discount.discount_type;
      const value = Number(discount.ticket_discount_value ?? discount.discount_value ?? 0);
      if (type === 'percent') return `${value}%`;
      return this.money(value);
    },
    emptyDiscountForm() {
      return { id: null, name: '', discount_type: 'percent', discount_value: 0, requires_reason: false };
    },
    async openDiscountModal() {
      if (!this.activeTicket) return;
      this.discountForm.reason = this.activeTicket.discount_reason || '';
      this.newDiscountForm = this.emptyDiscountForm();
      this.showDiscounts = true;
      await this.loadDiscounts();
    },
    openCouponModal() {
      if (!this.activeTicket || this.activeTicket._pending || Number(this.activeTicket.id)<=0) {
        this.showError('Primero sincroniza y selecciona una cuenta del servidor.');return;
      }
      if (!this.couponEnabled) { this.showError('Los cupones no están habilitados en esta sucursal.');return; }
      if (Number(this.activeTicket.paid_total||0)>0 && !this.activeTicket.coupon) {this.showError('Aplica el cupón antes del primer pago.');return;}
      this.couponCode='';this.couponPreview=null;this.couponSelectedItemId=0;this.couponQuantity=1;
      this.couponError='';this.showCoupon=true;
      this.$nextTick(()=>this.$refs.couponCodeInput?.focus?.());
    },
    async previewCoupon() {
      if(!this.activeTicket || !this.couponCode)return;
      this.couponBusy=true;this.couponError='';this.couponPreview=null;
      try {
        const {data}=await rawApi.post(`/api/pos/tickets/${this.activeTicket.id}/coupon/preview`,{code:this.couponCode});
        this.couponPreview=data;
        const candidates=data.eligible_items||[];
        if (candidates.length===1) this.couponSelectedItemId=Number(candidates[0].id);
        this.couponQuantity=1;
      }catch(e){this.couponError=e.response?.data?.error||'No se pudo validar el código. Revisa conexión y vigencia.';}
      finally{this.couponBusy=false;}
    },
    async applyCoupon() {
      if(!this.activeTicket||!this.couponPreview)return;
      this.couponBusy=true;this.couponError='';
      try {
        const payload={code:this.couponCode};
        if(this.couponPreview.campaign.application_mode==='choose_one'){
          payload.item_id=this.couponSelectedItemId;payload.quantity=this.couponQuantity;
        }
        const {data}=await rawApi.post(`/api/pos/tickets/${this.activeTicket.id}/coupon`,payload);
        this.showCoupon=false;await this.loadTicket(data.ticket.id);await this.loadTickets();
        this.showSuccess('Cupón reservado. Se canjeará cuando registres el primer pago.');
      }catch(e){this.couponError=e.response?.data?.error||'No se pudo aplicar el cupón. Recarga la cuenta.';}
      finally{this.couponBusy=false;}
    },
    async removeCoupon() {
      if(!this.activeTicket)return;
      this.couponBusy=true;this.couponError='';
      try {await rawApi.delete(`/api/pos/tickets/${this.activeTicket.id}/coupon`);
        await this.loadTicket(this.activeTicket.id);await this.loadTickets();this.showCoupon=false;
        this.showSuccess('Cupón liberado: no se consumió ningún uso.');
      }catch(e){this.couponError=e.response?.data?.error||'No fue posible quitar el cupón.';}
      finally{this.couponBusy=false;}
    },
    async applyDiscount(discount) {
      if (!this.activeTicket || !discount) return;
      if (Number(this.activeTicket.paid_total || 0) > 0) {
        this.showError('No puedes cambiar descuentos cuando la cuenta ya tiene pagos.');
        return;
      }
      try {
        const { data } = await api.post(`/api/pos/tickets/${this.activeTicket.id}/discount`, {
          discount_id: discount.id,
          reason: this.discountForm.reason,
        });
        this.activeTicket = data.ticket;
        this.items = data.items || [];
        this.payments = data.payments || [];
        this.activeTimer = data.timer ? { ...data.timer, _received_at: Date.now() } : null;
        this.paymentForm.amount = Number(this.balanceDue.toFixed(2));
        if (this.paymentForm.payment_method_code === 'cash') this.paymentForm.received_amount = Number(this.balanceDue.toFixed(2));
        this.showDiscounts = false;
        this.showSuccess('Descuento aplicado correctamente.');
        this.emitModuleEvent('pos.ticket.updated', { ticket: data.ticket });
        await this.loadTickets();
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo aplicar el descuento.');
      }
    },
    async clearTicketDiscount() {
      if (!this.activeTicket) return;
      if (Number(this.activeTicket.paid_total || 0) > 0) {
        this.showError('No puedes quitar descuentos cuando la cuenta ya tiene pagos.');
        return;
      }
      try {
        const { data } = await api.delete(`/api/pos/tickets/${this.activeTicket.id}/discount`);
        this.activeTicket = data.ticket;
        this.items = data.items || [];
        this.payments = data.payments || [];
        this.activeTimer = data.timer ? { ...data.timer, _received_at: Date.now() } : null;
        this.paymentForm.amount = Number(this.balanceDue.toFixed(2));
        if (this.paymentForm.payment_method_code === 'cash') this.paymentForm.received_amount = Number(this.balanceDue.toFixed(2));
        this.discountForm.reason = '';
        this.showSuccess('Descuento quitado.');
        this.emitModuleEvent('pos.ticket.updated', { ticket: data.ticket });
        await this.loadTickets();
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo quitar el descuento.');
      }
    },
    editDiscount(discount) {
      this.newDiscountForm = {
        id: discount.id,
        name: discount.name || '',
        discount_type: discount.discount_type || 'percent',
        discount_value: Number(discount.discount_value || 0),
        requires_reason: [true, 1, '1', 't', 'true'].includes(discount.requires_reason),
      };
    },
    async saveDiscount() {
      const form = { ...this.newDiscountForm };
      if (!form.name || Number(form.discount_value || 0) <= 0) {
        this.showError('Captura nombre y valor del descuento.');
        return;
      }
      try {
        if (form.id) {
          await api.put(`/api/pos/discounts/${form.id}`, form);
        } else {
          await api.post('/api/pos/discounts', form);
        }
        this.newDiscountForm = this.emptyDiscountForm();
        await this.loadDiscounts();
        this.showSuccess('Descuento guardado.');
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo guardar el descuento.');
      }
    },
    async payTicket() {
      if (!this.activeTicket || this.paymentInFlight) return;
      if (this.multiRegisterEnabled && !this.canOperateCash) {
        this.showError('Esta caja tiene otro cajero responsable. Solicita al gerente autorización.');return;
      }
      this.paymentInFlight = true;
      const shouldPrint = this.paymentPrintRequested;
      if (this.paymentForm.payment_method_code === 'cash' && this.cashReceivedMissing) {
        this.showError('El efectivo recibido no cubre el monto a cobrar.');
        this.paymentInFlight = false;
        return;
      }
      try {
        const payload = { ...this.paymentForm, amount: this.paymentForm.amount || this.balanceDue,
          cash_session_id: this.cashSession?.id, expected_total: this.activeTicket.total,
          print_requested: shouldPrint,
        };
        // El campo es optativo; no se exige ni se arrastra una referencia oculta.
        payload.reference = this.selectedPaymentReferenceVisible ? String(payload.reference || '').trim() : null;
        const { data } = await api.post(`/api/pos/tickets/${this.activeTicket.id}/pay`, payload);
        this.activeTicket = data.ticket;
        this.items = data.items || [];
        this.payments = data.payments || [];
        this.activeTimer = data.timer ? { ...data.timer, _received_at: Date.now() } : null;
        this.showPayment = false;
        this.resetPaymentForm();
        const locallyQueued = !!data.ticket?._pending;
        const loyaltyState = data.loyalty_sync?.state;
        if (locallyQueued) {
          this.showSuccess('Cobro guardado en este dispositivo. AÚN pendiente de sincronizar con caja.');
        } else if (loyaltyState === 'pending_retry') {
          this.showSuccess('Pago registrado. Los puntos quedaron guardados y se reintentaran automaticamente.');
        } else if (loyaltyState === 'local_only') {
          this.showSuccess('Pago y puntos registrados en Gestion Total. El cliente aun no tiene wallet externa vinculada.');
        } else {
          this.showSuccess('Pago registrado.');
        }
        if (data.ticket.status === 'paid' && shouldPrint) {
          // Close the payment modal before opening the browser's print document.
          // Print the confirmed snapshot before clearing the account or refreshing it.
          await this.$nextTick();
          try {
            if (locallyQueued) {
              // En efectivo el motor POS es offline-first, aunque haya conexion.
              // El ID de esta cuenta puede ser NEGATIVO y no existe todavia
              // pos_receipt_print_jobs en el servidor. El outbox conserva
              // print_requested y creara la orden de impresion al sincronizar.
              await printDocument(this.buildReceiptHtml(data.ticket, data.items || [], data.payments || []));
              this.showSuccess('Ticket local enviado a Chrome. Cuando sincronice, valida el papel en Impresiones pendientes.');
            } else {
              await this.printPaidReceiptQueued(data.ticket, data.items || [], data.payments || []);
            }
          } catch (_error) {
            this.showError(locallyQueued
              ? 'Cobro local guardado. La impresión no se confirmó; SINCRONIZA antes de recuperarla desde Impresiones pendientes. No vuelvas a cobrar.'
              : 'Cobro registrado, pero ticket pendiente de impresión. Abre Impresiones pendientes; NO vuelvas a cobrar.');
          }
        }
        if (data.ticket.status === 'paid') this.clearTicket();
        this.emitModuleEvent('pos.ticket.updated', { ticket: data.ticket });
        await Promise.all([this.loadTickets(), this.loadTables(), this.loadCustomers()]);
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo cobrar. Comprueba si el pago se registró antes de intentarlo de nuevo.');
      } finally {
        this.paymentInFlight = false;
      }
    },
    newPosRequestKey() {
      return (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `pos-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    },
    kitchenPrintStatusLabel(status) {
      return { pending: 'Pendiente de impresión', submitted: 'Enviada a Chrome (sin confirmar)', printed: 'Papel confirmado', error: 'Error de impresión' }[status] || 'Pendiente';
    },
    async openPendingPrints() {
      try {
        const [{ data: kitchen }, { data: receipts }] = await Promise.all([
          api.get('/api/pos/print/pending'), api.get('/api/pos/print/receipts/pending'),
        ]);
        this.kitchenTickets = kitchen.kitchen_tickets || [];
        this.receiptPrintJobs = receipts.receipts || [];
        if (!this.kitchenTickets.length && !this.receiptPrintJobs.length) this.showSuccess('No hay impresiones por confirmar.');
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo consultar la cola de impresión.');
      }
    },
    async printPaidReceiptQueued(ticket, items, payments) {
      const ticketId = Number(ticket.id);
      // El servidor persiste la solicitud antes de imprimir, desde la transaccion del cobro.
      try {
        await printDocument(this.buildReceiptHtml(ticket, items, payments));
        await api.post(`/api/pos/print/receipts/${ticketId}/status`, { status: 'submitted' });
        this.showSuccess('Comprobante enviado a Chrome. Confirma el papel desde Impresiones pendientes.');
      } catch (error) {
        const details = error.response?.data?.error || error.message || 'No se pudo abrir la impresión';
        try { await api.post(`/api/pos/print/receipts/${ticketId}/status`, { status: 'error', error: details }); }
        catch (_ignore) { /* La cola persistente sigue pendiente del lado servidor. */ }
        throw error;
      }
    },
    async printPendingReceipt(job) {
      if (job.print_busy) return;
      job.print_busy = true;
      try {
        const { data } = await api.get(`/api/pos/tickets/${job.ticket_id}`);
        await printDocument(this.buildReceiptHtml(data.ticket, data.items || [], data.payments || []));
        const result = await api.post(`/api/pos/print/receipts/${job.ticket_id}/status`, { status: 'submitted' });
        job.print_status = result.data.print_status;
        job.print_attempts = result.data.print_attempts;
        job.print_error = null;
        this.showSuccess('Enviado a Chrome. Confirma al ver el papel.');
      } catch (error) {
        const message = error.response?.data?.error || error.message || 'No se pudo imprimir';
        try { await api.post(`/api/pos/print/receipts/${job.ticket_id}/status`, { status: 'error', error: message }); }
        catch (_ignore) { /* Sigue pendiente. */ }
        job.print_status = 'error'; job.print_error = message;
        this.showError('Ticket pagado: ' + message + '. No vuelvas a cobrar.');
      } finally { job.print_busy = false; }
    },
    async confirmReceiptPrinted(job) {
      if (!await confirmAction('¿El comprobante salió físicamente y es legible?')) return;
      try {
        await api.post(`/api/pos/print/receipts/${job.ticket_id}/status`, { status: 'printed', operator_confirmed: true });
        this.receiptPrintJobs = this.receiptPrintJobs.filter(p => Number(p.ticket_id) !== Number(job.ticket_id));
        this.showSuccess('Comprobante marcado como impreso.');
      } catch (error) { this.showError(error.response?.data?.error || 'No se pudo confirmar el comprobante.'); }
    },
    async printCommand(command) {
      if (!command?.id || command.print_busy) return;
      command.print_busy = true;
      const localCommand = !/^\d+$/.test(String(command.id));
      try {
        await printDocument(this.buildKitchenHtml(command));
        if (localCommand) {
          // No existe un ID de comanda en el backend hasta que POS sincronice.
          // Mostrar en Impresiones pendientes despues para confirmar papel.
          this.showSuccess('Comanda local enviada a Chrome. Sincroniza y confirma su impresión desde la cola.');
          return;
        }
        // afterprint NO garantiza papel físico: solo se registra como enviada.
        if (command.print_status !== 'printed') {
          const { data } = await api.post(`/api/pos/print/commands/${command.id}/status`, { status: 'submitted' });
          command.print_status = data.print_status;
          command.print_attempts = data.print_attempts;
          command.print_error = '';
          this.showSuccess('Enviado a Chrome. Confirma cuando veas el papel impreso.');
        }
      } catch (error) {
        const err = error.response?.data?.error || error.message || 'No se pudo imprimir';
        if (command.print_status !== 'printed' && !localCommand) {
          try { await api.post(`/api/pos/print/commands/${command.id}/status`, { status: 'error', error: err }); }
          catch (_ignore) { /* Permanece pendiente en servidor. */ }
          command.print_status = 'error'; command.print_error = err;
        }
        this.showError(`Comanda ${command.id}: ${err}. Puedes reintentar sin enviar nuevamente la orden.`);
      } finally {
        command.print_busy = false;
      }
    },
    async confirmCommandPrinted(command) {
      if (!command?.id || !/^\d+$/.test(String(command.id)) || !await confirmAction('¿Confirmas que la comanda salió físicamente en papel y es legible?')) return;
      try {
        await api.post(`/api/pos/print/commands/${command.id}/status`, { status: 'printed', operator_confirmed: true });
        command.print_status = 'printed'; command.print_error = '';
        this.showSuccess('Comanda confirmada.');
      } catch (error) { this.showError(error.response?.data?.error || 'No se pudo confirmar la impresión.'); }
    },
    refundAvailable(payment) {
      const returned = this.refundHistory.filter(r => Number(r.payment_id) === Number(payment.id)).reduce((sum, r) => sum + Number(r.amount || 0), 0);
      return Math.max(0, Math.round((Number(payment.amount || 0) - returned) * 100) / 100);
    },
    async openRefundModal(ticket) {
      if (this.multiRegisterEnabled && !this.canOperateCash) return this.showError('Devoluciones reservadas al cajero responsable o gerente.');
      if (!this.cashSession) { this.showError('Abre una caja antes de devolver dinero.'); return; }
      try {
        const [{ data: detail }, { data: prior }] = await Promise.all([
          api.get(`/api/pos/tickets/${ticket.id}`), api.get(`/api/pos/tickets/${ticket.id}/refunds`),
        ]);
        this.refundPayments = detail.payments || [];
        this.refundHistory = prior.refunds || [];
        const first = this.refundPayments.find(p => this.refundAvailable(p) > 0 && p.payment_method_code !== 'customer_credit');
        if (!first) { this.showError('Este ticket no tiene pagos disponibles para devolución monetaria.'); return; }
        this.refundForm = { ticket_id: Number(ticket.id), payment_id: Number(first.id), amount: this.refundAvailable(first), reason: '', admin_pin: '', external_refund_confirmed: false, request_key: this.newPosRequestKey() };
        this.showRefundModal = true;
      } catch (error) { this.showError(error.response?.data?.error || 'No se pudieron consultar los pagos.'); }
    },
    selectRefundPayment() {
      const payment = this.refundPayments.find(p => Number(p.id) === Number(this.refundForm.payment_id));
      this.refundForm.amount = payment ? this.refundAvailable(payment) : 0;
      this.refundForm.external_refund_confirmed = false;
      this.refundForm.request_key = this.newPosRequestKey();
    },
    async submitRefund() {
      if (this.refundSaving) return;
      const pay = this.refundPayments.find(p => Number(p.id) === Number(this.refundForm.payment_id));
      if (!pay || Number(this.refundForm.amount) <= 0 || Number(this.refundForm.amount) > this.refundAvailable(pay)) {
        this.showError('El importe supera lo disponible para devolver.'); return;
      }
      if (String(this.refundForm.reason || '').trim().length < 8) {
        this.showError('El motivo necesita al menos ocho caracteres.'); return;
      }
      if (!await confirmAction('¿Ya devolviste el dinero? Se registrará un movimiento irreversible de caja.')) return;
      this.refundSaving = true;
      try {
        const payload = { ...this.refundForm, cash_session_id: this.cashSession.id };
        const { data } = await api.post(`/api/pos/tickets/${payload.ticket_id}/payments/${payload.payment_id}/refund`, payload);
        this.showRefundModal = false;
        this.showSuccess(data.message || 'Devolución monetaria registrada.');
        await Promise.all([this.loadCash(), this.loadSales()]);
      } catch (error) { this.showError(error.response?.data?.error || 'No se pudo registrar la devolución. Revisa el historial antes de reintentar.'); }
      finally { this.refundSaving = false; }
    },
    printCashCut(cut) {
      const html = this.buildCashCutHtml(cut);
      this.printHtml(html);
    },
    buildCashCutHtml(cut) {
      const settings = this.receiptSettings || {};
      const width = Number(settings.ticket_width_mm || 58);
      const methods = this.normalizedArray(cut.payment_breakdown);
      const areaPayments = this.cashCutAreaPaymentRows(cut);
      const denominations = this.normalizedArray(cut.denomination_counts).filter((row) => Number(row.count || 0) > 0);
      const expenseBreakdown = this.normalizedArray(cut.expense_breakdown);
      const expenseDetails = this.normalizedArray(cut.expenses_detail);
      const methodsHtml = methods.map((method) => `<div class="row"><span>${this.escapeHtml(method.name)}</span><strong>${this.money(method.total_amount)}</strong></div>`).join('');
      const areaPaymentsHtml = areaPayments.map((area) => {
        const rows = area.payment_breakdown.map((method) => `<div class="row"><span>${this.escapeHtml(method.name)}</span><strong>${this.money(method.total_amount)}</strong></div>`).join('');
        return `<div class="area-title">${this.escapeHtml(area.area_name || 'General')}</div>${rows}<div class="row area-total"><span>Total ${this.escapeHtml(area.area_name || 'General')}</span><strong>${this.money(area.total_amount)}</strong></div>`;
      }).join('<div class="mini-dash"></div>');
      const expensesHtml = expenseBreakdown.map((expense) => `<div class="row"><span>${this.escapeHtml(expense.category || 'general')} · ${this.escapeHtml(expense.payment_method_name || expense.payment_method_code || '')}</span><strong>${this.money(expense.total_amount)}</strong></div>`).join('');
      const expenseDetailsHtml = expenseDetails.map((expense) => {
        const meta = [this.shortTime(expense.created_at), expense.payment_method_name || expense.payment_method_code, expense.paid_to].filter(Boolean).join(' · ');
        return `<div class="row"><span>${this.escapeHtml(expense.concept || '')}<br><small>${this.escapeHtml(meta)}</small></span><strong>${this.money(expense.amount)}</strong></div>`;
      }).join('');
      const denominationsHtml = denominations.map((row) => `<div class="row"><span>${this.escapeHtml(row.label)} x ${this.escapeHtml(row.count)}</span><strong>${this.money(row.total)}</strong></div>`).join('');
      return `<!doctype html>
        <html><head><title>Corte ${this.escapeHtml(cut.cut_type || '')}</title>${this.ticketPrintStyles(width)}</head>
        <body>
          ${settings.show_logo && settings.logo_url ? `<img class="logo" src="${this.escapeHtml(settings.logo_url)}" alt="Logo">` : ''}
          <h1>CORTE ${this.escapeHtml(cut.cut_type || '')}</h1>
          <div class="center">${this.escapeHtml(settings.business_name || this.contextLabel)}<br>${this.escapeHtml(this.shortTime(cut.created_at || new Date()))}</div>
          <div class="dash"></div>
          <div class="row"><span>Ventas liquidadas</span><strong>${this.money(cut.paid_total)}</strong></div>
          <div class="row"><span>Cobros recibidos</span><strong>${this.money(cut.collections_total || 0)}</strong></div>
          <div class="row"><span>Abonos cuentas abiertas</span><strong>${this.money(cut.open_account_collections || 0)}</strong></div>
          <div class="row"><span>Devoluciones</span><strong>-${this.money(cut.refunds_total || 0)}</strong></div>
          <div class="row"><span>Tickets</span><strong>${this.escapeHtml(cut.tickets_count || 0)}</strong></div>
          <div class="row"><span>Pagos</span><strong>${this.escapeHtml(cut.payments_count || 0)}</strong></div>
          <div class="dash"></div>
          <h2>VENTAS LIQUIDADAS POR AREA</h2>
          ${areaPaymentsHtml || '<p class="center">Sin desglose por forma de pago</p>'}
          <div class="row total store-total"><span>TOTAL VENTA TIENDA · CORTE ${this.escapeHtml(cut.cut_type || '')}</span><strong>${this.money(this.cashCutStoreTotal(cut))}</strong></div>
          <div class="dash"></div>
          <h2>ENTRADAS POR FORMA DE PAGO</h2>
          ${methodsHtml || '<p class="center">Sin pagos</p>'}
          <div class="dash"></div>
          <h2>SALIDAS / GASTOS</h2>
          <div class="row"><span>Total salidas</span><strong>${this.money(cut.expenses_total || 0)}</strong></div>
          ${expensesHtml || '<p class="center">Sin salidas</p>'}
          ${expenseDetailsHtml ? `<div class="dash"></div><h2>DETALLE DE SALIDAS</h2>${expenseDetailsHtml}` : ''}
          <div class="dash"></div>
          <h2>CONTEO DE EFECTIVO</h2>
          <div class="row"><span>Fondo inicial</span><strong>${this.money(cut.opening_cash_amount)}</strong></div>
          <div class="row"><span>Efectivo esperado</span><strong>${this.money(cut.expected_cash_amount)}</strong></div>
          ${denominationsHtml || '<p class="center">Sin denominaciones capturadas</p>'}
          <div class="row total"><span>Efectivo contado</span><strong>${this.money(cut.declared_cash_amount)}</strong></div>
          <div class="row total"><span>Diferencia</span><strong>${this.money(cut.difference_amount)}</strong></div>
          ${cut.notes ? `<div class="dash"></div><div class="preline">${this.escapeHtml(cut.notes)}</div>` : ''}
          ${settings.footer_text ? `<div class="dash"></div><div class="center preline">${this.escapeHtml(settings.footer_text)}</div>` : ''}
        </body></html>`;
    },
    printReceiptPreview() {
      const sampleTicket = this.activeTicket || {
        ticket_number: 'VISTA-PREVIA',
        order_label: 'Mesa 1',
        customer_name: 'Cliente ejemplo',
        subtotal: 100,
        tax_total: 16,
        total: 116,
        paid_total: 0,
        opened_at: new Date().toISOString(),
      };
      const sampleItems = this.activeItems.length ? this.activeItems : [
        { quantity: 1, name: 'Latte', unit_price: 55, line_total: 70, notes: 'Sin azúcar', modifiers: [{ name: 'Leche almendras', price_delta: 15, preparation_note: 'Leche almendras' }] },
      ];
      this.printReceipt(sampleTicket, sampleItems, this.payments || [], false);
    },
    printReceipt(ticket, items = [], payments = [], autoClose = false) {
      const html = this.buildReceiptHtml(ticket, items, payments);
      return this.printHtml(html, autoClose);
    },
    printLetterHtml(html) { return this.printHtml(html); },
    buildCashExpenseHtml(expense) {
      const settings = this.receiptSettings || {};
      const width = Number(settings.ticket_width_mm || 58);
      return `<!doctype html>
        <html><head><title>Salida de caja</title>${this.ticketPrintStyles(width)}</head>
        <body>
          ${settings.show_logo && settings.logo_url ? `<img class="logo" src="${this.escapeHtml(settings.logo_url)}" alt="Logo">` : ''}
          <h1>SALIDA DE CAJA</h1>
          <div class="center">${this.escapeHtml(settings.business_name || this.contextLabel)}<br>${this.escapeHtml(this.shortDateTime(expense.created_at || Date.now()))}</div>
          <div class="dash"></div>
          <div class="row total"><span>Monto</span><strong>${this.money(expense.amount)}</strong></div>
          <div class="row"><span>Método</span><strong>${this.escapeHtml(expense.payment_method_name || expense.payment_method_code || '')}</strong></div>
          <div class="row"><span>Categoría</span><strong>${this.escapeHtml(expense.category || 'general')}</strong></div>
          <div class="dash"></div>
          <h2>CONCEPTO</h2>
          <div class="preline center">${this.escapeHtml(expense.concept || '')}</div>
          ${expense.paid_to ? `<div class="row"><span>Pagado a</span><strong>${this.escapeHtml(expense.paid_to)}</strong></div>` : ''}
          ${expense.receipt_number ? `<div class="row"><span>Folio</span><strong>${this.escapeHtml(expense.receipt_number)}</strong></div>` : ''}
          <div class="row"><span>Caja</span><strong>${this.escapeHtml(expense.cash_session_id || '')}</strong></div>
          <div class="row"><span>Registró</span><strong>${this.escapeHtml(expense.created_by_name || '')}</strong></div>
          ${expense.notes ? `<div class="dash"></div><h2>NOTAS</h2><div class="preline center">${this.escapeHtml(expense.notes)}</div>` : ''}
          <div class="dash"></div>
          <div class="center preline">ENTREGÓ

________________________</div>
          <div class="dash"></div>
          <div class="center preline">RECIBIÓ / AUTORIZÓ

________________________</div>
          ${settings.footer_text ? `<div class="dash"></div><div class="center preline">${this.escapeHtml(settings.footer_text)}</div>` : ''}
        </body></html>`;
    },

    printHtml(html) { return printDocument(html).catch(() => this.showError("No se pudo abrir la impresión. Revisa la impresora.")); },
    buildReceiptHtml(ticket, items = [], payments = []) {
      const settings = this.receiptSettings || {};
      const width = Number(settings.ticket_width_mm || 58);
      const traceId = ticket.ticket_uuid || ticket.uuid || '';
      const hasLoyaltyCustomer = Boolean(ticket.customer_id || ticket.customer_name || ticket.customer_email);
      const smartClaimUrl = traceId ? String(ticket.loyalty_claim_url || '').trim() : '';
      const qrSrc = smartClaimUrl && !hasLoyaltyCustomer ? qrDataUrl(smartClaimUrl) : '';
      const activeItems = (items || []).filter((item) => item.status !== 'removed');
      const paymentsHtml = (payments || []).map((payment) => `
        <div class="row"><span>${this.escapeHtml(payment.payment_method_code)}</span><strong>${this.money(payment.amount)}</strong></div>
        ${Number(payment.change_amount || 0) > 0 ? `<div class="row"><span>Cambio</span><strong>${this.money(payment.change_amount)}</strong></div>` : ''}
      `).join('');
      const itemsHtml = activeItems.map((item) => {
        const modifiers = this.normalizedModifiers(item).map((mod) => `<span class="mod">${this.escapeHtml(mod.preparation_note || mod.name)}${Number(mod.price_delta || 0) ? ` +${this.money(mod.price_delta)}` : ''}</span>`).join('');
        return `<div class="item"><div><strong>${this.escapeHtml(item.quantity)} x ${this.escapeHtml(item.name)}</strong>${modifiers}</div><span>${this.money(item.line_total)}</span></div>`;
      }).join('');
      return `<!doctype html>
        <html><head><title>Cuenta ${this.escapeHtml(ticket.ticket_number || '')}</title>${this.ticketPrintStyles(width)}</head>
        <body>
          ${settings.show_logo && settings.logo_url ? `<img class="logo" src="${this.escapeHtml(settings.logo_url)}" alt="Logo">` : ''}
          <h1>${this.escapeHtml(settings.business_name || this.contextLabel)}</h1>
          ${settings.header_text ? `<div class="center preline">${this.escapeHtml(settings.header_text)}</div>` : ''}
          <div class="dash"></div>
          <div class="row"><span>Cuenta</span><strong>${this.escapeHtml(ticket.ticket_number || '')}</strong></div>
          <div class="row"><span>Orden</span><strong>${this.escapeHtml(this.ticketLabel(ticket))}</strong></div>
          ${settings.show_customer && ticket.customer_name ? `<div class="row"><span>Cliente</span><strong>${this.escapeHtml(ticket.customer_name)}</strong></div>` : ''}
          <div class="row"><span>Hora</span><strong>${this.shortTime(ticket.closed_at || ticket.opened_at || new Date())}</strong></div>
          <div class="dash"></div>
          ${itemsHtml || '<p class="center">Sin productos</p>'}
          <div class="dash"></div>
          <div class="row"><span>Subtotal</span><strong>${this.money(ticket.subtotal)}</strong></div>
          ${Number(ticket.ticket_discount_amount || 0) > 0 ? `<div class="row"><span>${this.escapeHtml(ticket.ticket_discount_name || 'Descuento')}</span><strong>-${this.money(ticket.ticket_discount_amount)}</strong></div>` : ''}
          ${settings.show_tax ? `<div class="row"><span>IVA</span><strong>${this.money(ticket.tax_total)}</strong></div>` : ''}
          <div class="row total"><span>Total</span><strong>${this.money(ticket.total)}</strong></div>
          ${paymentsHtml ? `<div class="dash"></div>${paymentsHtml}` : ''}
          ${ticket._pending ? '<p class="center">Venta guardada en este equipo · pendiente de sincronizar. Los puntos estarán disponibles al confirmar la venta en el servidor.</p>' : ''}
          ${hasLoyaltyCustomer ? `<div class="dash"></div><div class="claim-title">Esta compra asignara tus puntos automaticamente a tu wallet.</div><div class="center">${this.escapeHtml(ticket.customer_name || 'Cliente con wallet')}<br>${this.escapeHtml(this.maskEmail(ticket.customer_email || ''))}</div>` : (smartClaimUrl ? `<div class="dash"></div><div class="claim-title">Escanea con la camara de tu celular para reclamar tus puntos</div><img class="uuid-qr" src="${qrSrc}" alt="QR para reclamar puntos"><div class="center">El enlace abre el programa de lealtad configurado para esta sucursal.</div>` : '')}
          ${settings.footer_text ? `<div class="dash"></div><div class="center preline">${this.escapeHtml(settings.footer_text)}</div>` : ''}
        </body></html>`;
    },
    buildKitchenHtml(command) {
      const settings = this.receiptSettings || {};
      const width = Number(settings.ticket_width_mm || 58);
      const itemsHtml = (command.items || []).map((item) => `<div class="kitem"><strong>${this.escapeHtml(item.quantity)} x ${this.escapeHtml(item.name)}</strong>${(item.modifiers || []).map((mod) => `<span class="mod">${this.escapeHtml(mod.preparation_note || mod.name)}</span>`).join('')}${item.notes ? `<span class="note">Nota: ${this.escapeHtml(item.notes)}</span>` : ''}</div>`).join('');
      return `<!doctype html>
        <html><head><title>Comanda ${this.escapeHtml(command.area_name)}</title>${this.ticketPrintStyles(width)}</head>
        <body>
          <h1>${command.type === 'cancellation' ? '*** CANCELACION ***' : 'COMANDA'} ${this.escapeHtml(command.area_name)}</h1>
          <div class="center">${this.escapeHtml(command.order_label)}<br>${this.escapeHtml(this.shortTime(command.created_at))}</div>
          ${command.type === 'cancellation' ? `<div class="dash"></div><div class="center preline">NO PREPARAR / DETENER PREPARACION<br>Motivo: ${this.escapeHtml(command.reason || 'Cancelado')}</div>` : ''}
          <div class="dash"></div>
          ${itemsHtml}
          ${settings.kitchen_footer_text ? `<div class="dash"></div><div class="center preline">${this.escapeHtml(settings.kitchen_footer_text)}</div>` : ''}
        </body></html>`;
    },
    ticketPrintStyles(width) {
      const rollWidth = [58, 72, 80].includes(Number(width)) ? Number(width) : 58;
      const paperWidth = rollWidth;
      const bodyWidth = rollWidth;
      const sidePadding = rollWidth <= 58 ? 2 : 3;
      const fontSize = rollWidth <= 58 ? 10.5 : 12;
      const titleSize = rollWidth <= 58 ? 13 : 16;
      const totalSize = rollWidth <= 58 ? 13 : 15;
      const logoWidth = rollWidth <= 58 ? 32 : 42;
      const logoHeight = rollWidth <= 58 ? 16 : 24;

      return `<style>
        @page { size: ${paperWidth}mm auto; margin: 0; }
        * { box-sizing: border-box; }
        html, body { margin: 0; padding: 0; background: #fff; }
        body {
          width: ${bodyWidth}mm;
          padding: ${sidePadding}mm;
          color: #000;
          font-family: "Courier New", Courier, monospace;
          font-size: ${fontSize}px;
          line-height: 1.22;
          font-weight: 700;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
          text-rendering: geometricPrecision;
        }
        h1 {
          font-size: ${titleSize}px;
          line-height: 1.12;
          text-align: center;
          margin: 0 0 5px;
          font-weight: 900;
          text-transform: uppercase;
        }
        h2 {
          font-size: ${Math.max(10, fontSize)}px;
          margin: 2px 0 4px;
          text-align: center;
          font-weight: 900;
          text-transform: uppercase;
        }
        .logo {
          display: block;
          max-width: ${logoWidth}mm;
          max-height: ${logoHeight}mm;
          object-fit: contain;
          margin: 0 auto 5px;
          filter: grayscale(1) contrast(1.35);
        }
        .claim-title { text-align: center; font-weight: 700; font-size: 10px; line-height: 1.2; margin-bottom: 4px; }
        .uuid-qr { display: block; width: 90%; max-width: 90%; aspect-ratio: 1 / 1; margin: 6px auto 3px; image-rendering: pixelated; }
        .trace-id { text-align: center; overflow-wrap: anywhere; word-break: break-word; font-size: ${Math.max(7, fontSize - 2)}px; font-weight: 900; }
        .center { text-align: center; }
        .preline { white-space: pre-line; }
        .dash { border-top: 1px dashed #000; margin: 5px 0; height: 0; }
        .mini-dash { border-top: 1px dotted #000; margin: 4px 0; height: 0; }
        .area-title { font-weight: 900; margin: 4px 0 2px; }
        .row { display: flex; justify-content: space-between; gap: 4px; margin: 2px 0; align-items: flex-start; }
        .row span { min-width: 0; }
        .row strong { text-align: right; font-weight: 900; }
        .total { font-size: ${totalSize}px; font-weight: 900; margin-top: 4px; }
        .area-total { border-top: 1px solid #000; padding-top: 2px; font-weight: 900; }
        .store-total { border: 2px solid #000; padding: 4px 2px; margin-top: 6px; }
        .item { display: flex; justify-content: space-between; gap: 4px; margin: 6px 0; break-inside: avoid; page-break-inside: avoid; }
        .item > div { flex: 1; min-width: 0; }
        .item > span { white-space: nowrap; font-weight: 900; }
        .item strong { font-weight: 900; }
        .mod, .note { display: block; margin-left: 6px; font-size: ${Math.max(9, fontSize - 1)}px; font-weight: 700; }
        .mod::before { content: "- "; }
        .note { font-weight: 900; }
        .kitem { margin: 7px 0; break-inside: avoid; page-break-inside: avoid; }
        .kitem strong { display: block; font-size: ${Math.max(12, titleSize)}px; font-weight: 900; }
        @media print {
          html, body { width: ${bodyWidth}mm; }
          button { display: none; }
        }
      
.category-tile,.category-card,.product-tile,.product-card{background:var(--pos-tile-color,#edf2f7) !important;min-height:var(--pos-tile-height,120px) !important;transition:opacity .15s,box-shadow .15s}
.product-tile.stock-empty,.product-card.stock-empty{opacity:.5;filter:saturate(.25)}
.product-tile:disabled,.product-card:disabled{cursor:not-allowed;opacity:.5}
.stock-label{display:block;font-weight:700;font-size:12px;color:#6e4d27}
.option-row-edit{display:flex!important;flex-wrap:wrap;gap:8px}.option-row-edit input:not([type=checkbox]),.option-row-edit select{flex:1;min-width:110px}
button[aria-busy=true]{cursor:wait;opacity:.6}
.category-grid,.product-grid{grid-template-columns:repeat(auto-fill,minmax(var(--pos-card-width,11.5rem),1fr))}

</style>`;
    },
    maskEmail(email) {
      const value = String(email || '').trim();
      const [name, domain] = value.split('@');
      if (!name || !domain) return '';
      const visibleStart = name.slice(0, 2);
      const visibleEnd = name.length > 3 ? name.slice(-1) : '';
      const maskedName = visibleStart + '*'.repeat(Math.max(4, name.length - visibleStart.length - visibleEnd.length)) + visibleEnd;
      return `${maskedName}@*****.***`;
    },
    escapeHtml(value) {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    },
    normalizedModifiers(item) {
      if (Array.isArray(item.modifiers)) return item.modifiers;
      try { return JSON.parse(item.modifiers || '[]'); } catch (_error) { return []; }
    },
    productSnapshot(item) {
      if (item?.product_snapshot && typeof item.product_snapshot === 'object') return item.product_snapshot;
      try { return JSON.parse(item?.product_snapshot || '{}'); } catch (_error) { return {}; }
    },
    timeMinutesForItem(item) {
      return Math.max(0, Number(this.productSnapshot(item).time_minutes || 0));
    },
    kitchenStateLabel(item) {
      const snapshot = this.productSnapshot(item);
      if (Number(snapshot.time_minutes || 0) > 0 && snapshot.send_to_kitchen === false) return 'Controlado por temporizador';
      if (snapshot.send_to_kitchen === false) return 'No requiere comanda';
      return item.kitchen_status === 'sent' ? 'Enviado a comanda' : 'Pendiente de comanda';
    },
    timerStatusLabel(status) {
      return { pending: 'Sin iniciar', running: 'En curso', paused: 'Pausado', finished: 'Terminado' }[status] || 'Tiempo';
    },
    timerRemainingSeconds(timer) {
      if (!timer) return 0;
      const now = this.timerDisplayTick;
      const baseline = Math.max(0, Number(timer.remaining_seconds || 0));
      if (timer.status !== 'running') return baseline;
      const elapsed = Math.floor((now - Number(timer._received_at || now)) / 1000);
      return Math.max(0, baseline - elapsed);
    },
    formatRemainingTime(timer) {
      const seconds = this.timerRemainingSeconds(timer);
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const remainder = seconds % 60;
      return [hours, minutes, remainder].map((value) => String(value).padStart(2, '0')).join(':');
    },
    async controlActiveTimer(action) {
      if (!this.activeTicket) return;
      try {
        const { data } = await api.post(`/api/pos/tickets/${this.activeTicket.id}/time/${action}`);
        this.activeTimer = data.timer ? { ...data.timer, _received_at: Date.now() } : null;
        await Promise.all([this.loadTickets(), this.loadTimeAlerts()]);
        this.showSuccess(action === 'pause' ? 'Tiempo pausado.' : 'Tiempo iniciado.');
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo actualizar el tiempo.');
      }
    },
    async acknowledgeTimeAlert({ alert, action }) {
      if (!alert?.id) return;
      try {
        const { data } = await api.post(`/api/pos/time-alerts/${alert.id}/acknowledge`, { action });
        this.timeAlerts = this.timeAlerts.filter((item) => Number(item.id) !== Number(alert.id));
        if (data.timer && this.activeTicket && Number(this.activeTicket.id) === Number(data.ticket_id)) {
          this.activeTimer = { ...data.timer, _received_at: Date.now() };
        }
        await this.loadTickets();
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo atender el aviso de tiempo.');
      }
    },
    async openTimeAlertTicket(alert) {
      if (!alert?.ticket_id) return;
      try {
        await this.loadTicket(alert.ticket_id);
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo abrir la cuenta del aviso.');
      }
    },
    ticketLabel(ticket) {
      if (!ticket) return '';
      if (ticket.order_label) return ticket.order_label;
      if (ticket.table_name) return `Mesa ${ticket.table_name}`;
      if (ticket.customer_name) return ticket.customer_name;
      return ticket.service_type === 'takeaway' ? 'Mostrador / Para llevar' : ticket.ticket_number;
    },
    clearTicket() {
      this.activeTicket = null;
      this.items = [];
      this.payments = [];
      this.activeTimer = null;
    },
    pushPosNotice(notice) {
      if (!notice) return;
      const exists = this.commandNotifications.some((item) => item.dedupe_key === notice.dedupe_key);
      if (exists) return;
      this.commandNotifications.unshift(notice);
      this.commandNotifications = this.commandNotifications.slice(0, 7);
    },
    handleIncomingCommand(payload = {}) {
      const notice = this.normalizeCommandNotification(payload);
      this.pushPosNotice(notice);
      this.loadTickets();
      if (this.showSales) this.loadSales();
    },
    handleAccountPrintRequest(payload = {}) {
      const notice = this.normalizeAccountPrintNotice(payload);
      this.pushPosNotice(notice);
      this.loadTickets();
    },
    handleComanderoTicketUpdate(payload = {}) {
      const notice = this.normalizeTicketUpdateNotice(payload);
      this.pushPosNotice(notice);
      this.handleTicketUpdated(payload);
    },
    broadcastKitchenCancellation(commands, ticket) {
      if (!commands?.length) return;
      this.emitModuleEvent('pos.command.created', {
        cancellation: true, ticket: ticket || {},
        ticket_id: commands[0]?.ticket_id,
        items: commands.flatMap(command => command.items || []),
        kitchen_tickets: commands,
        created_at: new Date().toISOString(),
        origin_channel: 'pos',
      });
    },
    normalizeCommandNotification(payload = {}) {
      const ticket = payload.ticket || {};
      const commands = Array.isArray(payload.kitchen_tickets) ? payload.kitchen_tickets : [];
      const items = commands.flatMap((command) => command.items || []);
      const fallbackItems = Array.isArray(payload.items) ? payload.items : [];
      const finalItems = items.length ? items : fallbackItems;
      const ticketId = ticket.id || payload.ticket_id || commands[0]?.ticket_id;
      if (!ticketId && !finalItems.length) return null;
      const title = this.ticketLabel(ticket) || commands[0]?.order_label || ticket.ticket_number || 'Nueva orden';
      return {
        kind: 'command',
        subtitle: payload.cancellation ? 'CANCELACIÓN de productos enviados a preparación' : 'Nueva comanda desde comandero',
        local_id: `${ticketId || 'cmd'}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        dedupe_key: `command:${ticketId || 'ticket'}:${commands.map((command) => command.id).join(',') || finalItems.length}:${payload.created_at || ''}`,
        ticket_id: ticketId,
        title,
        meta: `${finalItems.reduce((sum, item) => sum + Number(item.quantity || 1), 0)} producto(s)${payload.cancellation ? ' cancelado(s)' : ''}`,
        created_at: payload.created_at || commands[0]?.created_at || new Date().toISOString(),
        kitchen_tickets: commands,
        ticket,
        preview_items: finalItems.slice(0, 4),
        extra_items: Math.max(0, finalItems.length - 4),
        items_count: finalItems.reduce((sum, item) => sum + Number(item.quantity || 1), 0),
      };
    },
    normalizeAccountPrintNotice(payload = {}) {
      const ticket = payload.ticket || {};
      const ticketId = ticket.id || payload.ticket_id;
      if (!ticketId) return null;
      return {
        kind: 'account_print',
        subtitle: `Solicitan imprimir cuenta${payload.requested_by ? ` · ${payload.requested_by}` : ''}`,
        local_id: `account-print-${ticketId}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        dedupe_key: `account-print:${ticketId}:${payload.created_at || ''}`,
        ticket_id: ticketId,
        title: this.ticketLabel(ticket) || ticket.ticket_number || 'Cuenta abierta',
        meta: `${this.money(ticket.total || 0)} · ${Number(ticket.items_count || 0)} producto(s)`,
        created_at: payload.created_at || new Date().toISOString(),
        ticket,
        preview_items: [],
        extra_items: 0,
        items_count: Number(ticket.items_count || 0),
      };
    },
    normalizeTicketUpdateNotice(payload = {}) {
      const ticket = payload.ticket || {};
      const items = Array.isArray(payload.items) ? payload.items : [];
      const ticketId = ticket.id || payload.ticket_id;
      if (!ticketId && !items.length) return null;
      return {
        kind: 'ticket_update',
        subtitle: `Cuenta actualizada desde comandero${payload.updated_by ? ` · ${payload.updated_by}` : ''}`,
        local_id: `ticket-update-${ticketId || 'ticket'}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        dedupe_key: `ticket-update:${ticketId || 'ticket'}:${items.map((item) => item.id).join(',') || payload.created_at || ''}`,
        ticket_id: ticketId,
        title: this.ticketLabel(ticket) || ticket.ticket_number || 'Cuenta actualizada',
        meta: `${this.money(ticket.total || 0)} · ${items.length || 1} nuevo(s)`,
        created_at: payload.created_at || new Date().toISOString(),
        ticket,
        preview_items: items.slice(0, 4),
        extra_items: Math.max(0, items.length - 4),
        items_count: items.reduce((sum, item) => sum + Number(item.quantity || 1), 0),
      };
    },
    dismissCommandNotification(localId) {
      this.commandNotifications = this.commandNotifications.filter((notice) => notice.local_id !== localId);
    },
    async openCommandNotificationTicket(notice) {
      if (!notice?.ticket_id) return;
      await this.loadTicket(notice.ticket_id);
      this.dismissCommandNotification(notice.local_id);
    },
    async printCommandNotification(notice) {
      for (const command of (notice.kitchen_tickets || [])) {
        await this.printCommand(command);
      }
      this.dismissCommandNotification(notice.local_id);
    },
    async printAccountNotification(notice) {
      if (!notice?.ticket_id) return;
      try {
        const { data } = await api.get(`/api/pos/tickets/${notice.ticket_id}`);
        this.printReceipt(data.ticket, data.items || [], data.payments || [], false);
        this.dismissCommandNotification(notice.local_id);
      } catch (error) {
        this.showError(error.response?.data?.error || 'No se pudo imprimir la cuenta solicitada.');
      }
    },
    async openUpdateAndSendCommand(notice) {
      if (!notice?.ticket_id) return;
      await this.loadTicket(notice.ticket_id);
      this.dismissCommandNotification(notice.local_id);
      await this.sendCommand();
    },
    emitModuleEvent(event, data) {
      if (data?.ticket?._pending || Number(data?.ticket?.id || data?.ticket_id || 0) < 0) return;
      const socket = getOrConnectSocket(this.$store.state.context, this.$store.state.user) || getSocket();
      const context = this.$store.state.context;
      const room = context?.branch?.token ? `branch_${context.branch.token}` : `company_${context?.company?.token}`;
      if (socket && room) socket.emit('moduleEvent', { room, event, data });
    },
    truthy(value) {
      return [true, 1, '1', 't', 'true'].includes(value);
    },
    money(value) {
      return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(value || 0));
    },
    formatPoints(value) {
      const number = Number(value || 0);
      return Number.isInteger(number) ? String(number) : number.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
    },
    shortTime(value) {
      return parsePosDate(value || Date.now()).toLocaleTimeString('es-MX', { timeZone: POS_TIME_ZONE, hour: '2-digit', minute: '2-digit' });
    },
    shortDateTime(value) {
      return parsePosDate(value || Date.now()).toLocaleString('es-MX', { timeZone: POS_TIME_ZONE, dateStyle: 'short', timeStyle: 'short' });
    },
    shortDate(value) {
      const date = /^\d{4}-\d{2}-\d{2}$/.test(String(value || '')) ? new Date(`${value}T12:00:00`) : parsePosDate(value);
      return date.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
    },
    formatQuantity(value) {
      const number = Number(value || 0);
      return Number.isInteger(number) ? String(number) : number.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
    },
    localDateKey,
    showSuccess(text) { this.message = text; this.messageType = 'success'; setTimeout(() => { this.message = ''; }, 4000); },
    showError(text) { this.message = text; this.messageType = 'error'; setTimeout(() => { this.message = ''; }, 5200); },
  },
};
</script>

<style lang="scss" scoped>
.register-picker-list{display:flex;flex-direction:column;gap:.45rem;max-height:18rem;overflow:auto;margin:1rem 0}
.register-option{display:flex;justify-content:space-between;align-items:center;gap:1rem;text-align:left;width:100%}
.register-option small{font-weight:400}
.register-management{display:flex;flex-wrap:wrap;align-items:end;gap:.5rem;margin:1rem 0}
.register-management input{min-height:2.5rem;min-width:10rem;padding:.5rem;border:1px solid #cbd5e1;border-radius:8px}
.cash-form select{width:100%;min-height:2.5rem}
.pos-screen {
  --pos-header-height: 4.75rem;
  min-height: 100vh;
  background: #f3f5f8;
  color: var(--gt-text);
  overflow: hidden;
}
.pos-topbar {
  height: 4.75rem;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  gap: .6rem;
  padding: .7rem 1rem;
  background: #fff;
  border-bottom: 1px solid var(--gt-border);
}
.pos-top-actions { justify-self: end; min-width: 0; }
.pos-quick-actions { display: flex; align-items: center; justify-self: center; gap: .45rem; min-width: 0; }
.pos-quick-chip {
  display: inline-flex; justify-content: center; align-items: center; gap: .35rem;
  min-height: 2.65rem; border: 1px solid rgba(29,53,87,.15); border-radius: 999px;
  padding: .55rem .85rem; background: #eef3fb; color: var(--gt-primary);
  white-space: nowrap; font-weight: 900; cursor: pointer;
}
.pos-quick-chip:disabled { opacity: .5; cursor: not-allowed; }
.pos-quick-chip.clock-only { width: 2.65rem; padding: .25rem; background: #fff4e8; font-size: 1.25rem; }
.pos-quick-chip.new-account-chip { background: var(--gt-primary); color: #fff; }
.quick-count { display: inline-grid; place-items: center; min-width: 1.35rem; height: 1.35rem; padding: 0 .2rem; border-radius: 999px; background: #dce5f3; font-size: .78rem; }
.pos-quick-chip:focus-visible, .ticket-delete-button:focus-visible, .payment-reference-toggle:focus-visible, .empty-account-action:focus-visible { outline: 3px solid var(--gt-accent); outline-offset: 2px; }
.pos-brand, .pos-top-actions, .catalog-title, .ticket-head, .modal-head, .account-strip, .ticket-actions, .ticket-context { display: flex; align-items: center; gap: .7rem; }
.pos-brand small, .catalog-title small, .ticket-head small, .modal-head small { display: block; color: var(--gt-muted); margin-top: .15rem; }
.round-icon {
  width: 2.55rem;
  height: 2.55rem;
  display: grid;
  place-items: center;
  border: 0;
  border-radius: 999px;
  background: var(--gt-primary-soft);
  color: var(--gt-primary);
  text-decoration: none;
  font-weight: 900;
  cursor: pointer;
}
.pos-btn {
  border: 0;
  border-radius: 14px;
  padding: .82rem 1rem;
  font-weight: 900;
  cursor: pointer;
}
.pos-btn.primary { background: var(--gt-primary); color: #fff; }
.pos-btn.ghost { background: #eef2f7; color: var(--gt-primary); }
.pos-btn.full { width: 100%; }
.pos-btn:disabled { opacity: .45; cursor: not-allowed; }
.status-pill { border-radius: 999px; padding: .45rem .75rem; font-size: .82rem; font-weight: 900; background: #eef2f7; }
.status-pill.ok { background: #e8fbf5; color: #147161; }
.status-pill.warn { background: #fff5df; color: #94600e; }
.toast { position: fixed; top: 5.2rem; left: 50%; transform: translateX(-50%); z-index: 1500; padding: .8rem 1rem; border-radius: 999px; background: #e8fbf5; color: #147161; font-weight: 900; box-shadow: 0 14px 40px rgba(0,0,0,.12); }
.toast.error { background: #fff0ec; color: var(--gt-danger); }
.pos-main { height: calc(100vh - 4.75rem); display: grid; grid-template-columns: minmax(0, 1fr) 27rem; gap: .8rem; padding: .8rem; }
.pos-main.blocked { filter: blur(1px); pointer-events: none; }
.catalog-zone, .order-zone { min-height: 0; }
.catalog-zone { background: #fff; border: 1px solid var(--gt-border); border-radius: 24px; padding: 1rem; display: flex; flex-direction: column; min-width: 0; max-height: calc(100vh - var(--pos-header-height) - 1.6rem); max-height: calc(100dvh - var(--pos-header-height) - 1.6rem); }
.catalog-toolbar, .product-quickbar { flex: 0 0 auto; }
.catalog-toolbar { display: flex; justify-content: space-between; gap: 1rem; align-items: center; padding-bottom: .85rem; }
.catalog-toolbar h1 { margin: 0; font-size: clamp(1.5rem, 2vw, 2rem); }
.search-box { position: relative; width: min(24rem, 42%); }
.search-box input, .ticket-edit-row input, .ticket-edit-row select, .modal input, .modal select, .modal textarea, .inline-form input, .customer-search input {
  width: 100%; border: 1px solid var(--gt-border); border-radius: 14px; padding: .85rem .95rem; outline: none; background: #fff;
}
.clear-search { position: absolute; right: .45rem; top: .45rem; border: 0; border-radius: 999px; width: 2rem; height: 2rem; cursor: pointer; }
.category-grid, .product-grid { flex: 1 1 auto; min-height: 0; overflow-y: auto; overscroll-behavior-y: contain; touch-action: pan-y pinch-zoom; -webkit-overflow-scrolling: touch; padding: .2rem .15rem 1rem; display: grid; gap: .85rem; }
.category-grid { grid-template-columns: repeat(auto-fill, minmax(11.5rem, 1fr)); }
.product-grid { grid-template-columns: repeat(auto-fill, minmax(10.5rem, 1fr)); align-content:start; align-items:stretch; overscroll-behavior-y:contain; padding-bottom:4rem; }
.category-tile, .product-tile {
  border: 1px solid var(--gt-border); background: #fff; border-radius: 22px; padding: 1rem; text-align: left; cursor: pointer; min-height: 8.6rem; box-shadow: 0 10px 24px rgba(29,53,87,.06);
}
.category-tile:hover, .product-tile:hover, .account-card:hover, .table-card:hover, .customer-card:hover { border-color: rgba(29,53,87,.35); transform: translateY(-1px); }
.category-tile span, .product-tile strong { display: block; font-weight: 900; }
.category-tile strong { display: block; font-size: 2rem; margin-top: 1.2rem; }
.category-tile small, .product-tile small { color: var(--gt-muted); }
.product-tile { display: flex; flex-direction: column; justify-content: space-between; }
.product-tile span { color: var(--gt-primary); font-weight: 900; }
.extra-product-tile {
  border-style: dashed;
}

.extra-product-tile strong::before {
  content: '+ ';
}

.product-tile em { font-style: normal; color: var(--gt-accent); font-size: .78rem; font-weight: 900; }
.order-zone { display: grid; grid-template-rows: minmax(0, 1fr); gap: 0; min-width: 0; }
.account-strip { flex-wrap: wrap; }
.open-accounts, .ticket-card { background: #fff; border: 1px solid var(--gt-border); border-radius: 22px; padding: .9rem; min-height: 0; }
.mini-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: .55rem; }
.account-list { display: grid; gap: .45rem; max-height: 9rem; overflow: auto; }
.account-card { border: 1px solid var(--gt-border); background: #fff; border-radius: 14px; padding: .7rem; text-align: left; cursor: pointer; }
.account-card.active { background: var(--gt-primary-soft); border-color: rgba(29,53,87,.3); }
.account-card span, .account-card small { display: block; }
.account-card small { color: var(--gt-muted); margin-top: .15rem; }
.ticket-card { display: flex; flex-direction: column; gap: .75rem; overflow: hidden; }
.ticket-head { justify-content: space-between; align-items: flex-start; gap: .45rem; }
.ticket-head > div:first-child { min-width: 0; overflow-wrap: anywhere; }
.ticket-head-actions { display: flex; align-items: center; gap: .4rem; flex-shrink: 0; }
.ticket-head-actions .status-pill { font-size: .73rem; text-align: center; padding: .35rem .55rem; }
.ticket-delete-button { width: 2.3rem; height: 2.3rem; flex-shrink: 0; background: #fff0ec; color: var(--gt-danger); font-size: 1.15rem; }
.ticket-delete-button:disabled { opacity: .35; cursor: not-allowed; }
.empty-account-action { width: 100%; text-align: center; cursor: pointer; border: 1px dashed rgba(29,53,87,.36); background: #f8fafc; color: var(--gt-primary); }
.empty-account-action small { display: block; margin-top: .4rem; color: var(--gt-muted); font-weight: 700; }
.ticket-edit-row { display: grid; grid-template-columns: 7.5rem 1fr; gap: .45rem; }
.ticket-context { flex-wrap: wrap; }
.context-chip { border: 1px solid var(--gt-border); background: #f8fafc; border-radius: 999px; padding: .5rem .75rem; cursor: pointer; font-weight: 800; }
.ticket-items { display: grid; gap: .5rem; overflow: auto; padding-right: .15rem; min-height: 0; }
.ticket-item { display: flex; justify-content: space-between; gap: .65rem; padding: .75rem; border: 1px solid var(--gt-border); border-radius: 16px; background: #fff; }
.time-item-state { color: #8a4b08; font-weight: 900; }
.item-main small { display: block; color: var(--gt-muted); margin-top: .16rem; }
.item-main .kitchen-state { color: var(--gt-accent); font-weight: 800; }
.item-actions { text-align: right; display: grid; gap: .35rem; justify-items: end; }
.item-actions button { border: 0; background: #fff0ec; color: var(--gt-danger); border-radius: 999px; width: 1.8rem; height: 1.8rem; cursor: pointer; font-weight: 900; }
.totals-box { border-top: 1px solid var(--gt-border); padding-top: .65rem; display: grid; gap: .35rem; }
.totals-box div { display: flex; justify-content: space-between; }
.totals-box span { color: var(--gt-muted); font-weight: 800; }
.totals-box .total { font-size: 1.35rem; }
.ticket-actions { margin-top: auto; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: .65rem; }
.ticket-actions .pos-btn { width: 100%; min-height: 3.2rem; padding: .72rem .35rem; font-size: clamp(.78rem, 1vw, .95rem); }
.ticket-items .item-main { min-width: 0; overflow-wrap: anywhere; }
.catalog-helper-card {
  border: 1px solid var(--gt-border);
  border-radius: 20px;
  padding: 1rem;
  background: #f8fafc;
  display: grid;
  gap: .6rem;
  color: var(--gt-text);
}
.catalog-helper-card p { margin: 0; color: var(--gt-muted); line-height: 1.35; }
.product-helper { margin-bottom: .8rem; }
.nested-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2300;
  display: grid;
  place-items: center;
  padding: 1rem;
  background: rgba(15,23,42,.48);
}
.nested-editor-modal { width: min(48rem, 100%); }
.product-editor-modal { width: min(64rem, 100%); }

.empty-box, .empty-mini { border: 1px dashed var(--gt-border); border-radius: 18px; padding: 1rem; text-align: center; color: var(--gt-muted); }
.empty-mini.large { padding: 2rem 1rem; }
.empty-box.compact { margin-top: auto; }
.cash-overlay { position: fixed; inset: 4.75rem 0 0; z-index: 1200; display: grid; place-items: center; background: rgba(243,245,248,.82); backdrop-filter: blur(10px); }
.cash-card { width: min(34rem, calc(100% - 2rem)); background: #fff; border: 1px solid var(--gt-border); border-radius: 26px; padding: 2rem; box-shadow: var(--gt-shadow); }
.cash-card h1 { margin: .75rem 0 .4rem; }
.cash-form { display: flex; gap: .7rem; align-items: end; margin-top: 1rem; }
.cash-form label { flex: 1; display: grid; gap: .35rem; font-weight: 800; }
.cash-form input { width: 100%; border: 1px solid var(--gt-border); border-radius: 16px; padding: .9rem; }
.modal-backdrop { position: fixed; inset: 0; z-index: 2000; background: rgba(15,23,42,.42); display: grid; place-items: center; padding: 1rem; }
.modal { width: min(42rem, 100%); max-height: calc(100vh - 2rem); overflow: auto; background: #fff; border-radius: 26px; padding: 1rem; box-shadow: 0 30px 90px rgba(0,0,0,.22); }
.small-modal { width: min(36rem, 100%); display: grid; gap: .85rem; }
.new-ticket-modal { width: min(54rem, 100%); display: grid; gap: .85rem; }
.service-picker, .cut-type-row { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: .65rem; }
.cut-type-row { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.service-card { border: 1px solid var(--gt-border); background: #fff; border-radius: 20px; padding: 1rem; text-align: left; cursor: pointer; min-height: 6rem; box-shadow: 0 10px 24px rgba(29,53,87,.05); }
.service-card strong, .service-card small { display: block; }
.service-card small { color: var(--gt-muted); margin-top: .25rem; }
.service-card.active { background: var(--gt-primary-soft); border-color: rgba(29,53,87,.38); }
.service-card.danger-soft.active { background: #fff0ec; border-color: rgba(221, 86, 62, .35); }
.service-flow { display: grid; gap: .75rem; }
.service-flow label, .cash-cut-modal label { display: grid; gap: .35rem; font-weight: 800; }
.touch-grid .table-card, .touch-list .customer-card { min-height: 4.8rem; }
.cut-summary { border: 1px solid var(--gt-border); border-radius: 18px; background: #f8fafc; padding: .85rem; display: grid; gap: .35rem; }
.cut-summary div { display: flex; justify-content: space-between; }
.cut-summary .total { font-size: 1.15rem; }
.cut-summary small { color: var(--gt-muted); font-weight: 800; }


.cash-counter {
  border: 1px solid var(--gt-border);
  border-radius: 20px;
  padding: .85rem;
  background: #f8fafc;
  display: grid;
  gap: .75rem;
}
.counter-head {
  display: flex;
  justify-content: space-between;
  gap: .8rem;
  align-items: center;
}
.counter-head small { display: block; color: var(--gt-muted); margin-top: .2rem; }
.counter-head > strong { font-size: 1.35rem; color: var(--gt-primary); }
.denomination-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: .55rem;
}
.denomination-grid label {
  background: #fff;
  border: 1px solid var(--gt-border);
  border-radius: 16px;
  padding: .65rem;
}
.denomination-grid input { width: 100%; }
.denomination-grid small { color: var(--gt-muted); font-weight: 900; }
.breakdown-list {
  display: grid !important;
  gap: .25rem;
  border-top: 1px solid var(--gt-border);
  margin-top: .45rem;
  padding-top: .45rem;
}
.breakdown-list > div { display: flex; justify-content: space-between; }
.area-breakdown-list span small { display: inline; }
.options-admin-modal { width: min(74rem, 100%); }
.catalog-tabs {
  display: flex;
  gap: .55rem;
  flex-wrap: wrap;
  margin: -.25rem 0 .85rem;
}
.catalog-admin-layout {
  display: grid;
  grid-template-columns: 20rem minmax(0, 1fr);
  gap: .9rem;
}
.catalog-side-panel, .product-admin-panel, .mini-editor-card, .product-admin-list, .product-category-section {
  display: grid;
  gap: .65rem;
}
.catalog-side-panel {
  align-content: start;
  max-height: 72vh;
  overflow: auto;
  padding-right: .25rem;
}
.category-admin-list {
  display: grid;
  gap: .5rem;
  max-height: 17rem;
  overflow: auto;
  padding-right: .15rem;
}
.mini-editor-card {
  border: 1px solid var(--gt-border);
  border-radius: 18px;
  padding: .8rem;
  background: #f8fafc;
}
.mini-editor-card label, .product-editor-grid label {
  display: grid;
  gap: .35rem;
  font-weight: 800;
}
.mini-editor-card textarea {
  min-height: 4.5rem;
  resize: vertical;
}
.product-admin-panel {
  min-width: 0;
  max-height: 72vh;
  overflow: auto;
  padding-right: .25rem;
}
.product-editor-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: .65rem;
  border: 1px solid var(--gt-border);
  border-radius: 18px;
  background: #fff;
  padding: .8rem;
}
.product-editor-grid .full-field {
  grid-column: 1 / -1;
}
.product-check-grid.compact {
  max-height: 11rem;
}
.product-admin-list {
  margin-top: .15rem;
}
.product-category-section {
  border: 1px solid var(--gt-border);
  border-radius: 18px;
  background: #fff;
  padding: .75rem;
}
.product-category-section header {
  display: flex;
  justify-content: space-between;
  gap: .7rem;
  align-items: center;
  color: var(--gt-text);
}
.product-category-section header small {
  color: var(--gt-muted);
  font-weight: 800;
}
.product-admin-row {
  border: 1px solid var(--gt-border);
  border-radius: 14px;
  background: #f8fafc;
  padding: .7rem;
  display: flex;
  justify-content: space-between;
  gap: .7rem;
  text-align: left;
  cursor: pointer;
}
.product-admin-row small {
  display: block;
  color: var(--gt-muted);
  margin-top: .15rem;
  font-weight: 800;
}
.product-admin-row em {
  font-style: normal;
  font-size: .75rem;
  font-weight: 900;
  color: var(--gt-primary);
  white-space: nowrap;
}
.options-admin-layout {
  display: grid;
  grid-template-columns: 18rem minmax(0, 1fr);
  gap: .9rem;
}
.option-group-panel, .option-editor, .option-rows, .product-linker {
  display: grid;
  gap: .65rem;
}
.option-group-panel {
  align-content: start;
  max-height: 70vh;
  overflow: auto;
  padding-right: .25rem;
}
.option-group-card {
  border: 1px solid var(--gt-border);
  border-radius: 18px;
  background: #fff;
  text-align: left;
  padding: .85rem;
  cursor: pointer;
}
.option-group-card.active { background: var(--gt-primary-soft); border-color: rgba(29,53,87,.32); }
.option-group-card strong, .option-group-card small { display: block; }
.option-group-card small { color: var(--gt-muted); margin-top: .2rem; }
.editor-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: .65rem;
}
.editor-grid label, .option-row-edit {
  display: grid;
  gap: .35rem;
  font-weight: 800;
}
.section-title-row {
  display: flex;
  justify-content: space-between;
  gap: .7rem;
  align-items: center;
}
.section-title-row small { color: var(--gt-muted); font-weight: 800; }
.option-row-edit {
  grid-template-columns: 1.25fr .6fr 1.3fr auto auto;
  align-items: center;
  padding: .55rem;
  border: 1px solid var(--gt-border);
  border-radius: 16px;
  background: #f8fafc;
}
.tiny-check { display: flex !important; align-items: center; gap: .3rem; white-space: nowrap; font-size: .82rem; }
.round-icon.danger { background: #fff0ec; color: var(--gt-danger); }
.home-icon { font-size: 1rem; text-decoration: none; }
.product-check-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(12rem, 1fr));
  gap: .45rem;
  max-height: 18rem;
  overflow: auto;
  border: 1px solid var(--gt-border);
  border-radius: 18px;
  padding: .65rem;
  background: #f8fafc;
}
.product-check {
  display: flex;
  gap: .45rem;
  align-items: flex-start;
  background: #fff;
  border-radius: 12px;
  padding: .55rem;
  font-weight: 800;
}
.product-check small { display: block; color: var(--gt-muted); font-weight: 700; margin-top: .15rem; }

.side-modal { width: min(46rem, 100%); display: grid; gap: .85rem; }
.command-modal { width: min(52rem, 100%); }
.modal-head { justify-content: space-between; margin-bottom: .85rem; }
.modal h2 { margin: .15rem 0 0; }
.qty-row { display: flex; justify-content: space-between; align-items: center; background: #f8fafc; padding: .8rem; border-radius: 16px; margin-bottom: .8rem; }
.product-time-note, .product-pricing-note { display: grid; gap: .2rem; margin-bottom: .8rem; padding: .8rem; border: 1px solid rgba(212,132,68,.26); border-radius: 16px; background: #fff8ef; color: #7c3f08; }
.product-pricing-note { border-color: rgba(29,53,87,.2); background: var(--gt-primary-soft); color: var(--gt-primary); }
.qty-row div { display: flex; align-items: center; gap: .8rem; }
.qty-row button { border: 0; width: 2rem; height: 2rem; border-radius: 999px; cursor: pointer; font-weight: 900; }
.option-group { border-top: 1px solid var(--gt-border); padding-top: .8rem; margin-top: .8rem; }
.option-title { display: flex; justify-content: space-between; align-items: center; margin-bottom: .55rem; }
.option-title small { color: var(--gt-muted); }
.option-list { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: .5rem; }
.option-card { border: 1px solid var(--gt-border); border-radius: 16px; padding: .72rem; display: grid; grid-template-columns: auto 1fr auto; align-items: center; gap: .5rem; cursor: pointer; }
.option-card.checked { background: var(--gt-primary-soft); border-color: rgba(29,53,87,.3); }
.option-card.unavailable { cursor: not-allowed; opacity: .58; background: #fff1f2; color: #9f1239; text-decoration: line-through; }
.notes-box { display: grid; gap: .4rem; margin-top: .8rem; font-weight: 800; }
.notes-box textarea { min-height: 5rem; resize: vertical; }
.modal-actions { display: flex; justify-content: space-between; align-items: center; gap: 1rem; margin-top: 1rem; padding-top: 1rem; border-top: 1px solid var(--gt-border); }
.modal-actions strong { font-size: 1.35rem; }
.table-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(8rem, 1fr)); gap: .6rem; }
.table-card, .customer-card { border: 1px solid var(--gt-border); background: #fff; border-radius: 18px; padding: .85rem; text-align: left; cursor: pointer; }
.table-card.occupied { background: #fff5df; }
.inline-form { display: grid; grid-template-columns: 1fr 5rem auto; gap: .5rem; }
.customer-search { display: grid; grid-template-columns: 1fr auto; gap: .5rem; }
.customer-list { display: grid; gap: .5rem; max-height: 16rem; overflow: auto; }
.customer-card { display: grid; grid-template-columns: 1fr auto; gap: .2rem .7rem; }
.customer-card small { color: var(--gt-muted); }
.customer-card span { grid-row: 1 / span 2; grid-column: 2; align-self: center; font-weight: 900; color: var(--gt-success); }
.new-customer { display: grid; gap: .5rem; border-top: 1px solid var(--gt-border); padding-top: .8rem; }
.command-list { display: grid; grid-template-columns: repeat(auto-fit, minmax(16rem, 1fr)); gap: .8rem; }
.command-ticket { border: 1px solid var(--gt-border); border-radius: 18px; padding: .85rem; }
.command-ticket header { border-bottom: 1px dashed var(--gt-border); padding-bottom: .55rem; margin-bottom: .55rem; }
.command-ticket header small, .command-item small { display: block; color: var(--gt-muted); margin-top: .2rem; }
.command-item { padding: .5rem 0; }


.payment-method-panel {
  display: grid;
  gap: .45rem;
}
.field-label {
  font-weight: 900;
  color: var(--gt-text);
}
.payment-method-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(8.5rem, 1fr));
  gap: .55rem;
}
.payment-method-btn {
  border: 1px solid var(--gt-border);
  background: #fff;
  border-radius: 16px;
  padding: .75rem;
  text-align: left;
  cursor: pointer;
  display: grid;
  gap: .2rem;
  min-height: 4.2rem;
}
.payment-method-btn.active {
  background: var(--gt-primary-soft);
  border-color: rgba(29,53,87,.38);
  box-shadow: inset 0 0 0 1px rgba(29,53,87,.2);
}
.payment-method-btn strong { font-size: .95rem; }
.payment-method-btn small { color: var(--gt-muted); font-weight: 800; }
.payment-reference-toggle { display: flex; width: 100%; justify-content: space-between; align-items: center; padding: .7rem .85rem; border: 1px dashed var(--gt-border); border-radius: 12px; background: #f8fafc; color: var(--gt-primary); font-weight: 850; cursor: pointer; }
.payment-reference-toggle span:last-child { font-size: 1.3rem; line-height: 1; }
.expense-detail-list span { line-height: 1.25; }
.expense-detail-list em { font-style: normal; color: var(--gt-muted); }

.change-box {
  border: 1px solid var(--gt-border);
  background: #e8fbf5;
  color: #147161;
  border-radius: 16px;
  padding: .9rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 900;
}
.change-box.warn {
  background: #fff5df;
  color: #94600e;
}
.switch-row {
  display: flex;
  align-items: center;
  gap: .55rem;
  font-weight: 800;
  color: var(--gt-text);
}
.switch-row input {
  width: auto;
  accent-color: var(--gt-primary);
}
.open-accounts-modal {
  width: min(58rem, 100%);
}
.accounts-summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(16rem, 1fr));
  gap: .75rem;
}
.account-summary-card {
  border: 1px solid var(--gt-border);
  border-radius: 18px;
  padding: .9rem;
  display: grid;
  gap: .75rem;
  background: #fff;
}
.account-summary-card.active {
  background: var(--gt-primary-soft);
  border-color: rgba(29,53,87,.32);
}
.account-summary-card header, .account-summary-card footer {
  display: flex;
  justify-content: space-between;
  gap: .6rem;
  align-items: center;
}
.account-summary-card header strong, .account-summary-card header small {
  display: block;
}
.account-summary-card header span {
  font-size: 1.2rem;
  font-weight: 900;
  color: var(--gt-primary);
  white-space: nowrap;
}
.account-summary-meta {
  display: flex;
  flex-wrap: wrap;
  gap: .35rem;
}
.account-summary-meta span {
  border-radius: 999px;
  background: #f8fafc;
  color: var(--gt-muted);
  padding: .3rem .55rem;
  font-size: .78rem;
  font-weight: 800;
}

.thermal-tip {
  display: block;
  margin-top: .35rem;
  color: var(--gt-muted);
  font-size: .78rem;
  line-height: 1.35;
}

.sales-modal {
  width: min(74rem, 100%);
  display: grid;
  gap: .85rem;
}
.cash-cut-modal { width: min(72rem, 100%); }
.cash-cut-error { margin: .4rem 0; padding: .75rem 1rem; border: 1px solid #f2a89e; border-radius: 12px; background: #fff0ec; color: #9b2b20; font-weight: 800; white-space: pre-wrap; }
.sales-filters {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: .7rem;
  align-items: end;
}
.sales-filters label { display: grid; gap: .35rem; font-weight: 800; }
.sales-summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: .65rem;
}
.sales-summary-grid div {
  border: 1px solid var(--gt-border);
  border-radius: 16px;
  padding: .75rem;
  background: rgba(255,255,255,.74);
  display: grid;
  gap: .2rem;
}
.sales-summary-grid span { color: var(--gt-muted); font-size: .82rem; font-weight: 800; }
.sales-summary-grid strong { font-size: 1.1rem; }
.sales-cut-actions { display: flex; justify-content: space-between; gap: .75rem; align-items: center; border: 1px solid var(--gt-border); border-radius: 16px; background: #f8fafc; padding: .75rem; font-weight: 900; }
.sales-area-list { border: 1px solid var(--gt-border); background: #f8fafc; border-radius: 18px; padding: .85rem; display: grid; gap: .45rem; }
.sales-area-list > div { display: grid; grid-template-columns: 1fr auto; gap: .1rem .8rem; padding: .55rem; border-radius: 12px; background: #fff; }
.sales-area-list span { font-weight: 900; }
.sales-area-list small { color: var(--gt-muted); }
.sales-area-list > div > strong { grid-column: 2; grid-row: 1 / span 2; align-self: center; }
.area-payment-matrix { border: 1px solid var(--gt-border); background: #f8fafc; border-radius: 18px; padding: .85rem; display: grid; grid-template-columns: repeat(auto-fit, minmax(13rem, 1fr)); gap: .65rem; }
.area-payment-matrix > strong { grid-column: 1 / -1; }
.area-payment-matrix article { border: 1px solid var(--gt-border); border-radius: 14px; padding: .7rem; background: #fff; display: grid; gap: .35rem; }
.area-payment-matrix article header, .area-payment-matrix article div { display: flex; justify-content: space-between; gap: .65rem; }
.area-payment-matrix article header { border-bottom: 1px dashed var(--gt-border); padding-bottom: .4rem; font-weight: 900; }
.area-payment-matrix article header span { display: grid; gap: .1rem; }
.area-payment-matrix article header small { color: var(--gt-muted); font-weight: 800; }
.area-payment-matrix article div span { color: var(--gt-muted); font-weight: 800; }
.store-sales-total { grid-column: 1 / -1; display: flex; justify-content: space-between; gap: 1rem; align-items: center; border: 2px solid var(--gt-primary); border-radius: 15px; padding: .8rem; background: var(--gt-primary-soft); font-size: 1.05rem; font-weight: 900; }
.store-sales-total strong { font-size: 1.2rem; }
.compact-matrix { margin-top: .45rem; grid-column: 1 / -1; }
.cut-summary .area-payment-matrix, .cut-summary .area-payment-matrix article { display: grid; }
.product-report-modal { width: min(72rem, 100%); display: grid; gap: .85rem; }
.product-report-filters { display: grid; grid-template-columns: minmax(16rem, 2fr) repeat(2, minmax(10rem, 1fr)) auto; gap: .65rem; align-items: end; }
.product-report-filters label { display: grid; gap: .35rem; font-weight: 800; }
.product-report-table-wrap { overflow: auto; border: 1px solid var(--gt-border); border-radius: 18px; max-height: min(55vh, 34rem); }
.product-report-table { width: 100%; border-collapse: collapse; }
.product-report-table th, .product-report-table td { padding: .75rem; text-align: right; border-bottom: 1px solid var(--gt-border); white-space: nowrap; }
.product-report-table th:first-child, .product-report-table td:first-child { text-align: left; }
.product-report-table thead { position: sticky; top: 0; background: #f8fafc; z-index: 1; }
.product-report-table tbody tr:last-child td { border-bottom: 0; }
.sales-list { display: grid; gap: .65rem; max-height: min(55vh, 34rem); overflow: auto; padding-right: .2rem; }
.sale-ticket-card { border: 1px solid var(--gt-border); border-radius: 18px; padding: .8rem; background: #fff; display: grid; gap: .55rem; }
.sale-ticket-card header, .sale-ticket-card footer { display: flex; justify-content: space-between; gap: .7rem; align-items: center; }
.sale-ticket-card header small { display: block; color: var(--gt-muted); margin-top: .2rem; }

.expense-modal {
  width: min(72rem, 100%);
}
.expense-layout {
  display: grid;
  grid-template-columns: minmax(18rem, 0.9fr) minmax(22rem, 1.1fr);
  gap: .85rem;
  align-items: start;
}
.expense-form-card, .expense-list-card {
  border: 1px solid var(--gt-border);
  border-radius: 20px;
  background: rgba(255,255,255,.74);
  padding: .85rem;
  display: grid;
  gap: .75rem;
}
.expense-summary-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); }
.expense-card footer { display: flex; justify-content: flex-end; gap: .5rem; }

.discount-modal {
  width: min(56rem, 100%);
  display: grid;
  gap: .85rem;
}
.discount-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(12rem, 1fr));
  gap: .65rem;
}
.discount-card, .active-discount-card, .discount-editor-card {
  border: 1px solid var(--gt-border);
  border-radius: 18px;
  background: rgba(255,255,255,.8);
}
.discount-card {
  padding: .95rem;
  text-align: left;
  cursor: pointer;
  display: grid;
  gap: .25rem;
}
.discount-card strong { font-size: 1rem; }
.discount-card span { font-size: 1.15rem; font-weight: 900; color: var(--gt-primary); }
.discount-card small { color: var(--gt-muted); font-weight: 800; }
.discount-card:disabled { opacity: .55; cursor: not-allowed; }
.active-discount-card {
  padding: .85rem;
  display: flex;
  justify-content: space-between;
  gap: .8rem;
  align-items: center;
  background: #e8fbf5;
}
.active-discount-card small { display: block; color: #147161; font-weight: 900; margin-top: .2rem; }
.discount-editor-card {
  padding: .85rem;
  display: grid;
  gap: .75rem;
}
.compact-switch { align-self: end; min-height: 3rem; }

.receipt-settings-modal {
  width: min(50rem, 100%);
}
.settings-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: .7rem;
}
.settings-grid label {
  display: grid;
  gap: .35rem;
  font-weight: 800;
}
.settings-grid textarea {
  min-height: 5rem;
  resize: vertical;
}
.settings-grid .full-field {
  grid-column: 1 / -1;
}
.setting-switch {
  align-self: end;
  min-height: 3rem;
}


.command-notification-stack {
  position: fixed;
  top: 5.35rem;
  right: 1rem;
  z-index: 1600;
  display: grid;
  gap: .8rem;
  width: min(26rem, calc(100vw - 2rem));
  pointer-events: none;
}
.command-notification {
  pointer-events: auto;
  overflow: hidden;
  border-radius: 26px;
  border: 1px solid rgba(255,255,255,.28);
  background:
    radial-gradient(circle at 12% 0%, rgba(212,132,68,.28), transparent 11rem),
    linear-gradient(145deg, rgba(19,31,51,.96), rgba(12,19,32,.94));
  color: #fff;
  box-shadow: 0 24px 80px rgba(10,18,32,.35);
  backdrop-filter: blur(18px);
  padding: 1rem;
  animation: commandPop .22s ease-out;
}
@keyframes commandPop {
  from { transform: translateY(-10px) scale(.98); opacity: 0; }
  to { transform: translateY(0) scale(1); opacity: 1; }
}
.command-notification header {
  display: flex;
  justify-content: space-between;
  gap: .8rem;
  align-items: flex-start;
}
.command-notification header small,
.command-notification header em {
  display: block;
  color: rgba(255,255,255,.68);
  font-size: .78rem;
  font-weight: 800;
  font-style: normal;
}
.command-notification header strong {
  display: block;
  margin: .18rem 0;
  font-size: 1.05rem;
  font-weight: 950;
}
.notice-close {
  flex: 0 0 auto;
  width: 2.4rem;
  height: 2.4rem;
  border: 0;
  border-radius: 999px;
  background: rgba(255,255,255,.14);
  color: #fff;
  font-size: 1.25rem;
  cursor: pointer;
}
.notice-items {
  display: grid;
  gap: .45rem;
  margin: .85rem 0;
}
.notice-item {
  border: 1px solid rgba(255,255,255,.12);
  border-radius: 16px;
  padding: .6rem .7rem;
  background: rgba(255,255,255,.10);
}
.notice-item span { display: block; font-weight: 900; }
.notice-item small { display: block; margin-top: .15rem; color: rgba(255,255,255,.64); }
.notice-more { color: #ffd7a8; font-weight: 900; font-size: .86rem; }
.notice-actions { display: flex; gap: .6rem; flex-wrap: wrap; }
.notice-actions .pos-btn { flex: 1; min-width: 9rem; justify-content: center; }

.account-time-summary {
  display: flex;
  justify-content: space-between;
  gap: .75rem;
  margin-top: .65rem;
  padding: .65rem .75rem;
  border-radius: 14px;
  background: #eef6ff;
  color: #173b67;
  font-weight: 900;
  font-variant-numeric: tabular-nums;
}
.account-time-summary.is-running { background: #e8fbf5; color: #147161; }
.account-time-summary.is-paused { background: #fff8e6; color: #8a5a00; }
.account-time-summary.is-finished { background: #fff0ec; color: var(--gt-danger); }

@media (min-width: 1181px) {
  .category-grid, .product-grid { gap: 1rem; }
  .category-grid { grid-template-columns: repeat(auto-fill, minmax(13.5rem, 1fr)); }
  .product-grid { grid-template-columns: repeat(auto-fill, minmax(12.75rem, 1fr)); }
  .category-tile, .product-tile { min-height: 10rem; padding: 1.15rem; }
  .category-tile span, .product-tile strong { font-size: 1.05rem; }
  .product-tile span { font-size: 1.12rem; }
}

@media (max-width: 1180px) {
  .pos-screen { overflow: visible; }
  .pos-topbar { position: sticky; top: 0; z-index: 150; }
  .pos-main { grid-template-columns: minmax(0, 1fr); overflow: visible; height: auto; }
  .order-zone { grid-template-rows: auto; }
  .ticket-items { max-height: 55vh; }
}
@media (min-width: 721px) and (max-width: 980px) {
  .pos-top-actions .status-pill { display: none; }
}
@media (max-width: 720px) {
  .pos-screen { --pos-header-height: 3.85rem; }
  .pos-topbar { grid-template-columns: auto minmax(0, 1fr) auto; height: 3.85rem; padding: .45rem .55rem; gap: .35rem; }
  .cash-overlay { inset: 3.85rem 0 0; }
  .pos-brand { gap: 0; }
  .pos-brand > div { display: none; }
  .pos-brand .home-icon { width: 2.3rem; height: 2.3rem; }
  .pos-quick-actions { gap: .25rem; }
  .pos-quick-chip { min-height: 2.3rem; padding: .38rem .55rem; gap: .2rem; font-size: .78rem; }
  .pos-quick-chip.clock-only { width: 2.3rem; font-size: 1.12rem; }
  .quick-count { min-width: 1.1rem; height: 1.1rem; font-size: .66rem; }
  .pos-top-actions { gap: .25rem; }
  .pos-top-actions .status-pill { display: none; }
  .pos-menu-trigger { width: 2.3rem; height: 2.3rem; }
  .catalog-toolbar, .cash-form { flex-direction: column; height: auto; align-items: stretch; }
  .pos-main { height: auto; padding: .5rem; }
  .search-box { width: 100%; }
  .ticket-card { padding: .7rem; gap: .6rem; }
  .ticket-actions { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .ticket-actions .pos-btn { min-height: 2.85rem; font-size: .78rem; }
  .option-list, .inline-form, .customer-search, .service-picker, .cut-type-row, .sales-filters, .sales-summary-grid, .denomination-grid, .options-admin-layout, .catalog-admin-layout, .product-editor-grid, .editor-grid, .option-row-edit, .expense-layout, .discount-grid, .payment-method-grid { grid-template-columns: 1fr; }
}
@media (max-width: 390px) {
  .pending-label { font-size: 0; }
  .pending-label::after { content: 'Pend.'; font-size: .75rem; }
  .pos-quick-chip { padding: .35rem .42rem; }
  .pos-brand .home-icon, .pos-menu-trigger { width: 2.1rem; height: 2.1rem; }
  .pos-quick-chip.clock-only { width: 2.1rem; }
  .ticket-head-actions .status-pill { font-size: .66rem; }
}

.loyalty-modal { max-width: 620px; }
.loyalty-hero { display: flex; gap: 14px; align-items: center; padding: 16px; border: 1px solid rgba(255,255,255,.14); border-radius: 22px; background: linear-gradient(135deg, rgba(255,214,102,.14), rgba(255,255,255,.06)); margin-bottom: 14px; }
.loyalty-hero span { width: 44px; height: 44px; display: grid; place-items: center; border-radius: 999px; background: rgba(255,214,102,.18); }
.loyalty-hero strong { display: block; color: #fff; }
.loyalty-hero small { color: rgba(255,255,255,.68); }
.loyalty-search-box { margin-bottom: 14px; }
.loyalty-result-card { border: 1px solid rgba(255,255,255,.14); border-radius: 24px; padding: 18px; background: rgba(255,255,255,.08); box-shadow: inset 0 1px 0 rgba(255,255,255,.08); }
.loyalty-result-card small { color: rgba(255,255,255,.58); text-transform: uppercase; letter-spacing: .08em; font-weight: 900; }
.loyalty-result-card h3 { margin: 6px 0 4px; color: #fff; }
.loyalty-result-card p { margin: 0 0 12px; color: rgba(255,255,255,.72); }
.points-big { font-size: clamp(34px, 7vw, 58px); color: #ffe28a; font-weight: 1000; line-height: 1; margin: 12px 0 16px; }
.redemption-items { display: grid; gap: 8px; margin: 14px 0; }
.redemption-items div { display: flex; justify-content: space-between; gap: 12px; border: 1px solid rgba(255,255,255,.1); border-radius: 14px; padding: 10px 12px; background: rgba(0,0,0,.18); }
.redemption-items span { color: #fff; font-weight: 800; display: grid; gap: 3px; }
.redemption-items span b { color: inherit; }
.redemption-items span em { font-size: 11px; font-style: normal; font-weight: 700; opacity: .78; }
.redemption-items small { color: #ffe28a; }
.loyalty-customer-results { display: grid; gap: 8px; margin-top: 10px; }
.loyalty-customer-row { width: 100%; border: 1px solid rgba(255,255,255,.1); border-radius: 16px; padding: 12px 14px; background: rgba(0,0,0,.18); color: #fff; display: grid; grid-template-columns: 1fr auto; gap: 4px 12px; text-align: left; }
.loyalty-customer-row span { font-weight: 900; }
.loyalty-customer-row small { color: rgba(255,255,255,.68); }
.loyalty-customer-row strong { grid-row: span 2; align-self: center; color: #ffe28a; }
.loyalty-search-actions { grid-template-columns: 1fr auto auto; }
.external-result { margin-bottom: 14px; border-color: rgba(126, 231, 135, .22); background: linear-gradient(135deg, rgba(58, 211, 126, .10), rgba(255,255,255,.06)); }
.loyalty-customer-row.external strong { color: #9dffbc; }
.loyalty-external-errors { display: grid; gap: 8px; margin: 10px 0; color: #ffd2d2; font-size: .86rem; }
@media (max-width: 720px) {
  .loyalty-search-actions { grid-template-columns: 1fr; }
  .loyalty-search-actions .pos-btn { width: 100%; }
}


.flow-note { border: 1px solid rgba(255,255,255,.12); border-radius: 18px; padding: 12px 14px; background: rgba(255,255,255,.08); color: rgba(255,255,255,.76); display: grid; gap: 4px; }
.flow-note strong { color: #fff; font-weight: 1000; }
.flow-note span { font-size: .88rem; line-height: 1.35; }
.loyalty-flow-note { border-color: rgba(255,214,102,.24); background: linear-gradient(135deg, rgba(255,214,102,.10), rgba(255,255,255,.06)); }
.compact-note { margin-bottom: 12px; }
.customer-search-expanded { grid-template-columns: minmax(14rem, 1fr) auto auto auto auto; }
.loyalty-flow-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin: -4px 0 14px; }
.loyalty-flow-grid div { border: 1px solid rgba(255,255,255,.10); border-radius: 16px; padding: 10px 12px; background: rgba(0,0,0,.16); display: grid; gap: 4px; }
.loyalty-flow-grid strong { color: #fff; font-size: .88rem; }
.loyalty-flow-grid span { color: rgba(255,255,255,.62); font-size: .78rem; line-height: 1.25; }
.qr-scanner-modal { max-width: 560px; }
.qr-scanner-stage { position: relative; overflow: hidden; border-radius: 24px; min-height: 320px; background: rgba(0,0,0,.42); border: 1px solid rgba(255,255,255,.14); display: grid; place-items: center; }
.qr-scanner-video, .qr-reader-box { width: 100%; min-height: 320px; background: #05070b; }
.qr-reader-box :deep(video) { width: 100% !important; min-height: 320px; object-fit: cover; border-radius: 24px; }
.qr-reader-box :deep(img), .qr-reader-box :deep(span), .qr-reader-box :deep(div[style*="display: none"]) { display: none !important; }
.qr-scanner-frame { position: absolute; width: min(70%, 280px); aspect-ratio: 1/1; border: 3px solid rgba(255,214,102,.92); border-radius: 28px; box-shadow: 0 0 0 999px rgba(0,0,0,.28), 0 0 42px rgba(255,214,102,.20); pointer-events: none; }
.qr-scanner-status { margin: 12px 0; color: rgba(255,255,255,.76); font-weight: 800; }
.qr-scanner-status.error { color: #ffd1d1; }
.manual-qr-fallback { display: grid; grid-template-columns: 1fr auto; gap: 10px; }
.qr-help { display: block; margin-top: 10px; color: rgba(255,255,255,.54); line-height: 1.35; }
@media (max-width: 720px) {
  .customer-search-expanded { grid-template-columns: 1fr; }
  .loyalty-flow-grid { grid-template-columns: 1fr; }
  .manual-qr-fallback { grid-template-columns: 1fr; }
  .qr-scanner-stage, .qr-scanner-video { min-height: 260px; }
}


/* Entrega 015 refinamientos PV */
.compact-top-actions { justify-content: flex-end; }
.pos-menu-trigger { width: 46px; height: 46px; border-radius: 50%; border: 1px solid var(--gt-border); background: #fff; display: grid; place-items: center; padding: 10px; cursor: pointer; box-shadow: 0 10px 24px rgba(29,53,87,.08); }
.pos-menu-trigger span { display: block; width: 18px; height: 2px; border-radius: 99px; background: var(--gt-primary); }
.pos-side-overlay { position: fixed; inset: 0; background: rgba(15,23,42,.38); z-index: 2100; backdrop-filter: blur(4px); }
.pos-side-menu { position: fixed; top: 0; right: 0; bottom: 0; width: min(360px, 92vw); z-index: 2200; background: #fff; border-left: 1px solid var(--gt-border); box-shadow: -24px 0 70px rgba(15,23,42,.22); transform: translateX(104%); transition: transform .22s ease; padding: 18px; overflow: auto; display: grid; align-content: start; gap: 10px; }
.pos-side-menu.open { transform: translateX(0); }
.pos-side-menu header { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding-bottom: 12px; border-bottom: 1px solid var(--gt-border); margin-bottom: 6px; }
.pos-side-menu header small, .pos-side-menu header strong { display: block; }
.pos-side-menu header small { color: var(--gt-muted); font-weight: 900; text-transform: uppercase; letter-spacing: .08em; }
.side-action { border: 1px solid var(--gt-border); background: #f8fafc; color: var(--gt-text); border-radius: 16px; padding: 13px 14px; text-align: left; text-decoration: none; font-weight: 900; cursor: pointer; }
.side-action:hover, .side-action.featured { background: var(--gt-primary-soft); color: var(--gt-primary); }
.side-action:disabled { opacity: .45; cursor: not-allowed; }
.item-actions { grid-template-columns: auto auto; }
.item-actions strong { grid-column: 1 / -1; }
.item-discount-btn { background: #fff5df !important; color: #94600e !important; }
.toast { left: auto; right: 1rem; transform: none; top: 5.35rem; border-radius: 18px; min-width: min(24rem, calc(100vw - 2rem)); text-align: left; }
.switch-visual { justify-content: flex-start; }
.switch-visual input { position: absolute; opacity: 0; pointer-events: none; }
.switch-visual i { width: 48px; height: 28px; border-radius: 999px; background: #cbd5e1; position: relative; transition: background .18s ease; }
.switch-visual i::after { content: ''; position: absolute; width: 22px; height: 22px; top: 3px; left: 3px; border-radius: 50%; background: #fff; box-shadow: 0 2px 8px rgba(15,23,42,.22); transition: transform .18s ease; }
.switch-visual input:checked + i { background: var(--gt-primary); }
.switch-visual input:checked + i::after { transform: translateX(20px); }
.customer-assign-modal { width: min(86rem, 100%); display: grid; grid-template-columns: minmax(20rem, .92fr) minmax(26rem, 1.08fr); gap: .85rem; align-items: start; }
.customer-assign-modal .modal-head { grid-column: 1 / -1; }
.customer-assign-modal .flow-note { grid-column: 1; grid-row: 2; min-height: 8rem; color: var(--gt-text); background: #f8fafc; border-color: var(--gt-border); }
.customer-assign-modal .flow-note strong { color: var(--gt-primary); }
.customer-assign-modal .flow-note span { color: var(--gt-muted); }
.customer-assign-modal .customer-search { grid-column: 2; grid-row: 2; align-self: stretch; grid-template-columns: 1fr; }
.customer-assign-modal .customer-search .pos-btn { min-height: 2.55rem; padding: .62rem .85rem; }
.customer-assign-modal .customer-list { grid-column: 2; grid-row: 3 / span 2; max-height: 34rem; }
.customer-assign-modal .new-customer { grid-column: 1; grid-row: 3; border: 1px solid var(--gt-border); border-radius: 18px; padding: .85rem; background: #fff; }
.loyalty-modal { width: min(78rem, 100%); max-width: min(78rem, 100%); background: #fff; color: var(--gt-text); display: grid; gap: .75rem; }
.loyalty-hero, .loyalty-flow-grid div, .loyalty-result-card, .redemption-items div, .loyalty-customer-row { background: #f8fafc; border-color: var(--gt-border); color: var(--gt-text); }
.loyalty-hero strong, .loyalty-flow-grid strong, .loyalty-result-card h3, .redemption-items span, .loyalty-customer-row span { color: var(--gt-text); }
.loyalty-hero small, .loyalty-flow-grid span, .loyalty-result-card p, .loyalty-customer-row small, .loyalty-result-card small { color: var(--gt-muted); }
.loyalty-hero span { background: #fff5df; color: #94600e; }
.loyalty-flow-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.points-big, .redemption-items small, .loyalty-customer-row strong { color: var(--gt-primary); }
.external-result { background: #f0fdf4; border-color: #bbf7d0; }
.loyalty-external-errors { color: #991b1b; }
.sales-summary-button { border: 1px solid var(--gt-border); border-radius: 16px; padding: .75rem; background: rgba(255,255,255,.74); display: grid; gap: .2rem; text-align: left; cursor: pointer; }
.sales-summary-button span { color: var(--gt-muted); font-size: .82rem; font-weight: 800; }
.sales-summary-button strong { font-size: 1.1rem; }
.sales-expense-list { border: 1px solid var(--gt-border); background: #f8fafc; border-radius: 18px; padding: .85rem; display: grid; gap: .45rem; }
.sales-expense-list > div { display: grid; grid-template-columns: 1fr auto; gap: .1rem .8rem; padding: .55rem; border-radius: 12px; background: #fff; }
.sales-expense-list span { font-weight: 900; }
.sales-expense-list small { color: var(--gt-muted); }
.sales-expense-list strong { grid-column: 2; grid-row: 1 / span 2; align-self: center; }
.discount-card-admin { padding: 0; overflow: hidden; display: grid; }
.discount-main { border: 0; background: transparent; text-align: left; display: grid; gap: .25rem; padding: .95rem; cursor: pointer; }
.discount-main:disabled { opacity: .55; cursor: not-allowed; }
.discount-card-actions { display: flex; gap: .35rem; border-top: 1px solid var(--gt-border); padding: .45rem; background: #f8fafc; }
.mini-action { border: 0; border-radius: 10px; background: #eef2f7; color: var(--gt-primary); font-weight: 900; padding: .45rem .6rem; cursor: pointer; }
.mini-action.danger { color: var(--gt-danger); background: #fff0ec; }
.point-adjust-modal { width: min(62rem, 100%); display: grid; gap: .75rem; }
.admin-note { margin: 0; border: 1px solid var(--gt-border); border-radius: 16px; background: #f8fafc; padding: .8rem; color: var(--gt-muted); font-weight: 800; }
.point-form-grid.two-cols { grid-template-columns: .5fr 1fr; }
.customer-card.active { background: var(--gt-primary-soft); border-color: rgba(29,53,87,.36); }
.qr-scanner-status, .qr-help { color: var(--gt-text); }
@media (max-width: 920px) { .customer-assign-modal, .loyalty-flow-grid, .point-form-grid.two-cols { grid-template-columns: 1fr; } .customer-assign-modal .modal-head, .customer-assign-modal .flow-note, .customer-assign-modal .customer-search, .customer-assign-modal .customer-list, .customer-assign-modal .new-customer { grid-column: auto; grid-row: auto; } }
@media (max-width: 920px) { .product-report-filters { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 720px) { .toast { top: 4.25rem; } .product-report-filters, .sales-filters, .sales-summary-grid { grid-template-columns: 1fr; } }


.category-tile,.category-card,.product-tile,.product-card{background:var(--pos-tile-color,#edf2f7) !important;min-height:var(--pos-tile-height,120px) !important;transition:opacity .15s,box-shadow .15s}
.product-tile.stock-empty,.product-card.stock-empty{opacity:.5;filter:saturate(.25)}
.product-tile:disabled,.product-card:disabled{cursor:not-allowed;opacity:.5}
.stock-label{display:block;font-weight:700;font-size:12px;color:#6e4d27}
.option-row-edit{display:flex!important;flex-wrap:wrap;gap:8px}.option-row-edit input:not([type=checkbox]),.option-row-edit select{flex:1;min-width:110px}
button[aria-busy=true]{cursor:wait;opacity:.6}
.category-grid,.product-grid{grid-template-columns:repeat(auto-fill,minmax(var(--pos-card-width,11.5rem),1fr))}

/* Fase 2: accesos rapidos, favoritos y edicion tactil de cantidades. */
.product-quickbar { display:flex; flex-wrap:wrap; align-items:center; gap:.45rem; padding:0 0 .85rem; }
.quickbar-button { border:1px solid var(--gt-border); background:#f8fafc; color:var(--gt-primary); border-radius:999px; padding:.55rem .8rem; font-weight:800; cursor:pointer; white-space:nowrap; }
.quickbar-button.selected { color:white; background:var(--gt-primary); border-color:var(--gt-primary); }
.quickbar-button span { font-size:.72rem; opacity:.75; }
.quickbar-button.repeat-last { margin-left:auto; }
.product-tile-wrap { position:relative; min-width:0; }
.product-tile-wrap > .product-tile { width:100%; height:100%; min-width:0; padding-right:2.45rem; }
.favorite-pin { position:absolute; right:.45rem; top:.45rem; width:2.2rem; height:2.2rem; border-radius:999px; background:#fff; border:1px solid rgba(29,53,87,.18); font-size:1.4rem; line-height:1; cursor:pointer; color:#5f6a77; box-shadow:0 2px 8px rgba(0,0,0,.08); }
.favorite-pin.selected { color:#9c6c00; background:#fff5d1; }
.ticket-quantity-controls { display:flex; align-items:center; flex-wrap:wrap; gap:.5rem; margin-top:.5rem; }
.ticket-quantity-controls > button { min-width:2.5rem; min-height:2.5rem; border-radius:.75rem; border:1px solid var(--gt-border); background:#f1f5f9; color:var(--gt-primary); font-weight:900; font-size:1.25rem; cursor:pointer; }
.ticket-quantity-controls > strong { min-width:1.2rem; text-align:center; font-variant-numeric:tabular-nums; }
.ticket-quantity-controls > .item-note-shortcut { font-size:.75rem; padding:0 .55rem; }
.ticket-quantity-controls > button:disabled { opacity:.4; cursor:not-allowed; }
.quantity-lock-note { color:#7c5c22 !important; font-size:.72rem; }
.inline-note-editor { display:grid; gap:.35rem; margin-top:.5rem; }
.inline-note-editor textarea { width:100%; border-radius:.75rem; border:1px solid var(--gt-border); padding:.55rem; font:inherit; }
.inline-note-editor > div { display:flex; gap:.5rem; }
.inline-note-editor button { min-height:2.3rem; padding:.4rem .65rem; border-radius:.6rem; background:var(--gt-primary); border:0; color:white; cursor:pointer; }
.inline-note-editor button.subtle { background:#eef2f7; color:var(--gt-primary); }
@media (max-width:720px) { .product-quickbar { flex-wrap:nowrap; overflow-x:auto; padding-bottom:.65rem; } .quickbar-button { padding:.5rem .65rem; } .quickbar-button.repeat-last { margin-left:0; } .ticket-quantity-controls > button { min-height:2.6rem; min-width:2.6rem; } }

/* Fase 2: traslado y union segura de cuentas. */
.item-actions .item-move-btn { background:#ecf4ff; color:var(--gt-primary); font-size:1.1rem; }
.account-arrange-modal { max-width:520px; }
.arrange-info { color:var(--gt-muted); line-height:1.45; margin:.5rem 0 1rem; }
.arrange-field { display:grid; gap:.4rem; margin-bottom:.85rem; font-weight:800; }
.arrange-field input, .arrange-field select { width:100%; min-height:2.9rem; border:1px solid var(--gt-border); border-radius:12px; padding:.6rem .75rem; background:white; }
.arrange-warning { display:block; padding:.7rem .85rem; border-radius:12px; background:#fff7e6; color:#785b1c; line-height:1.4; margin-bottom:1rem; }

.sent-item-actions { display:grid; gap:.3rem; margin-top:.4rem; }
.sent-item-actions button { justify-self:start; background:#f1f5f9; border:1px solid var(--gt-border); border-radius:.6rem; padding:.45rem .6rem; font-size:.75rem; color:var(--gt-primary); font-weight:800; cursor:pointer; }
</style>

<style>
.coupon-modal{max-width:min(650px,95vw);max-height:90vh;overflow-y:auto}.coupon-input-row{display:flex;gap:7px;flex-wrap:wrap;margin:14px 0}.coupon-input-row input{flex:1 1 180px;border:1px solid #cbd5e1;border-radius:9px;padding:12px;min-width:0}.coupon-preview{display:grid;gap:12px;padding:14px;border-radius:12px;background:#f7fafc}.coupon-preview h3{font-size:18px;font-weight:800}.coupon-preview label{display:grid;gap:6px}.coupon-preview select,.coupon-preview input{width:100%;padding:10px;border:1px solid #bdcdd8;border-radius:9px}.coupon-eligible-list{display:grid;gap:7px}.coupon-current{display:grid;gap:9px;border:1px solid #b5e0cc;border-radius:12px;padding:13px;background:#f0faf5}.coupon-warning{background:#fff2da;color:#814415;border-radius:9px;padding:10px}
</style>
